import React, { useState, useEffect, useContext, useRef } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useParams, Link } from 'react-router-dom';
import { ClubAuthProvider, ClubLoginForm, ClubRegisterForm, ClubDashboard } from './ClubAuth';
import { AuthContext, AuthProvider } from './contexts/AuthContext';
import { LoginForm, RegisterForm, makeAuthenticatedRequest } from './components/auth/AuthForms';
import { bottomTabs, sidebarGroups, tabs, themeOptions } from './config/navigation';
import { ConfirmDialogProvider, useConfirmDialog } from './components/ui/ConfirmDialog';
import ImportStatsFromLink from './components/ImportStatsFromLink';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  RadialLinearScale,
} from 'chart.js';
import { Line, Bar, Doughnut, Radar } from 'react-chartjs-2';
import Papa from 'papaparse';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import './App.css';
import LandingPage from './LandingPage';
import { PaginaEventoPubblica } from './IscrizioniEvento';
import AdminDashboard from './pages/AdminDashboard';
import CalendarContainer from './components/calendar/CalendarContainer';
import { PublicClubPage, AttivaCodicePage, AreaSocioPage, GuidaPage, PublicProfilePage } from './pages/PublicPages';
import ClubApp from './components/club/ClubApp';
import EXIF from 'exif-js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  RadialLinearScale
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function App() {
  // Use AuthContext to get authentication state
  const { authState, logout } = useContext(AuthContext);
  const { showConfirm } = useConfirmDialog();
  const [showAdminDashboard, setShowAdminDashboard] = useState(false);
  
  // Comprehensive debug logging
  console.log('🔍 APP RENDER: Auth state is:', {
    isAuthenticated: authState.isAuthenticated,
    isLoading: authState.isLoading,
    hasUser: !!authState.user,
    userEmail: authState.user?.email,
    tokenExists: !!authState.token
  });
  
  // Check URL for admin route
  useEffect(() => {
    const urlPath = window.location.pathname;
    if (urlPath === '/admin' && authState.user?.plan_type === 'owner') {
      setShowAdminDashboard(true);
    }
  }, [authState.user]);
  
  // Show loading while checking authentication
  if (authState.isLoading) {
    console.log('🔍 APP: Showing loading state');
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Caricamento...</p>
        </div>
      </div>
    );
  }

  // 🔐 ADMIN DASHBOARD - Show admin dashboard if owner and admin route
  if (authState.isAuthenticated && showAdminDashboard && authState.user?.plan_type === 'owner') {
    console.log('🔍 APP: Showing AdminDashboard');
    return (
      <AdminDashboard 
        user={authState.user} 
        onLogout={logout}
        onBackToApp={() => {
          setShowAdminDashboard(false);
          window.history.pushState({}, '', '/');
        }}
      />
    );
  }

  // 🏠 MAIN APP - Show main app if authenticated
  if (authState.isAuthenticated) {
    console.log('🔍 APP: Should show MainApp for authenticated user');
    return (
      <MainApp 
        user={authState.user} 
        onLogout={logout}
        onShowAdmin={() => setShowAdminDashboard(true)}
      />
    );
  }

  // 🔐 LOGIN/REGISTER - Show login form if not authenticated  
  console.log('🔍 APP: Showing LoginForm (not authenticated)');
  return <LoginForm />;
}

// 🏠 MAIN APP COMPONENT (existing App logic)
function MainApp({ user, onLogout, onShowAdmin }) {
  // 🔧 Custom hook for confirm dialog
  const { showConfirm } = useConfirmDialog();
  
  // Existing states
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [generalStats, setGeneralStats] = useState(null);
  const [opponentStats, setOpponentStats] = useState([]);
  const [tournamentStats, setTournamentStats] = useState([]);
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // 🍔 SMART HAMBURGER MENU STATES
  const [quickStats, setQuickStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [menuContext, setMenuContext] = useState(null);
  const [showQuickAddModal, setShowQuickAddModal] = useState(false);

  // NEW ADVANCED STATES 🚀
  const [streaks, setStreaks] = useState(null);
  const [teamPerformance, setTeamPerformance] = useState([]);
  const [temporalAnalysis, setTemporalAnalysis] = useState(null);
  const [achievements, setAchievements] = useState([]);
  const [predictions, setPredictions] = useState(null);
  const [settings, setSettings] = useState(null);
  const [goalsTracking, setGoalsTracking] = useState(null);
  
  // 🏆 TROPHIES FILTER STATE
  const [trophyFilter, setTrophyFilter] = useState('tutti');

  // 🏢 USER CLUBS STATE
  const [userClubs, setUserClubs] = useState([]);
  const [loadingClubs, setLoadingClubs] = useState(false);
  const [joinClubCode, setJoinClubCode] = useState('');
  const [joiningClub, setJoiningClub] = useState(false);
  const [joinClubError, setJoinClubError] = useState('');
  const [joinClubSuccess, setJoinClubSuccess] = useState('');

  // 🚀 NEW ADVANCED FEATURES STATE
  const [seasons, setSeasons] = useState([]);
  const [currentSeason, setCurrentSeason] = useState(null);
  const [dailyChallenges, setDailyChallenges] = useState([]);
  const [dashboardWidgets, setDashboardWidgets] = useState([]);
  const [availableWidgets, setAvailableWidgets] = useState([]);
  const [selectedSeasonComparison, setSelectedSeasonComparison] = useState({ season1: '', season2: '' });
  const [seasonComparison, setSeasonComparison] = useState(null);
  const [backupData, setBackupData] = useState(null);
  // 🎨 THEME STATES - Enhanced with Dynamic Themes
  const [theme, setTheme] = useState(() => localStorage.getItem('subbuteo_theme') || 'auto');
  const [currentAutoTheme, setCurrentAutoTheme] = useState('light');
  const [sunTimes, setSunTimes] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [themeTransition, setThemeTransition] = useState(false);
  const [showGoalForm, setShowGoalForm] = useState(false);
  
  // 🌐 PROFILO PUBBLICO STATES
  const [profiloPublicEnabled, setProfiloPublicEnabled] = useState(false);
  const [profiloUsername, setProfiloUsername] = useState('');
  const [togglingProfilo, setTogglingProfilo] = useState(false);

  // Form states
  // NEW MUSEUM STATES 🏟️
  const [museumTeams, setMuseumTeams] = useState([]);
  const [museumOverview, setMuseumOverview] = useState(null);
  const [showTeamForm, setShowTeamForm] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [museumFilter, setMuseumFilter] = useState({
    search: '',
    country: '',
    team_type: ''
  });

  // 🎨 NEW MUSEUM FILTERS STATE - Enhanced for new design
  const [museumAdvancedFilter, setMuseumAdvancedFilter] = useState({
    search: '',
    country: '',
    category: '', // club, nazionale, fantasy
    sortBy: 'name', // name, win_rate, total_matches
    viewMode: 'grid' // grid, list
  });

  // 🔍 DEBUG & MIGRATION STATES
  const [showDebugModal, setShowDebugModal] = useState(false);
  const [debugData, setDebugData] = useState(null);
  const [migrationInProgress, setMigrationInProgress] = useState(false);

  // 🏆 NEW TOURNAMENTS STATES (visual cards)
  const [tournamentsCards, setTournamentsCards] = useState([]);
  const [loadingTournamentsCards, setLoadingTournamentsCards] = useState(false);
  const [selectedTournamentModal, setSelectedTournamentModal] = useState(null);
  const [tournamentModalMatches, setTournamentModalMatches] = useState([]);
  const [visibleActiveCount, setVisibleActiveCount] = useState(10); // Initially show 10 active tournaments
  const [visibleArchivedCount, setVisibleArchivedCount] = useState(1); // Initially show only 1 archived tournament (last one)

  // 🖼️ TOURNAMENT IMAGE UPLOAD STATES
  const [showTournamentForm, setShowTournamentForm] = useState(false);
  const [editingTournament, setEditingTournament] = useState(null);
  const [uploadingTournamentImage, setUploadingTournamentImage] = useState(false);

  // 🏆 NEW TOURNAMENTS FUNCTIONS (visual cards)
  const loadTournamentsCards = async () => {
    if (loadingTournamentsCards) return;
    
    setLoadingTournamentsCards(true);
    try {
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/tournaments`);
      if (response && response.ok) {
        const data = await response.json();
        const tournaments = data.tournaments || [];
        
        // 🎯 SMART SORTING: Active tournaments first, then completed
        // Within each group, sort by date (most recent first)
        const sortedTournaments = tournaments.sort((a, b) => {
          // First, sort by status (active first)
          if (a.status === 'active' && b.status !== 'active') return -1;
          if (a.status !== 'active' && b.status === 'active') return 1;
          
          // Within same status, sort by created_at (most recent first)
          const dateA = new Date(a.created_at || 0);
          const dateB = new Date(b.created_at || 0);
          return dateB - dateA;
        });
        
        setTournamentsCards(sortedTournaments);
        console.log('✅ Tournament cards loaded and sorted:', sortedTournaments.length);
      } else {
        console.error('Failed to load tournament cards:', response?.status);
        setTournamentsCards([]);
      }
    } catch (error) {
      console.error('Error loading tournament cards:', error);
      setTournamentsCards([]);
      showToastNotification('❌ Errore caricamento tornei', 'error');
    } finally {
      setLoadingTournamentsCards(false);
    }
  };

  const loadTournamentModalMatches = async (tournamentName) => {
    try {
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/tournaments/${encodeURIComponent(tournamentName)}/matches`);
      if (response && response.ok) {
        const data = await response.json();
        setTournamentModalMatches(data.matches || []);
        setSelectedTournamentModal(tournamentName);
        console.log(`Matches loaded for ${tournamentName}:`, data.matches?.length);
      } else {
        console.error('Failed to load tournament matches:', response?.status);
        showToastNotification('Errore caricamento partite torneo', 'error');
      }
    } catch (error) {
      console.error('Error loading tournament matches:', error);
      showToastNotification('Errore caricamento partite torneo', 'error');
    }
  };

  // NEW EDIT MATCH STATES ✏️
  const [editingMatch, setEditingMatch] = useState(null);
  const [showEditForm, setShowEditForm] = useState(false);
  
  // 🏛️ MUSEUM TEAMS FOR DROPDOWN ASSOCIATION (using existing museumTeams state)
  const [loadingMuseumTeams, setLoadingMuseumTeams] = useState(false);

  // 👥 OPPONENT MATCHES MODAL STATES
  const [showOpponentModal, setShowOpponentModal] = useState(false);
  const [selectedOpponent, setSelectedOpponent] = useState(null);
  const [opponentMatches, setOpponentMatches] = useState([]);

  // 🖼️ LAZY LOADING STATES
  const [loadedImages, setLoadedImages] = useState(new Set());

  // 🖼️ LAZY IMAGE COMPONENT
  const LazyTournamentImage = ({ src, alt, className, tournamentName }) => {
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);
    const imgRef = useRef(null);

    // Reset state when src changes
    useEffect(() => {
      setImageLoaded(false);
      setImageError(false);
      
      // If image was already loaded in cache, set it immediately
      if (loadedImages.has(src)) {
        setImageLoaded(true);
      }
    }, [src]);

    useEffect(() => {
      const imageElement = imgRef.current;
      if (!imageElement || !src) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !imageLoaded && !loadedImages.has(src)) {
              const img = new Image();
              img.onload = () => {
                setImageLoaded(true);
                setLoadedImages(prev => new Set(prev.add(src)));
              };
              img.onerror = () => {
                setImageError(true);
              };
              img.src = src;
              observer.unobserve(imageElement);
            }
          });
        },
        { threshold: 0.1 }
      );

      observer.observe(imageElement);
      return () => observer.disconnect();
    }, [src, imageLoaded, loadedImages]);

    return (
      <div ref={imgRef} className={className} style={{ position: 'relative' }}>
        {/* Loading placeholder */}
        {!imageLoaded && !imageError && (
          <div className="w-full h-full bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center animate-pulse">
            <div className="text-white text-2xl">🏆</div>
          </div>
        )}
        
        {/* Error placeholder */}
        {imageError && (
          <div className="w-full h-full bg-gradient-to-br from-green-500 to-blue-600 flex items-center justify-center">
            <div className="text-white text-2xl font-bold">🏆</div>
          </div>
        )}
        
        {/* Actual image - always rendered but hidden until loaded */}
        {!imageError && (
          <img
            src={src}
            alt={alt}
            className="w-full h-full object-cover transition-opacity duration-300"
            style={{ opacity: imageLoaded ? 1 : 0 }}
          />
        )}
      </div>
    );
  };

  // NEW PROFILE STATES (for debouncing) 🧑‍💼
  const [localPlayerName, setLocalPlayerName] = useState('');
  const [playerNameTimeout, setPlayerNameTimeout] = useState(null);

  // 🏛️ MUSEUM TEAMS LOADING (using existing loadMuseumTeams function)

  // 🏛️ ASSOCIATE TOURNAMENT TO MUSEUM TEAM
  const associateTournamentToMuseumTeam = async (tournamentName, museumTeamId) => {
    if (!museumTeamId) return;
    
    try {
      const response = await makeAuthenticatedRequest(
        `${BACKEND_URL}/api/tournament/associate-museum-team?tournament_name=${encodeURIComponent(tournamentName)}&museum_team_id=${museumTeamId}`,
        { method: 'PUT' }
      );
      
      if (response && response.ok) {
        const data = await response.json();
        showToastNotification(
          `✅ Torneo "${tournamentName}" associato a "${data.museum_team}" - ${data.updated_matches} partite aggiornate`,
          'success'
        );
        
        // Reload museum data to refresh statistics
        await loadMuseumTeams();
      } else {
        showToastNotification('Errore durante associazione torneo-squadra Museum', 'error');
      }
    } catch (error) {
      console.error('Error associating tournament to museum team:', error);
      showToastNotification('Errore durante associazione torneo-squadra Museum', 'error');
    }
  };

  // 🖼️ IMAGE COMPRESSION UTILITY WITH EXIF ORIENTATION FIX
  const compressImage = (file, maxWidth = 600, maxHeight = 400, quality = 0.75) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onerror = () => {
        console.error('❌ Error loading image');
        reject(new Error('Failed to load image'));
      };
      
      img.onload = () => {
        try {
          // Get EXIF orientation using library
          EXIF.getData(img, function() {
            const orientation = EXIF.getTag(this, 'Orientation') || 1;
            
            // Calculate new dimensions maintaining aspect ratio
            let { width, height } = img;
            
            // Swap dimensions if orientation requires it (5,6,7,8)
            const needsSwap = orientation >= 5 && orientation <= 8;
            if (needsSwap) {
              [width, height] = [height, width];
            }
            
            if (width > height) {
              if (width > maxWidth) {
                height = (height * maxWidth) / width;
                width = maxWidth;
              }
            } else {
              if (height > maxHeight) {
                width = (width * maxHeight) / height;
                height = maxHeight;
              }
            }
            
            // Set canvas dimensions
            canvas.width = width;
            canvas.height = height;
            
            // Apply transformations based on EXIF orientation
            ctx.save();
            
            switch (orientation) {
              case 2:
                // horizontal flip
                ctx.translate(width, 0);
                ctx.scale(-1, 1);
                break;
              case 3:
                // 180° rotation
                ctx.translate(width, height);
                ctx.rotate(Math.PI);
                break;
              case 4:
                // vertical flip
                ctx.translate(0, height);
                ctx.scale(1, -1);
                break;
              case 5:
                // vertical flip + 90° rotate right
                ctx.rotate(0.5 * Math.PI);
                ctx.scale(1, -1);
                break;
              case 6:
                // 90° rotate right
                ctx.rotate(0.5 * Math.PI);
                ctx.translate(0, -height);
                break;
              case 7:
                // horizontal flip + 90° rotate right
                ctx.rotate(0.5 * Math.PI);
                ctx.translate(width, -height);
                ctx.scale(-1, 1);
                break;
              case 8:
                // 90° rotate left
                ctx.rotate(-0.5 * Math.PI);
                ctx.translate(-width, 0);
                break;
              default:
                // case 1: normal, no transformation
                break;
            }
            
            // Draw image with correct orientation
            if (needsSwap) {
              ctx.drawImage(img, 0, 0, height, width);
            } else {
              ctx.drawImage(img, 0, 0, width, height);
            }
            ctx.restore();
            
            // Convert to blob with compression
            canvas.toBlob(
              (blob) => {
                if (!blob) {
                  reject(new Error('Failed to create blob'));
                  return;
                }
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now()
                });
                console.log('✅ Image compressed with EXIF orientation:', {
                  original: `${img.width}x${img.height}`,
                  compressed: `${width}x${height}`,
                  orientation: orientation,
                  size: `${(blob.size / 1024).toFixed(2)}KB`
                });
                resolve(compressedFile);
              },
              'image/jpeg',
              quality
            );
          });
        } catch (error) {
          console.error('❌ Error processing image:', error);
          reject(error);
        }
      };
      
      const reader = new FileReader();
      reader.onerror = () => {
        console.error('❌ Error reading file');
        reject(new Error('Failed to read file'));
      };
      reader.onload = (e) => {
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  // 👥 OPEN OPPONENT MATCHES MODAL
  const openOpponentModal = (opponentName) => {
    console.log('Opening opponent modal for:', opponentName);
    
    // Filter matches for this specific opponent
    const filteredMatches = matches.filter(match => 
      match.opponent_name === opponentName
    ).sort((a, b) => {
      // Sort by date ascending (oldest first)
      return new Date(a.match_date) - new Date(b.match_date);
    });
    
    // Calculate head-to-head stats considering piazzati
    let wins = 0, draws = 0, losses = 0;
    filteredMatches.forEach(m => {
      // Se ha piazzati, usa quello per determinare il vincitore
      if (m.has_piazzati && m.piazzati_miei !== null && m.piazzati_avversario !== null) {
        if (m.piazzati_miei > m.piazzati_avversario) wins++;
        else if (m.piazzati_miei < m.piazzati_avversario) losses++;
        else draws++;
      } else {
        // Altrimenti usa il risultato normale
        if (m.my_goals > m.opponent_goals) wins++;
        else if (m.my_goals < m.opponent_goals) losses++;
        else draws++;
      }
    });
    
    setSelectedOpponent({
      name: opponentName,
      wins,
      draws, 
      losses,
      totalMatches: filteredMatches.length
    });
    setOpponentMatches(filteredMatches);
    setShowOpponentModal(true);
  };

  // 💳 PAYMENT & SUBSCRIPTION STATES
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);

  // 📅 CALENDAR STATES
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarFilter, setCalendarFilter] = useState('all'); // all, upcoming, past
  const [calendarNotifications, setCalendarNotifications] = useState({
    weekly_email: false,
    monthly_email: false,
    reminder_24h: false,
    reminder_1week: false
  });
  const [backupReminder, setBackupReminder] = useState({ weekly_reminder: true });
  const [isExporting, setIsExporting] = useState(false);
  const [showAddEventModal, setShowAddEventModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentCalendarDate, setCurrentCalendarDate] = useState(new Date());
  const [eventFormData, setEventFormData] = useState({
    name: '',
    date: '',
    time: '',
    category: 'training',
    description: ''
  });

  // 🔔 TOAST NOTIFICATIONS STATE
  const [toastMessage, setToastMessage] = useState(null);

  // 🔔 TOAST NOTIFICATION FUNCTION
  const showToastNotification = (message, type = 'success') => {
    setToastMessage({ message, type });
    setTimeout(() => setToastMessage(null), 4000); // Auto hide after 4 seconds
  };

  // NEW IMPORT SYSTEM STATES 📁
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importProgress, setImportProgress] = useState({ show: false, message: '', percent: 0 });
  const [importResult, setImportResult] = useState(null);
  const [importStep, setImportStep] = useState('upload'); // 'upload', 'preview', 'mapping', 'execute', 'result'
  const [replaceExistingData, setReplaceExistingData] = useState(true); // Default: replace existing data
  const [customColumnMapping, setCustomColumnMapping] = useState({}); // Editor visuale mappatura
  const [showMappingEditor, setShowMappingEditor] = useState(false);

  // NEW TROFEI & ANALYTICS STATES 🏆🧠
  const [allTrophies, setAllTrophies] = useState([]);
  const [aiInsights, setAiInsights] = useState({});
  const [advancedStats, setAdvancedStats] = useState({});
  
  // 🔄 LOADING STATES FOR BUTTONS
  const [loadingStates, setLoadingStates] = useState({
    trophies: false,
    challenges: false,
    aiInsights: false
  });

  // 📱 PWA INSTALL STATE
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [deferredInstallPrompt, setDeferredInstallPrompt] = useState(null);

  const [formData, setFormData] = useState({
    my_team: '',
    opponent_team: '',
    opponent_name: '',
    my_goals: '',
    opponent_goals: '',
    match_date: new Date().toISOString().split('T')[0],
    tournament: '',
    // NEW: Museum associations (solo per la mia squadra)
    my_museum_team_id: '',
    // PIAZZATI (Shootouts)
    has_piazzati: false,
    piazzati_miei: '',
    piazzati_avversario: ''
  });

  // Museum Team Form State 🏟️
  const [teamFormData, setTeamFormData] = useState({
    name: '',
    photo_url: '',
    base_color: '#FFFFFF',
    inner_color: '#000000', 
    country: '',
    team_type: 'Club',  // Club or Nazionale
    notes: ''
  });

  // Load initial data
  useEffect(() => {
    loadAllData();
    loadAdvancedData();
    
    // Handle PayPal return
    const urlParams = new URLSearchParams(window.location.search);
    const paymentId = urlParams.get('paymentId');
    const PayerID = urlParams.get('PayerID');
    
    if (paymentId && PayerID) {
      handlePayPalReturn(paymentId, PayerID);
    }

    // 📱 PWA INSTALL PROMPT HANDLER
    const handleBeforeInstallPrompt = (e) => {
      console.log('🔔 PWA: Install prompt available');
      e.preventDefault(); // Prevent Chrome 67 and earlier from automatically showing the prompt
      setDeferredInstallPrompt(e); // Stash the event so it can be triggered later
      setShowInstallPrompt(true); // Show our custom install button
    };

    const handleAppInstalled = () => {
      console.log('🎉 PWA: App was installed');
      setShowInstallPrompt(false); // Hide install button
      setDeferredInstallPrompt(null);
      showToastNotification('🎉 App installata con successo!', 'success');
    };

    // Add event listeners for PWA
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Cleanup
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handlePayPalReturn = async (paymentId, PayerID) => {
    try {
      console.log('🔄 Elaborazione pagamento PayPal in corso...');
      const success = await executePayment(paymentId, PayerID);
      
      // Clean URL regardless of outcome
      window.history.replaceState({}, document.title, window.location.pathname);
      
      if (!success) {
        // Only show error if payment actually failed
        showToastNotification('❌ Errore nel completamento del pagamento. Contatta il supporto.', 'error');
      }
      // If success, the modal will be shown by executePayment (setShowPaymentSuccess)
    } catch (error) {
      console.error('Error handling PayPal return:', error);
      showToastNotification('❌ Errore nel completamento del pagamento. Contatta il supporto.', 'error');
    }
  };

  // Apply theme
  useEffect(() => {
    if (settings?.theme) {
      setTheme(settings.theme);
      document.documentElement.setAttribute('data-theme', settings.theme);
    }
  }, [settings]);

  // Sync local player name with settings
  useEffect(() => {
    if (settings?.player_name !== undefined) {
      setLocalPlayerName(settings.player_name);
    }
  }, [settings?.player_name]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (playerNameTimeout) {
        clearTimeout(playerNameTimeout);
      }
    };
  }, [playerNameTimeout]);

  // 🖼️ TOURNAMENT IMAGE UPLOAD FUNCTIONS
  const uploadTournamentImage = async (file, tournamentName) => {
    setUploadingTournamentImage(true);
    try {
      // Compress image before upload (full version)
      console.log('🖼️ Compressing tournament image...');
      const compressedFile = await compressImage(file, 600, 400, 0.75);
      
      // Create thumbnail (200x200)
      console.log('🖼️ Creating thumbnail...');
      const thumbnailFile = await compressImage(file, 200, 200, 0.7);
      
      // Upload full image
      const formData = new FormData();
      formData.append('file', compressedFile);
      
      const response = await fetch(`${BACKEND_URL}/api/tournaments/upload-poster`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('subbuteo_token') || sessionStorage.getItem('subbuteo_token')}`
        },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Upload thumbnail
        const thumbnailFormData = new FormData();
        thumbnailFormData.append('file', thumbnailFile);
        
        const thumbnailResponse = await fetch(`${BACKEND_URL}/api/tournaments/upload-poster`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('subbuteo_token') || sessionStorage.getItem('subbuteo_token')}`
          },
          body: thumbnailFormData
        });
        
        let thumbnailUrl = data.photo_url; // Fallback to full image
        if (thumbnailResponse.ok) {
          const thumbnailData = await thumbnailResponse.json();
          thumbnailUrl = thumbnailData.photo_url;
          console.log('✅ Thumbnail created successfully');
        }
        
        // Update editingTournament state with new poster_url and thumbnail
        setEditingTournament(prev => ({
          ...prev,
          poster_url: data.photo_url,
          poster_thumbnail: thumbnailUrl
        }));
        
        // Save tournament metadata with image and thumbnail
        const metadataResponse = await makeAuthenticatedRequest(`${BACKEND_URL}/api/tournaments/metadata`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: tournamentName,
            poster_url: data.photo_url,
            poster_thumbnail: thumbnailUrl,
            description: editingTournament?.description || null,
            status: editingTournament?.status || 'active'
          })
        });
        
        if (metadataResponse && metadataResponse.ok) {
          showToastNotification('✅ Locandina torneo salvata!', 'success');
          loadTournamentsCards(); // Reload tournaments
          return data.photo_url;
        } else {
          showToastNotification('❌ Errore salvataggio metadati', 'error');
        }
      } else {
        showToastNotification('❌ Errore upload immagine', 'error');
      }
    } catch (error) {
      console.error('Error uploading tournament image:', error);
      showToastNotification('Errore upload immagine', 'error');
    } finally {
      setUploadingTournamentImage(false);
    }
  };

  const editTournamentImage = async (tournament) => {
    setEditingTournament({
      name: tournament.name,
      poster_url: tournament.poster_url,
      description: tournament.description || '',
      museum_team_id: tournament.museum_team_id || '',
      status: tournament.status || 'active'
    });
    setShowTournamentForm(true);
    
    // Load museum teams for dropdown
    await loadMuseumTeams();
  };

  // Load tournament cards when tab becomes active
  useEffect(() => {
    if (activeTab === 'tournaments') {
      loadTournamentsCards();
    }
    if (activeTab === 'my-clubs') {
      loadUserClubs();
    }
  }, [activeTab]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadMatches(),
        loadTeams(),
        loadPlayers(), 
        loadTournaments(),
        loadGeneralStats(),
        loadOpponentStats(),
        loadTournamentStats(),
        loadMonthlyStats(),
        loadStreaks(),
        loadTeamPerformance(),
        loadTemporalAnalysis(),
        loadAchievements(),
        loadPredictions(),
        loadSettings(),
        loadGoalsTracking(),
        loadMuseumTeams(),
        loadMuseumOverview(),
        // 🚀 NEW ADVANCED FEATURES
        loadSeasons(),
        loadDailyChallenges(), 
        loadAvailableWidgets(),
        loadSubscriptionStatus(),  // Load subscription status
        loadCalendarEvents(),  // Load calendar events
        loadCalendarNotifications(),  // Load notification preferences
        loadBackupReminder(),  // Load backup reminder preferences
        loadProfiloStatus()  // Load profilo pubblico status
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAdvancedData = async () => {
    try {
      await Promise.all([
        loadStreaks(),
        loadTeamPerformance(),
        loadTemporalAnalysis(),
        loadAchievements(),
        loadPredictions(),
        loadSettings(),
        loadGoalsTracking(),
        loadMuseumTeams(),  // NEW! 🏟️
        loadMuseumOverview(),  // NEW! 🏟️
        loadTrophies(),  // NEW! 🏆
        loadDailyChallenges(),  // NEW! 🎮
        loadAiInsights(),  // NEW! 🧠
        loadAdvancedAnalytics()  // NEW! 📊
      ]);
    } catch (error) {
      console.error('Error loading advanced data:', error);
    }
  };

  // Existing load functions
  const loadMatches = async () => {
    const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/matches`);
    if (response) {
      const data = await response.json();
      setMatches(data.matches || []);
    }
  };

  const loadTeams = async () => {
    const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/teams`);
    if (response) {
      const data = await response.json();
      setTeams(data.teams || []);
    }
  };

  const loadPlayers = async () => {
    const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/players`);
    if (response) {
      const data = await response.json();
      setPlayers(data.players || []);
    }
  };

  const loadTournaments = async () => {
    const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/tournaments`);
    if (response) {
      const data = await response.json();
      setTournaments(data.tournaments || []);
    }
  };

  const loadGeneralStats = async () => {
    const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/stats/general`);
    if (response) {
      const data = await response.json();
      setGeneralStats(data);
    }
  };

  const loadOpponentStats = async () => {
    const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/stats/by-opponent`);
    if (response) {
      const data = await response.json();
      setOpponentStats(data.opponent_stats || []);
    }
  };

  const loadTournamentStats = async () => {
    const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/stats/by-tournament`);
    if (response) {
      const data = await response.json();
      setTournamentStats(data.tournament_stats || []);
    }
  };

  const loadMonthlyStats = async () => {
    const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/stats/by-month`);
    if (response) {
      const data = await response.json();
      setMonthlyStats(data.monthly_stats || []);
    }
  };

  // NEW ADVANCED LOAD FUNCTIONS 🚀
  const loadStreaks = async () => {
    try {
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/stats/streaks`);
      if (response && response.ok) {
        const data = await response.json();
        setStreaks(data);
      }
    } catch (error) {
      console.error('Error loading streaks:', error);
    }
  };

  const loadTeamPerformance = async () => {
    try {
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/stats/team-performance`);
      if (response && response.ok) {
        const data = await response.json();
        setTeamPerformance(data.team_performance || []);
      }
    } catch (error) {
      console.error('Error loading team performance:', error);
    }
  };

  const loadTemporalAnalysis = async () => {
    try {
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/stats/temporal-analysis`);
      if (response && response.ok) {
        const data = await response.json();
        setTemporalAnalysis(data);
      }
    } catch (error) {
      console.error('Error loading temporal analysis:', error);
    }
  };

  const loadAchievements = async () => {
    try {
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/stats/achievements`);
      if (response && response.ok) {
        const data = await response.json();
        setAchievements(data.achievements || []);
      }
    } catch (error) {
      console.error('Error loading achievements:', error);
    }
  };

  const loadPredictions = async () => {
    try {
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/stats/predictions`);
      if (response && response.ok) {
        const data = await response.json();
        setPredictions(data);
      }
    } catch (error) {
      console.error('Error loading predictions:', error);
    }
  };

  // 🚀 NEW ADVANCED FEATURES LOADING FUNCTIONS
  const loadSeasons = async () => {
    try {
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/seasons`);
      if (response && response.ok) {
        const data = await response.json();
        setSeasons(data.seasons);
        setCurrentSeason(data.current_season);
      }
    } catch (error) {
      console.error('Error loading seasons:', error);
    }
  };

  const loadAvailableWidgets = async () => {
    try {
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/dashboard/widgets`);
      if (response && response.ok) {
        const data = await response.json();
        setAvailableWidgets(data.available_widgets);
        
        // Set default dashboard layout
        const currentLayout = settings?.dashboard_widgets || data.layouts.default;
        setDashboardWidgets(currentLayout);
      }
    } catch (error) {
      console.error('Error loading widgets:', error);
    }
  };

  const compareSeasons = async (season1, season2) => {
    if (!season1 || !season2) return;
    try {
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/seasons/compare/${encodeURIComponent(season1)}/${encodeURIComponent(season2)}`);
      if (response && response.ok) {
        const data = await response.json();
        setSeasonComparison(data);
      }
    } catch (error) {
      console.error('Error comparing seasons:', error);
    }
  };

  const createBackup = async () => {
    try {
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/backup/create`);
      if (response && response.ok) {
        const data = await response.json();
        if (data.success) {
          setBackupData(data.backup);
          
          // Auto-download backup
          const dataStr = JSON.stringify(data.backup, null, 2);
          const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
          const exportFileDefaultName = `subbuteo_backup_${new Date().toISOString().split('T')[0]}.json`;
          
          const linkElement = document.createElement('a');
          linkElement.setAttribute('href', dataUri);
          linkElement.setAttribute('download', exportFileDefaultName);
          linkElement.click();
          
          alert('✅ Backup creato e scaricato con successo!');
        }
      }
    } catch (error) {
      console.error('Error creating backup:', error);
      alert('❌ Errore nella creazione del backup');
    }
  };

  const restoreFromBackup = async (backupFile) => {
    try {
      const fileContent = await backupFile.text();
      const backupData = JSON.parse(fileContent);
      
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/backup/restore`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(backupData)
      });
      
      if (response) {
        const result = await response.json();
        if (result.success) {
          const restoredCounts = result.restored_counts || {};
          const countsText = Object.entries(restoredCounts)
            .map(([key, count]) => `${key}: ${count}`)
            .join(', ');
          
          alert(`✅ Backup ripristinato con successo!\n\nDati ripristinati: ${countsText}\n\nRicaricando l'applicazione...`);
          // Reload all data
          await loadAllData();
        } else {
          alert('❌ Errore nel ripristino del backup: ' + (result.detail || 'Errore sconosciuto'));
        }
      }
    } catch (error) {
      console.error('Error restoring backup:', error);
      alert('❌ Errore nel ripristino del backup: ' + error.message);
    }
  };

  const loadSettings = async () => {
    try {
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/settings`);
      if (response) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  // 🌐 PROFILO PUBBLICO FUNCTIONS
  const loadProfiloStatus = async () => {
    try {
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/user/profilo-status`);
      if (response && response.ok) {
        const data = await response.json();
        setProfiloPublicEnabled(data.profilo_pubblico || false);
        // Estrai nome dall'URL del profilo (es: /profilo/NomeUtente)
        if (data.profile_url) {
          const username = data.profile_url.split('/profilo/')[1];
          setProfiloUsername(username || '');
        }
      }
    } catch (error) {
      console.error('Error loading profilo status:', error);
    }
  };

  const toggleProfiloPublico = async () => {
    setTogglingProfilo(true);
    try {
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/user/profilo-pubblico`, {
        method: 'PUT'
      });
      if (response && response.ok) {
        const data = await response.json();
        setProfiloPublicEnabled(data.profilo_pubblico);
        showToastNotification(data.message, 'success');
      } else {
        showToastNotification('Errore nell\'aggiornamento', 'error');
      }
    } catch (error) {
      console.error('Error toggling profilo:', error);
      showToastNotification('Errore nell\'aggiornamento', 'error');
    }
    setTogglingProfilo(false);
  };

  // 🏢 USER CLUBS FUNCTIONS
  const loadUserClubs = async () => {
    setLoadingClubs(true);
    try {
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/user/clubs`);
      if (response) {
        const data = await response.json();
        setUserClubs(data.clubs || []);
      }
    } catch (error) {
      console.error('Error loading user clubs:', error);
    }
    setLoadingClubs(false);
  };

  const joinClubWithCode = async () => {
    if (!joinClubCode.trim()) {
      setJoinClubError('Inserisci un codice');
      return;
    }
    
    setJoiningClub(true);
    setJoinClubError('');
    setJoinClubSuccess('');
    
    try {
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/user/clubs/join`, {
        method: 'POST',
        body: JSON.stringify({ code: joinClubCode.trim().toUpperCase() })
      });
      
      if (response) {
        const data = await response.json();
        
        if (response.ok) {
          setJoinClubSuccess(data.message || 'Ti sei unito al club!');
          setJoinClubCode('');
          loadUserClubs();
        } else {
          setJoinClubError(data.detail || 'Errore nell\'iscrizione');
        }
      }
    } catch (error) {
      console.error('Error joining club:', error);
      setJoinClubError('Errore di connessione');
    }
    
    setJoiningClub(false);
  };

  const leaveClub = async (clubId, clubName) => {
    showConfirm({
      title: 'Esci dal Club',
      message: `Vuoi uscire dal club "${clubName}"?`,
      confirmText: '🚪 Esci',
      cancelText: 'Annulla',
      variant: 'warning',
      onConfirm: async () => {
        try {
          const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/user/clubs/${clubId}`, {
            method: 'DELETE'
          });
          
          if (response && response.ok) {
            loadUserClubs();
            showToastNotification('✅ Sei uscito dal club');
          }
        } catch (error) {
          console.error('Error leaving club:', error);
        }
      }
    });
  };

  const loadGoalsTracking = async () => {
    try {
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/stats/goals-tracking`);
      if (response) {
        const data = await response.json();
        setGoalsTracking(data);
      }
    } catch (error) {
      console.error('Error loading goals tracking:', error);
    }
  };

  // NEW MUSEUM LOAD FUNCTIONS 🏟️
  const loadMuseumTeams = async () => {
    try {
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/team-museum`);
      if (response) {
        const data = await response.json();
        setMuseumTeams(data.teams || []);
      }
    } catch (error) {
      console.error('Error loading museum teams:', error);
    }
  };

  const loadMuseumOverview = async () => {
    try {
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/team-museum/stats/overview`);
      if (response) {
        const data = await response.json();
        setMuseumOverview(data);
      }
    } catch (error) {
      console.error('Error loading museum overview:', error);
    }
  };

  const uploadTeamPhoto = async (file) => {
    try {
      console.log('📸 Uploading team photo:', {
        name: file.name,
        size: `${(file.size / 1024 / 1024).toFixed(2)}MB`,
        type: file.type
      });
      
      const formData = new FormData();
      
      // Comprimi l'immagine prima dell'upload (800x800 max, 70% quality)
      const compressedFile = await compressImage(file, 800, 800, 0.7);
      console.log('✅ Photo compressed, uploading to server...');
      
      formData.append('file', compressedFile);
      
      const response = await fetch(`${BACKEND_URL}/api/team-museum/upload-photo`, {
        method: 'POST',
        body: formData,
      });
      
      const data = await response.json();
      if (response.ok) {
        console.log('✅ Photo uploaded successfully');
        return data.photo_url;
      } else {
        throw new Error(data.detail || 'Upload failed');
      }
    } catch (error) {
      console.error('❌ Error uploading photo:', error);
      showToastNotification('❌ Errore upload foto: ' + error.message, 'error');
      return null;
    }
  };

  const uploadProfilePhoto = async (file) => {
    try {
      console.log('📸 Uploading profile photo:', {
        name: file.name,
        size: `${(file.size / 1024 / 1024).toFixed(2)}MB`,
        type: file.type
      });
      
      const formData = new FormData();
      
      // Comprimi l'immagine profilo (400x400 max, 80% quality)
      const compressedFile = await compressImage(file, 400, 400, 0.8);
      console.log('✅ Profile photo compressed, uploading...');
      
      formData.append('file', compressedFile);
      
      const response = await fetch(`${BACKEND_URL}/api/profile/upload-photo`, {
        method: 'POST',
        body: formData,
      });
      
      const data = await response.json();
      if (response.ok) {
        return data.photo_url;
      } else {
        throw new Error(data.detail || 'Upload failed');
      }
    } catch (error) {
      console.error('Error uploading profile photo:', error);
      alert('Errore upload foto profilo: ' + error.message);
      return null;
    }
  };

  // 🍔 SMART HAMBURGER MENU FUNCTIONS
  const generateQuickStats = () => {
    if (!generalStats || !matches.length) return null;
    
    const today = new Date().toISOString().split('T')[0];
    const thisWeek = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    
    const todayMatches = matches.filter(m => m.match_date === today);
    const weekMatches = matches.filter(m => m.match_date >= thisWeek);
    
    // Helper per determinare il risultato considerando i piazzati
    const getOutcome = (m) => {
      if (m.has_piazzati && m.piazzati_miei !== null && m.piazzati_avversario !== null) {
        if (m.piazzati_miei > m.piazzati_avversario) return 'win';
        if (m.piazzati_miei < m.piazzati_avversario) return 'loss';
        return 'draw';
      }
      if (m.my_goals > m.opponent_goals) return 'win';
      if (m.my_goals < m.opponent_goals) return 'loss';
      return 'draw';
    };
    
    // Current streak calculation
    let currentStreak = 0;
    let streakType = 'none';
    
    if (matches.length > 0) {
      const sortedMatches = [...matches].sort((a, b) => new Date(b.match_date) - new Date(a.match_date));
      const lastResult = sortedMatches[0];
      
      streakType = getOutcome(lastResult);
      
      // Count consecutive same results
      for (const match of sortedMatches) {
        const matchOutcome = getOutcome(match);
        
        if (matchOutcome === streakType) {
          currentStreak++;
        } else {
          break;
        }
      }
    }
    
    // Conta vittorie considerando piazzati
    const countWins = (matchList) => matchList.filter(m => getOutcome(m) === 'win').length;
    
    return {
      today: {
        matches: todayMatches.length,
        goals: todayMatches.reduce((sum, m) => sum + m.my_goals, 0),
        wins: countWins(todayMatches)
      },
      week: {
        matches: weekMatches.length,
        winRate: weekMatches.length ? Math.round((countWins(weekMatches) / weekMatches.length) * 100) : 0
      },
      streak: {
        count: currentStreak,
        type: streakType,
        emoji: streakType === 'win' ? '🔥' : streakType === 'loss' ? '❄️' : '⚖️'
      },
      total: {
        matches: generalStats.total_matches,
        winRate: generalStats.win_percentage,
        goalAvg: generalStats.goals_per_match
      }
    };
  };

  const generateRecentActivity = () => {
    if (!matches.length) return [];
    
    const sortedMatches = [...matches]
      .sort((a, b) => new Date(b.match_date) - new Date(a.match_date))
      .slice(0, 3);
    
    return sortedMatches.map(match => {
      const date = new Date(match.match_date);
      const timeAgo = getTimeAgo(date);
      
      // Determina il risultato considerando i piazzati
      let result;
      if (match.has_piazzati && match.piazzati_miei !== null && match.piazzati_avversario !== null) {
        result = match.piazzati_miei > match.piazzati_avversario ? 'win' : 
                 match.piazzati_miei < match.piazzati_avversario ? 'loss' : 'draw';
      } else {
        result = match.my_goals > match.opponent_goals ? 'win' : 
                 match.my_goals < match.opponent_goals ? 'loss' : 'draw';
      }
      
      // Formatta il sottotitolo con piazzati se presenti
      let subtitle = `${match.my_goals}-${match.opponent_goals}`;
      if (match.has_piazzati) {
        subtitle += ` (${match.piazzati_miei}-${match.piazzati_avversario} p)`;
      }
      subtitle += ` • ${timeAgo}`;
      
      return {
        id: match.id,
        type: 'match',
        title: `${match.my_team} vs ${match.opponent_team}`,
        subtitle,
        result,
        icon: result === 'win' ? '🏆' : result === 'loss' ? '😞' : '🤝'
      };
    });
  };

  const generateNotifications = () => {
    const notifications = [];
    
    // Check daily challenges
    if (dailyChallenges && dailyChallenges.length > 0) {
      const activeChallenges = dailyChallenges.filter(c => !c.completed);
      if (activeChallenges.length > 0) {
        notifications.push({
          id: 'challenges',
          type: 'challenge',
          title: `${activeChallenges.length} obiettivi attivi`,
          subtitle: 'Completa gli obiettivi di allenamento!',
          icon: '🎯',
          count: activeChallenges.length,
          action: () => setActiveTab('sfide')
        });
      }
    }
    
    // Check if user hasn't played today
    const today = new Date().toISOString().split('T')[0];
    const todayMatches = matches.filter(m => m.match_date === today);
    
    if (todayMatches.length === 0 && matches.length > 0) {
      const daysSinceLastMatch = Math.floor((Date.now() - new Date(matches[0]?.match_date)) / (1000 * 60 * 60 * 24));
      if (daysSinceLastMatch >= 1) {
        notifications.push({
          id: 'play_reminder',
          type: 'reminder',
          title: 'Gioca una partita oggi!',
          subtitle: `Ultima partita: ${daysSinceLastMatch} giorni fa`,
          icon: '⚽',
          action: () => setShowQuickAddModal(true)
        });
      }
    }
    
    // Check museum completion
    if (museumOverview && museumOverview.total_teams > 0) {
      const completionRate = (museumOverview.total_teams / 50) * 100; // Assuming 50 is a good collection size
      if (completionRate >= 80 && completionRate < 100) {
        notifications.push({
          id: 'museum_completion',
          type: 'achievement',
          title: 'Museum quasi completo!',
          subtitle: `${museumOverview.total_teams} squadre raccolte`,
          icon: '🏟️',
          action: () => setActiveTab('museum')
        });
      }
    }
    
    return notifications;
  };

  const generateMenuContext = () => {
    const hour = new Date().getHours();
    let greeting = 'Ciao';
    let suggestion = '';
    
    if (hour < 12) {
      greeting = 'Buongiorno';
      suggestion = 'Inizia la giornata con una partita!';
    } else if (hour < 18) {
      greeting = 'Buon pomeriggio';
      suggestion = 'Che ne dici di una sfida?';
    } else {
      greeting = 'Buonasera';  
      suggestion = 'Concludi la giornata in bellezza!';
    }
    
    // Context based on recent activity
    const quickStats = generateQuickStats();
    if (quickStats?.streak.count > 2 && quickStats.streak.type === 'win') {
      suggestion = `${quickStats.streak.emoji} Sei in striscia! Continua così!`;
    } else if (quickStats?.today.matches === 0 && matches.length > 0) {
      suggestion = 'Hai voglia di giocare oggi?';
    }
    
    return {
      greeting,
      suggestion,
      user: settings?.player_name || 'Giocatore'
    };
  };

  const getTimeAgo = (date) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 60) {
      return `${diffInMinutes}m fa`;
    } else if (diffInMinutes < 1440) {
      return `${Math.floor(diffInMinutes / 60)}h fa`;
    } else {
      return `${Math.floor(diffInMinutes / 1440)}g fa`;
    }
  };

  const handleQuickAction = (action) => {
    setMobileMenuOpen(false);
    
    switch (action) {
      case 'add_match':
        setShowQuickAddModal(true);
        break;
      case 'add_team':
        setShowTeamForm(true);
        break;
      case 'view_stats':
        setActiveTab('stats');
        break;
      case 'view_challenges':
        setActiveTab('challenges');
        break;
      case 'export_pdf':
        exportAdvancedReport();
        break;
      case 'backup':
        exportCompleteData();
        break;
      case 'settings':
        setActiveTab('settings');
        break;

      case 'logout':
        onLogout();
        break;
      default:
        console.log('Unknown action:', action);
    }
  };

  // Update menu data when relevant state changes
  useEffect(() => {
    if (matches.length > 0 || generalStats) {
      setQuickStats(generateQuickStats());
      setRecentActivity(generateRecentActivity());
      setNotifications(generateNotifications());
      setMenuContext(generateMenuContext());
    }
  }, [menuContext]);

  // 🍔 GESTURE & KEYBOARD SUPPORT
  useEffect(() => {
    let startX = 0;
    let startY = 0;
    
    const handleTouchStart = (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
    };
    
    const handleTouchEnd = (e) => {
      if (!e.changedTouches[0]) return;
      
      const endX = e.changedTouches[0].clientX;
      const endY = e.changedTouches[0].clientY;
      const diffX = endX - startX;
      const diffY = endY - startY;
      
      // Swipe right to open menu (from left edge)
      if (diffX > 100 && Math.abs(diffY) < 100 && startX < 50 && !mobileMenuOpen) {
        setMobileMenuOpen(true);
      }
      
      // Swipe left to close menu
      if (diffX < -100 && Math.abs(diffY) < 100 && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };
    
    const handleKeyDown = (e) => {
      // Cmd/Ctrl + M to toggle menu
      if ((e.metaKey || e.ctrlKey) && e.key === 'm') {
        e.preventDefault();
        setMobileMenuOpen(!mobileMenuOpen);
      }
      
      // Cmd/Ctrl + N for quick add
      if ((e.metaKey || e.ctrlKey) && e.key === 'n') {
        e.preventDefault();
        setShowQuickAddModal(true);
      }
      
      // Escape to close menu/modals
      if (e.key === 'Escape') {
        setMobileMenuOpen(false);
        setShowQuickAddModal(false);
      }
    };
    
    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchend', handleTouchEnd);
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [mobileMenuOpen]);

  // Update menu data when relevant state changes

  // 🌅 DYNAMIC THEME SYSTEM - Alba/Tramonto
  const getUserLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setUserLocation(location);
          resolve(location);
        },
        (error) => {
          // Fallback to Rome coordinates if geolocation fails
          const fallbackLocation = { lat: 41.9028, lng: 12.4964 };
          setUserLocation(fallbackLocation);
          resolve(fallbackLocation);
        }
      );
    });
  };

  const calculateSunTimes = (lat, lng) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    // Simplified sunrise/sunset calculation for Italy
    const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / 86400000);
    const P = Math.asin(0.39795 * Math.cos(0.98563 * (dayOfYear - 173) * Math.PI / 180));
    
    // Calculate sunrise and sunset times
    const argument = -Math.tan(lat * Math.PI / 180) * Math.tan(P);
    let hourAngle;
    
    if (argument < -1) {
      hourAngle = Math.PI; // Polar day
    } else if (argument > 1) {
      hourAngle = 0; // Polar night  
    } else {
      hourAngle = Math.acos(argument);
    }
    
    const sunrise = 12 - hourAngle * 12 / Math.PI;
    const sunset = 12 + hourAngle * 12 / Math.PI;
    
    // Convert to actual time objects
    const sunriseTime = new Date(today);
    sunriseTime.setHours(Math.floor(sunrise), (sunrise % 1) * 60, 0, 0);
    
    const sunsetTime = new Date(today);
    sunsetTime.setHours(Math.floor(sunset), (sunset % 1) * 60, 0, 0);
    
    // Add some buffer times for gradual transitions
    const dawnTime = new Date(sunriseTime.getTime() - 30 * 60000); // 30 min before sunrise
    const duskTime = new Date(sunsetTime.getTime() + 30 * 60000); // 30 min after sunset
    
    return {
      dawn: dawnTime,
      sunrise: sunriseTime,
      sunset: sunsetTime,
      dusk: duskTime
    };
  };

  const getThemeForTime = (currentTime, sunTimes) => {
    if (!sunTimes) return 'light';
    
    const { dawn, sunrise, sunset, dusk } = sunTimes;
    
    if (currentTime >= dawn && currentTime <= sunrise) {
      return 'dawn'; // Transition to light
    } else if (currentTime > sunrise && currentTime < sunset) {
      return 'light'; // Full light
    } else if (currentTime >= sunset && currentTime <= dusk) {
      return 'dusk'; // Transition to dark
    } else {
      return 'dark'; // Full dark
    }
  };

  const updateAutoTheme = () => {
    if (theme !== 'auto' || !sunTimes) return;
    
    const now = new Date();
    const newTheme = getThemeForTime(now, sunTimes);
    
    // Map special themes to base themes
    const baseTheme = (newTheme === 'dawn' || newTheme === 'light') ? 'light' : 'dark';
    
    if (currentAutoTheme !== baseTheme) {
      setThemeTransition(true);
      setTimeout(() => {
        setCurrentAutoTheme(baseTheme);
        setTimeout(() => setThemeTransition(false), 1000);
      }, 300);
      
      // Show subtle notification
      if (newTheme === 'dawn') {
        console.log('🌅 Buongiorno! Tema passato a modalità chiara');
      } else if (newTheme === 'dusk') {
        console.log('🌆 Buonasera! Tema passato a modalità scura');
      }
    }
  };

  const initializeDynamicTheme = async () => {
    try {
      const location = await getUserLocation();
      const times = calculateSunTimes(location.lat, location.lng);
      setSunTimes(times);
      
      // Set initial theme
      if (theme === 'auto') {
        const now = new Date();
        const initialTheme = getThemeForTime(now, times);
        const baseTheme = (initialTheme === 'dawn' || initialTheme === 'light') ? 'light' : 'dark';
        setCurrentAutoTheme(baseTheme);
      }
    } catch (error) {
      console.warn('Could not initialize dynamic theme:', error);
      // Fallback to simple time-based theme
      const hour = new Date().getHours();
      setCurrentAutoTheme(hour >= 6 && hour <= 19 ? 'light' : 'dark');
    }
  };

  const getEffectiveTheme = () => {
    if (theme === 'auto') {
      return currentAutoTheme;
    }
    return theme;
  };

  const formatTimeToString = (date) => {
    return date.toLocaleTimeString('it-IT', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // Initialize dynamic theme on mount
  useEffect(() => {
    initializeDynamicTheme();
  }, []);

  // Update theme every minute when on auto mode
  useEffect(() => {
    if (theme === 'auto') {
      const interval = setInterval(updateAutoTheme, 60000); // Check every minute
      return () => clearInterval(interval);
    }
  }, [theme, sunTimes, currentAutoTheme]);

  const loadMatch = async (matchId) => {
    try {
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/matches/${matchId}`);
      if (response && response.ok) {
        const data = await response.json();
        return data;
      }
      return null;
    } catch (error) {
      console.error('Error loading match:', error);
      return null;
    }
  };

  const editMatch = async (matchId) => {
    const match = await loadMatch(matchId);
    if (match) {
      setEditingMatch(match);
      setFormData({
        my_team: match.my_team,
        opponent_team: match.opponent_team,
        opponent_name: match.opponent_name,
        my_goals: match.my_goals.toString(),
        opponent_goals: match.opponent_goals.toString(),
        match_date: match.match_date,
        tournament: match.tournament || '',
        my_museum_team_id: match.my_museum_team_id || '', // Add museum team ID to form
        // PIAZZATI
        has_piazzati: match.has_piazzati || false,
        piazzati_miei: match.piazzati_miei !== null && match.piazzati_miei !== undefined ? match.piazzati_miei.toString() : '',
        piazzati_avversario: match.piazzati_avversario !== null && match.piazzati_avversario !== undefined ? match.piazzati_avversario.toString() : ''
      });
      setShowEditForm(true);
      
      // Load museum teams for dropdown
      await loadMuseumTeams();
    }
  };

  const updateMatch = async (e) => {
    e.preventDefault();
    if (!editingMatch) return;
    
    setLoading(true);
    try {
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/matches/${editingMatch.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          my_goals: parseInt(formData.my_goals),
          opponent_goals: parseInt(formData.opponent_goals),
          // PIAZZATI
          has_piazzati: formData.has_piazzati || false,
          piazzati_miei: formData.has_piazzati && formData.piazzati_miei ? parseInt(formData.piazzati_miei) : null,
          piazzati_avversario: formData.has_piazzati && formData.piazzati_avversario ? parseInt(formData.piazzati_avversario) : null
        }),
      });

      if (response && response.ok) {
        // Reset form
        setFormData({
          my_team: '',
          opponent_team: '',
          opponent_name: '',
          my_goals: '',
          opponent_goals: '',
          match_date: new Date().toISOString().split('T')[0],
          tournament: '',
          my_museum_team_id: '',
          // PIAZZATI
          has_piazzati: false,
          piazzati_miei: '',
          piazzati_avversario: ''
        });
        
        setEditingMatch(null);
        setShowEditForm(false);
        
        // Reload all data
        await loadAllData();
        await loadAdvancedData();
        
        alert('Partita modificata con successo!');
      } else {
        alert('Errore nel modificare la partita');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Errore nel modificare la partita');
    } finally {
      setLoading(false);
    }
  };

  // 🔍 PHASE 2: DATA DIAGNOSTIC - Analyze Old vs New Team Structure (Enhanced with Modal)
  const diagnoseTeamData = () => {
    console.log('🔍 DIAGNOSTIC: Starting analysis...');
    
    if (!museumTeams || museumTeams.length === 0) {
      showToastNotification('⚠️ Nessuna squadra nel Museum da analizzare', 'warning');
      console.log('❌ No teams found in Museum');
      return;
    }
    
    console.log('🔍 DIAGNOSTIC: Analyzing Museum teams data structure...');
    
    const detailedAnalysis = [];
    let problematicTeams = 0;
    
    museumTeams.forEach((team, index) => {
      const diagnostic = {
        name: team.name,
        id: team.id,
        hasOldColors: !!(team.primary_color || team.secondary_color),
        hasNewColors: !!(team.base_color || team.inner_color),
        hasAllFields: !!(team.name && team.id),
        fieldCount: Object.keys(team).length,
        missingFields: [],
        colorFields: {
          old: {
            primary_color: team.primary_color || null,
            secondary_color: team.secondary_color || null
          },
          new: {
            base_color: team.base_color || null,
            inner_color: team.inner_color || null
          }
        },
        status: 'ok'
      };
      
      // Check for missing expected fields
      const expectedFields = ['id', 'name', 'country', 'category', 'team_type'];
      expectedFields.forEach(field => {
        if (!team[field]) diagnostic.missingFields.push(field);
      });
      
      // Determine status
      if (diagnostic.hasOldColors && !diagnostic.hasNewColors) {
        diagnostic.status = 'needs_migration';
        problematicTeams++;
      } else if (diagnostic.hasOldColors && diagnostic.hasNewColors) {
        diagnostic.status = 'mixed_format';
        problematicTeams++;
      } else if (diagnostic.missingFields.length > 0) {
        diagnostic.status = 'missing_fields';
        problematicTeams++;
      }
      
      detailedAnalysis.push(diagnostic);
      
      // Log problematic teams
      if (diagnostic.status !== 'ok') {
        console.log(`⚠️ TEAM ${index + 1} - ${team.name}:`, diagnostic);
      }
    });
    
    // Summary
    const oldColorTeams = museumTeams.filter(t => t.primary_color || t.secondary_color);
    const newColorTeams = museumTeams.filter(t => t.base_color || t.inner_color);
    
    const analysis = {
      totalTeams: museumTeams.length,
      oldColorTeams: oldColorTeams.length,
      newColorTeams: newColorTeams.length,
      teamsNeedingMigration: oldColorTeams,
      problematicTeams,
      detailedAnalysis,
      summary: {
        needsMigration: detailedAnalysis.filter(t => t.status === 'needs_migration').length,
        mixedFormat: detailedAnalysis.filter(t => t.status === 'mixed_format').length,
        missingFields: detailedAnalysis.filter(t => t.status === 'missing_fields').length,
        healthy: detailedAnalysis.filter(t => t.status === 'ok').length
      }
    };
    
    const summaryMessage = `📊 DIAGNOSTIC SUMMARY:
    - Total teams: ${analysis.totalTeams}
    - Teams needing migration: ${analysis.summary.needsMigration}
    - Teams with mixed format: ${analysis.summary.mixedFormat}
    - Teams with missing fields: ${analysis.summary.missingFields}
    - Healthy teams: ${analysis.summary.healthy}`;
    
    console.log(summaryMessage);
    
    // Store analysis data and show modal
    setDebugData(analysis);
    setShowDebugModal(true);
    
    // Show summary notification
    if (analysis.summary.needsMigration > 0) {
      showToastNotification(`🔍 Analisi: ${analysis.summary.needsMigration} squadre necessitano migrazione. Vedi dettagli nel modal.`, 'info');
    } else {
      showToastNotification('✅ Tutte le squadre hanno formato aggiornato!', 'success');
    }
    
    return analysis;
  };

  // 🛠️ PHASE 3: DATA MIGRATION - Fix Old Team Structure (Enhanced with Progress)
  const migrateOldTeams = async () => {
    console.log('🛠️ MIGRATION: Starting migration process...');
    
    // Run diagnosis first (without showing modal)
    if (!museumTeams || museumTeams.length === 0) {
      showToastNotification('⚠️ Nessuna squadra nel Museum da migrare', 'warning');
      return;
    }

    const teamsToMigrate = museumTeams.filter(team => 
      team.primary_color || team.secondary_color
    );
    
    if (teamsToMigrate.length === 0) {
      showToastNotification('✅ Tutti i dati sono già aggiornati!', 'success');
      return;
    }
    
    setMigrationInProgress(true);
    showToastNotification(`⏳ Migrazione ${teamsToMigrate.length} squadre...`, 'info');
    console.log(`🛠️ MIGRATION: Found ${teamsToMigrate.length} teams to migrate`);
    console.log('🛠️ MIGRATION: Teams to migrate:', teamsToMigrate.map(t => t.name));
    
    try {
      let migratedCount = 0;
      let failedCount = 0;
      const migrationResults = [];
      
      for (const team of teamsToMigrate) {
        console.log(`🔄 Migrating team: ${team.name}`, team);
        
        // Create migrated team data with better field mapping
        const migratedTeam = {
          name: team.name || 'Squadra Sconosciuta',
          photo_url: team.photo_url || null,
          base_color: team.base_color || team.primary_color || '#FFFFFF',
          inner_color: team.inner_color || team.secondary_color || '#000000',
          country: team.country || 'Italia',
          category: team.category || team.team_type || 'Club',
          team_type: team.team_type || team.category || 'Club',
          notes: team.notes || null,
          matches_played: team.matches_played || 0,
          wins: team.wins || 0,
          draws: team.draws || 0,
          losses: team.losses || 0,
          goals_for: team.goals_for || 0,
          goals_against: team.goals_against || 0,
          win_percentage: team.win_percentage || 0,
          goals_per_match: team.goals_per_match || 0,
          goals_against_per_match: team.goals_against_per_match || 0,
          created_at: team.created_at || new Date().toISOString(),
          user_id: team.user_id
        };
        
        console.log(`📝 Migrated data for ${team.name}:`, {
          old: { primary_color: team.primary_color, secondary_color: team.secondary_color },
          new: { base_color: migratedTeam.base_color, inner_color: migratedTeam.inner_color }
        });
        
        try {
          const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/team-museum/${team.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(migratedTeam)
          });
          
          if (response && response.ok) {
            const result = await response.json();
            migratedCount++;
            migrationResults.push({ team: team.name, success: true, id: team.id });
            console.log(`✅ Migrated successfully: ${team.name}`, result);
          } else {
            failedCount++;
            migrationResults.push({ team: team.name, success: false, error: `HTTP ${response?.status}`, id: team.id });
            console.log(`❌ Failed to migrate ${team.name}:`, response?.status);
          }
        } catch (error) {
          failedCount++;
          migrationResults.push({ team: team.name, success: false, error: error.message, id: team.id });
          console.log(`❌ Error migrating ${team.name}:`, error);
        }
        
        // Small delay to avoid overwhelming the server
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      console.log(`🛠️ MIGRATION COMPLETE: ${migratedCount} success, ${failedCount} failed`);
      console.log('Migration results:', migrationResults);
      
      if (migratedCount > 0) {
        showToastNotification(`✅ ${migratedCount} squadre migrate con successo!`, 'success');
        
        // Force reload all museum data multiple times to ensure consistency
        console.log('🔄 Reloading museum data after migration...');
        await loadMuseumTeams();
        
        // Additional reloads with delays to handle caching
        setTimeout(async () => {
          console.log('🔄 Second reload...');
          await loadMuseumTeams();
        }, 1000);
        
        setTimeout(async () => {
          console.log('🔄 Final reload...');
          await loadMuseumTeams();
        }, 2500);
        
      } else {
        showToastNotification('❌ Nessuna squadra migrata con successo', 'error');
      }
      
      if (failedCount > 0) {
        showToastNotification(`⚠️ ${failedCount} squadre non migrate`, 'warning');
      }
      
    } catch (error) {
      console.error('Migration error:', error);
      showToastNotification('❌ Errore nella migrazione', 'error');
    } finally {
      setMigrationInProgress(false);
    }
  };
  const deleteMuseumTeam = async (teamId) => {
    showToastNotification('⏳ Eliminando squadra...', 'info');
    
    try {
      console.log('🗑️ Deleting museum team:', teamId);
      
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/team-museum/${teamId}`, {
        method: 'DELETE'
      });

      if (response && response.ok) {
        console.log('✅ Team deleted successfully:', teamId);
        
        // Remove from local state immediately
        setMuseumTeams(prev => prev.filter(team => team.id !== teamId));
        
        showToastNotification('✅ Squadra eliminata!', 'success');
        
        // Reload museum data to update stats
        await loadMuseumTeams();
        
      } else {
        throw new Error(`HTTP ${response?.status || 'unknown'}`);
      }
    } catch (error) {
      console.error('Error deleting team:', error);
      showToastNotification('❌ Errore nell\'eliminazione', 'error');
    }
  };
  const saveMuseumTeam = async () => {
    if (!teamFormData.name.trim()) {
      showToastNotification('❌ Il nome della squadra è obbligatorio', 'error');
      return;
    }

    const loadingToastId = showToastNotification('⏳ Salvando squadra...', 'info');
    
    try {
      console.log('🏟️ Saving museum team:', teamFormData.name);
      
      // Optimize photo if it's a data URL and larger than expected
      let optimizedPhoto = teamFormData.photo_url;
      if (optimizedPhoto && optimizedPhoto.startsWith('data:') && optimizedPhoto.length > 50000) {
        console.log('🎨 Optimizing large photo...');
        // Create a smaller version for better performance
        optimizedPhoto = await new Promise((resolve) => {
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            // Set maximum dimensions
            const maxSize = 200;
            let { width, height } = img;
            
            if (width > height) {
              if (width > maxSize) {
                height = (height * maxSize) / width;
                width = maxSize;
              }
            } else {
              if (height > maxSize) {
                width = (width * maxSize) / height;
                height = maxSize;
              }
            }
            
            canvas.width = width;
            canvas.height = height;
            ctx.drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL('image/jpeg', 0.8));
          };
          img.src = optimizedPhoto;
        });
      }

      const teamData = {
        ...teamFormData,
        photo_url: optimizedPhoto,
        category: teamFormData.team_type // Ensure compatibility
      };

      const url = editingTeam 
        ? `${BACKEND_URL}/api/team-museum/${editingTeam.id}`
        : `${BACKEND_URL}/api/team-museum`;
      
      const method = editingTeam ? 'PUT' : 'POST';

      const response = await makeAuthenticatedRequest(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(teamData)
      });

      if (response && response.ok) {
        const savedTeam = await response.json();
        console.log('✅ Team saved successfully:', savedTeam);

        // Update local state efficiently
        if (editingTeam) {
          setMuseumTeams(prev => prev.map(team => 
            team.id === editingTeam.id ? savedTeam : team
          ));
          showToastNotification(`✅ ${teamData.name} aggiornata!`, 'success');
        } else {
          setMuseumTeams(prev => [...prev, savedTeam]);
          showToastNotification(`✅ ${teamData.name} aggiunta al Museum!`, 'success');
        }

        // Reset form and close
        setShowTeamForm(false);
        setEditingTeam(null);
        setTeamFormData({
          name: '',
          photo_url: '',
          base_color: '#FFFFFF',
          inner_color: '#000000',
          country: '',
          team_type: 'Club',
          notes: ''
        });

        // Reload museum data to get updated stats
        await loadMuseumTeams();
        
      } else {
        throw new Error(`HTTP ${response?.status || 'unknown'}`);
      }
    } catch (error) {
      console.error('Error saving team:', error);
      showToastNotification('❌ Errore nel salvare la squadra', 'error');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/matches`, {
        method: 'POST',
        body: JSON.stringify({
          ...formData,
          my_goals: parseInt(formData.my_goals),
          opponent_goals: parseInt(formData.opponent_goals),
          // PIAZZATI
          has_piazzati: formData.has_piazzati || false,
          piazzati_miei: formData.has_piazzati && formData.piazzati_miei ? parseInt(formData.piazzati_miei) : null,
          piazzati_avversario: formData.has_piazzati && formData.piazzati_avversario ? parseInt(formData.piazzati_avversario) : null
        }),
      });

      if (response && response.ok) {
        // Reset form
        setFormData({
          my_team: '',
          opponent_team: '',
          opponent_name: '',
          my_goals: '',
          opponent_goals: '',
          match_date: new Date().toISOString().split('T')[0],
          tournament: '',
          // Reset Museum associations
          my_museum_team_id: '',
          // Reset PIAZZATI
          has_piazzati: false,
          piazzati_miei: '',
          piazzati_avversario: ''
        });
        
        setTeamFormData({
          name: '',
          photo_url: '',
          primary_color: '#FFFFFF', 
          secondary_color: '#000000',
          inner_color: '#FFFFFF',
          base_color: '#000000',
          country: '',
          team_type: 'Club',  // Club or Nazionale
          notes: ''
        });
        
        // Reload all data
        await loadAllData();
        await loadAdvancedData();
        
        // Show celebration for win - considera i piazzati
        let result;
        if (formData.has_piazzati && formData.piazzati_miei && formData.piazzati_avversario) {
          result = parseInt(formData.piazzati_miei) > parseInt(formData.piazzati_avversario) ? 'victory' : 
                   parseInt(formData.piazzati_miei) < parseInt(formData.piazzati_avversario) ? 'defeat' : 'draw';
        } else {
          result = formData.my_goals > formData.opponent_goals ? 'victory' : 
                   formData.my_goals < formData.opponent_goals ? 'defeat' : 'draw';
        }
        if (result === 'victory') {
          showCelebration('🎉 VITTORIA! 🏆');
        }
        
        alert('Partita aggiunta con successo!');
      } else {
        alert('Errore nell\'aggiungere la partita');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Errore nell\'aggiungere la partita');
    } finally {
      setLoading(false);
    }
  };

  const showCelebration = (message) => {
    // Simple celebration - could be enhanced with confetti library
    const celebration = document.createElement('div');
    celebration.innerHTML = message;
    celebration.style.cssText = `
      position: fixed; top: 20px; left: 50%; transform: translateX(-50%);
      background: linear-gradient(45deg, #10B981, #059669);
      color: white; padding: 15px 30px; border-radius: 10px;
      font-size: 18px; font-weight: bold; z-index: 1000;
      animation: celebrationPop 3s ease-in-out forwards;
    `;
    document.body.appendChild(celebration);
    setTimeout(() => document.body.removeChild(celebration), 3000);
  };

  const deleteMatch = async (matchId) => {
    showConfirm({
      title: 'Elimina Partita',
      message: 'Sei sicuro di voler eliminare questa partita? L\'azione non può essere annullata.',
      confirmText: 'Elimina',
      cancelText: 'Annulla',
      variant: 'danger',
      onConfirm: async () => {
        try {
          const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/matches/${matchId}`, {
            method: 'DELETE',
          });

          if (response && response.ok) {
            await loadAllData();
            await loadAdvancedData();
            showToastNotification('✅ Partita eliminata con successo!');
          } else {
            showToastNotification('❌ Errore nell\'eliminare la partita');
          }
        } catch (error) {
          console.error('Error:', error);
          showToastNotification('❌ Errore nell\'eliminare la partita');
        }
      }
    });
  };

  const updateSettings = async (newSettings) => {
    try {
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/settings`, {
        method: 'POST',
        body: JSON.stringify(newSettings),
      });

      if (response && response.ok) {
        setSettings(newSettings);
        if (newSettings.theme !== theme) {
          setTheme(newSettings.theme);
          document.documentElement.setAttribute('data-theme', newSettings.theme);
        }
        // Only show alert for non-player-name changes to avoid spam
        if (!newSettings.player_name || newSettings.player_name === settings?.player_name) {
          alert('Impostazioni salvate!');
        }
      }
    } catch (error) {
      console.error('Error updating settings:', error);
    }
  };

  // Debounced function for player name updates
  const handlePlayerNameChange = (newName) => {
    setLocalPlayerName(newName);
    
    // Clear existing timeout
    if (playerNameTimeout) {
      clearTimeout(playerNameTimeout);
    }
    
    // Set new timeout for debounced save (750ms delay)
    const newTimeout = setTimeout(() => {
      if (settings) {
        const newSettings = {...settings, player_name: newName};
        // Silent update (no alert) for player name
        fetch(`${BACKEND_URL}/api/settings`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(newSettings),
        }).then(response => {
          if (response.ok) {
            setSettings(newSettings);
          }
        }).catch(error => {
          console.error('Error updating player name:', error);
        });
      }
    }, 750);
    
    setPlayerNameTimeout(newTimeout);
  };

  // IMPORT SYSTEM FUNCTIONS 📁
  const handleFileUpload = async (file) => {
    if (!file) return;
    
    // Validate file type
    const validTypes = ['.csv', '.xlsx', '.xls'];
    const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    
    if (!validTypes.includes(fileExtension)) {
      alert('File non supportato! Usa solo file CSV, Excel (.xlsx) o Excel (.xls)');
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('File troppo grande! Massimo 10MB consentito.');
      return;
    }
    
    setImportFile(file);
    setImportStep('preview');
    setImportProgress({ show: true, message: 'Analizzando il file...', percent: 20 });
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Get auth token
      const token = localStorage.getItem('subbuteo_token') || sessionStorage.getItem('subbuteo_token');
      
      const response = await fetch(`${BACKEND_URL}/api/import/upload-preview`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData,
      });
      
      if (response.ok) {
        const previewData = await response.json();
        setImportPreview(previewData);
        setImportProgress({ show: false, message: '', percent: 0 });
        setImportStep('preview');
      } else {
        const error = await response.json();
        alert(`Errore nell'analisi del file: ${error.detail || 'Errore sconosciuto'}`);
        setImportStep('upload');
        setImportProgress({ show: false, message: '', percent: 0 });
      }
    } catch (error) {
      console.error('Upload preview error:', error);
      alert('Errore durante l\'analisi del file. Riprova.');
      setImportStep('upload');
      setImportProgress({ show: false, message: '', percent: 0 });
    }
  };
  
  const executeImport = async () => {
    if (!importFile || !importPreview) return;
    
    setImportProgress({ show: true, message: 'Importando le partite...', percent: 50 });
    setImportStep('execute');
    
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('skip_errors', 'true');
      formData.append('replace_existing', replaceExistingData.toString());
      
      // Include custom mapping if provided
      const mappingToUse = customColumnMapping && Object.keys(customColumnMapping).length > 0 
        ? customColumnMapping 
        : importPreview.detected_columns;
      if (mappingToUse) {
        formData.append('column_mapping', JSON.stringify(mappingToUse));
      }
      
      const token = localStorage.getItem('subbuteo_token') || sessionStorage.getItem('subbuteo_token');
      
      const response = await fetch(`${BACKEND_URL}/api/import/execute`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData,
      });
      
      if (response.ok) {
        const result = await response.json();
        setImportResult(result);
        setImportProgress({ show: false, message: '', percent: 0 });
        setImportStep('result');
        setCustomColumnMapping({}); // Reset custom mapping
        setShowMappingEditor(false);
        
        if (result.imported_matches > 0) {
          await loadAllData();
          await loadAdvancedData();
        }
      } else {
        const error = await response.json();
        alert(`Errore nell'importazione: ${error.detail || 'Errore sconosciuto'}`);
        setImportStep('preview');
        setImportProgress({ show: false, message: '', percent: 0 });
      }
    } catch (error) {
      console.error('Import execution error:', error);
      alert('Errore durante l\'importazione. Riprova.');
      setImportStep('preview');
      setImportProgress({ show: false, message: '', percent: 0 });
    }
  };
  
  const resetImport = () => {
    setImportFile(null);
    setImportPreview(null);
    setImportResult(null);
    setImportStep('upload');
    setImportProgress({ show: false, message: '', percent: 0 });
    setCustomColumnMapping({});
    setShowMappingEditor(false);
  };
  
  const downloadTemplate = () => {
    window.open(`${BACKEND_URL}/api/import/template`, '_blank');
  };

  // NEW TROFEI & ANALYTICS FUNCTIONS 🏆🧠
  const loadTrophies = async () => {
    try {
      console.log('🏆 Loading trophies...');
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/trophies/all`);
      
      if (response && response.ok) {
        const data = await response.json();
        console.log('✅ Trophies loaded:', data);
        
        setAllTrophies(data.trophies || []);
      } else {
        console.error('Failed to load trophies');
      }
    } catch (error) {
      console.error('Error loading trophies:', error);
    }
  };

  const loadDailyChallenges = async () => {
    setLoadingStates(prev => ({ ...prev, challenges: true }));
    showToastNotification('🔄 Aggiornamento obiettivi in corso...', 'info');
    
    try {
      console.log('🎯 Loading daily challenges...');
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/challenges/daily`);
      
      if (response && response.ok) {
        const data = await response.json();
        console.log('✅ Daily challenges loaded:', data);
        
        setDailyChallenges(data.challenges || []);
        
        showToastNotification(`✅ ${(data.challenges || []).length} obiettivi aggiornati!`, 'success');
      } else {
        showToastNotification('❌ Errore nel caricare gli obiettivi', 'error');
      }
    } catch (error) {
      console.error('Error loading daily challenges:', error);
      showToastNotification('❌ Errore di connessione', 'error');
    } finally {
      setLoadingStates(prev => ({ ...prev, challenges: false }));
    }
  };

  const loadAiInsights = async () => {
    setLoadingStates(prev => ({ ...prev, aiInsights: true }));
    showToastNotification('🔄 Aggiornamento AI Insights in corso...', 'info');
    
    try {
      console.log('🧠 Loading AI insights...');
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/analytics/ai-insights`);
      
      if (response && response.ok) {
        const data = await response.json();
        console.log('✅ AI insights loaded:', data);
        
        setAiInsights(data);
        
        const insightsCount = (data.insights || []).length + (data.recommendations || []).length;
        showToastNotification(`🧠 ${insightsCount} insights aggiornati!`, 'success');
      } else {
        showToastNotification('❌ Errore nel caricare AI Insights', 'error');
      }
    } catch (error) {
      console.error('Error loading AI insights:', error);
      showToastNotification('❌ Errore di connessione', 'error');
    } finally {
      setLoadingStates(prev => ({ ...prev, aiInsights: false }));
    }
  };

  const loadAdvancedAnalytics = async () => {
    try {
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/analytics/advanced-stats`);
      if (response && response.ok) {
        const data = await response.json();
        setAdvancedStats(data);
      }
    } catch (error) {
      console.error('Error loading advanced analytics:', error);
    }
  };

  // 💳 PAYMENT & SUBSCRIPTION FUNCTIONS
  const loadSubscriptionStatus = async () => {
    try {
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/subscription/status`);
      if (response && response.ok) {
        const data = await response.json();
        
        // Calculate days_remaining from end_date if not provided
        if (data.subscription && data.subscription.end_date && !data.days_remaining) {
          const endDate = new Date(data.subscription.end_date);
          const now = new Date();
          const timeDiff = endDate.getTime() - now.getTime();
          const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
          data.days_remaining = Math.max(0, daysDiff);
        }
        
        console.log('🔍 Subscription Status loaded:', data); // Debug log
        setSubscriptionStatus(data);
      } else {
        console.error('❌ Failed to load subscription status');
      }
    } catch (error) {
      console.error('Error loading subscription status:', error);
    }
  };

  const createPayment = async () => {
    try {
      setPaymentProcessing(true);
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/subscription/create-payment`, {
        method: 'POST'
      });
      
      if (response && response.ok) {
        const data = await response.json();
        if (data.success && data.approval_url) {
          // Redirect to PayPal
          window.location.href = data.approval_url;
        }
      } else {
        alert('❌ Errore nella creazione del pagamento');
      }
    } catch (error) {
      console.error('Error creating payment:', error);
      alert('❌ Errore nella creazione del pagamento');
    } finally {
      setPaymentProcessing(false);
    }
  };

  const executePayment = async (paymentId, payerId) => {
    try {
      console.log('🔄 Executing PayPal payment:', { paymentId, payerId });
      
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/subscription/execute-payment`, {
        method: 'POST',
        body: JSON.stringify({ paymentId, PayerID: payerId })
      });
      
      console.log('📡 Payment response:', response);
      
      if (response && response.ok) {
        const data = await response.json();
        console.log('✅ Payment data:', data);
        
        if (data.success) {
          setShowPaymentSuccess(true);
          await loadSubscriptionStatus(); // Refresh subscription status
          console.log('✅ Payment completed successfully!');
          return true;
        } else {
          console.warn('⚠️ Payment response OK but success=false');
        }
      } else {
        console.error('❌ Payment response not OK:', response);
      }
      return false;
    } catch (error) {
      console.error('❌ Error executing payment:', error);
      return false;
    }
  };

  const cancelSubscription = async () => {
    showConfirm({
      title: 'Cancella Abbonamento',
      message: 'Sei sicuro di voler cancellare il tuo abbonamento? Perderai l\'accesso alle funzionalità premium.',
      confirmText: '❌ Cancella',
      cancelText: 'Mantieni',
      variant: 'danger',
      onConfirm: async () => {
        try {
          const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/subscription/cancel`, {
            method: 'POST'
          });
          
          if (response && response.ok) {
            showToastNotification('✅ Abbonamento cancellato con successo');
            await loadSubscriptionStatus();
          } else {
            showToastNotification('❌ Errore nella cancellazione dell\'abbonamento');
          }
        } catch (error) {
          console.error('Error canceling subscription:', error);
          showToastNotification('❌ Errore nella cancellazione dell\'abbonamento');
        }
      }
    });
  };

  // 📅 CALENDAR FUNCTIONS
  const loadCalendarEvents = async (year = null, month = null) => {
    try {
      let url = `${BACKEND_URL}/api/calendar/events`;
      const params = new URLSearchParams();
      
      if (year) params.append('year', year);
      if (month) params.append('month', month);
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await makeAuthenticatedRequest(url);
      if (response && response.ok) {
        const data = await response.json();
        setCalendarEvents(data.events || []);
      }
    } catch (error) {
      console.error('Error loading calendar events:', error);
    }
  };


  // Load calendar notification preferences
  const loadCalendarNotifications = async () => {
    try {
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/calendar/notification-preferences`);
      if (response && response.ok) {
        const data = await response.json();
        setCalendarNotifications(data);
      }
    } catch (error) {
      console.error('Error loading calendar notifications:', error);
    }
  };

  // Save calendar notification preferences
  const saveCalendarNotifications = async (preferences) => {
    try {
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/calendar/notification-preferences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(preferences)
      });
      
      if (response && response.ok) {
        const data = await response.json();
        setCalendarNotifications(preferences);
        alert('✅ Preferenze notifiche salvate con successo!');
        return true;
      } else {
        alert('❌ Errore nel salvataggio delle preferenze');
        return false;
      }
    } catch (error) {
      console.error('Error saving calendar notifications:', error);
      alert('❌ Errore nel salvataggio delle preferenze');
      return false;
    }
  };

  // 💾 BACKUP REMINDER FUNCTIONS
  const loadBackupReminder = async () => {
    try {
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/backup/reminder-preferences`);
      if (response && response.ok) {
        const data = await response.json();
        setBackupReminder(data);
      }
    } catch (error) {
      console.error('Error loading backup reminder:', error);
    }
  };

  const saveBackupReminder = async (preferences) => {
    try {
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/backup/reminder-preferences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(preferences)
      });
      
      if (response && response.ok) {
        setBackupReminder(preferences);
        alert('✅ Preferenze promemoria backup salvate!');
        return true;
      } else {
        alert('❌ Errore nel salvataggio');
        return false;
      }
    } catch (error) {
      console.error('Error saving backup reminder:', error);
      alert('❌ Errore nel salvataggio');
      return false;
    }
  };

  // 📥 EXPORT ALL DATA FUNCTION
  const exportAllData = async () => {
    setIsExporting(true);
    try {
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/backup/create`);
      
      if (response && response.ok) {
        const data = await response.json();
        
        if (data.success && data.backup) {
          // Create downloadable file
          const blob = new Blob([JSON.stringify(data.backup, null, 2)], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          
          // Generate filename with date
          const today = new Date().toISOString().split('T')[0];
          link.download = `my_subbuteo_backup_${today}.json`;
          link.href = url;
          link.click();
          
          URL.revokeObjectURL(url);
          
          alert(`✅ Backup scaricato con successo!\n\n📊 Contenuto:\n• ${data.backup.stats?.total_matches || 0} partite\n• ${data.backup.stats?.total_museum_teams || 0} squadre museum\n• ${data.backup.stats?.total_calendar_events || 0} eventi calendario\n• ${data.backup.stats?.total_trophies || 0} trofei`);
        } else {
          alert('❌ Errore nella creazione del backup');
        }
      } else {
        alert('❌ Errore nel download del backup');
      }
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('❌ Errore nel download del backup');
    } finally {
      setIsExporting(false);
    }
  };


  const createCalendarEvent = async (eventData) => {
    try {
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/calendar/events`, {

        method: 'POST',
        body: JSON.stringify(eventData)
      });
      
      if (response && response.ok) {
        const data = await response.json();
        if (data.success) {
          await loadCalendarEvents(); // Load all events without date filter
          setShowAddEventModal(false);
          setEventFormData({
            name: '',
            date: '',
            time: '',
            category: 'training',
            description: ''
          });
          showToastNotification('✅ Evento aggiunto al calendario!', 'success');
          return true;
        }
      } else {
        const errorData = await response.json();
        showToastNotification(`❌ Errore: ${errorData.detail}`, 'error');
      }
      return false;
    } catch (error) {
      console.error('Error creating calendar event:', error);
      showToastNotification('❌ Errore nella creazione dell\'evento', 'error');
      return false;
    }
  };

  const updateCalendarEvent = async (eventId, eventData) => {
    try {
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/calendar/events/${eventId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(eventData)
      });

      if (response && response.ok) {
        await loadCalendarEvents(); // Refresh events list
        showToastNotification('Evento aggiornato con successo! 📅');
      } else {
        throw new Error('Errore nell\'aggiornamento dell\'evento');
      }
    } catch (error) {
      console.error('Error updating calendar event:', error);
      showToastNotification('❌ Errore nell\'aggiornamento dell\'evento');
    }
  };

  const deleteCalendarEvent = async (eventId) => {
    showConfirm({
      title: 'Elimina Evento',
      message: 'Sei sicuro di voler eliminare questo evento dal calendario?',
      confirmText: '🗑️ Elimina',
      cancelText: 'Annulla',
      variant: 'danger',
      onConfirm: async () => {
        try {
          const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/calendar/events/${eventId}`, {
            method: 'DELETE'
          });
          
          if (response && response.ok) {
            await loadCalendarEvents();
            showToastNotification('✅ Evento eliminato dal calendario!', 'success');
          } else {
            showToastNotification('❌ Errore nell\'eliminazione dell\'evento', 'error');
          }
        } catch (error) {
          console.error('Error deleting calendar event:', error);
          showToastNotification('❌ Errore nell\'eliminazione dell\'evento', 'error');
        }
      }
    });
  };

  // 📱 PWA INSTALL FUNCTION
  const installPWA = async () => {
    if (deferredInstallPrompt) {
      try {
        deferredInstallPrompt.prompt();
        const choiceResult = await deferredInstallPrompt.userChoice;
        
        if (choiceResult.outcome === 'accepted') {
          console.log('🎉 PWA: User accepted the install prompt');
          showToastNotification('📱 Installazione avviata...', 'info');
        } else {
          console.log('❌ PWA: User dismissed the install prompt');
          showToastNotification('ℹ️ Installazione annullata', 'info');
        }
        
        setDeferredInstallPrompt(null);
        setShowInstallPrompt(false);
      } catch (error) {
        console.error('Error installing PWA:', error);
        showToastNotification('❌ Errore nell\'installazione', 'error');
      }
    } else {
      // Fallback for iOS Safari or other browsers
      if (navigator.userAgent.match(/iPhone|iPad/i)) {
        showToastNotification('📱 Su iOS: Tocca il pulsante "Condividi" e seleziona "Aggiungi alla schermata Home"', 'info');
      } else {
        showToastNotification('ℹ️ Installazione non disponibile in questo browser', 'info');
      }
    }
  };

  // 🎨 IMAGE OPTIMIZATION FUNCTION FOR NEW MUSEUM
  const optimizeImage = (file, maxSize = 80) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions maintaining aspect ratio
        let { width, height } = img;
        const aspectRatio = width / height;
        
        if (width > height) {
          width = maxSize;
          height = maxSize / aspectRatio;
        } else {
          height = maxSize;
          width = maxSize * aspectRatio;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        const optimizedDataUrl = canvas.toDataURL('image/jpeg', 0.8);
        resolve(optimizedDataUrl);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  // 🏟️ MUSEUM TEAM CARD RENDERER - Helper function to render a single team card
  const renderMuseumTeamCard = (team) => {
    return (
      <>
        {/* Desktop Layout (md and up): Single Row Horizontal */}
        <div className="hidden md:flex items-center p-4">
          
          {/* Team Photo - 100x100 Size */}
          <div className="relative flex-shrink-0 mr-6">
            <div className="w-25 h-25 rounded-xl overflow-hidden border-2 border-gray-200/50 shadow-sm">
              {team.photo_url ? (
                <img
                  src={team.photo_url}
                  alt={team.name}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                  style={{ 
                    width: '100px', 
                    height: '100px',
                    objectFit: 'cover'
                  }}
                />
              ) : (
                <div className={`w-full h-full flex items-center justify-center ${
                  getEffectiveTheme() === 'dark' ? 'bg-gray-700' : 'bg-gray-100'
                }`} style={{ width: '100px', height: '100px' }}>
                  <span className="text-3xl">⚽</span>
                </div>
              )}
            </div>
          </div>

          {/* Team Info Section - Flex grows to take available space */}
          <div className="flex-grow min-w-0 mr-6">
            <div className="flex items-center gap-3 mb-2">
              {/* Team Name */}
              <h3 className={`text-xl font-bold truncate ${
                getEffectiveTheme() === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                {team.name}
              </h3>
              
              {/* Category Badge */}
              <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                team.team_type === 'Nazionale'
                  ? getEffectiveTheme() === 'dark'
                    ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                    : 'bg-green-100 text-green-700 border border-green-200'
                  : team.team_type === 'Fantasy'
                  ? getEffectiveTheme() === 'dark'
                    ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                    : 'bg-purple-100 text-purple-700 border border-purple-200'
                  : getEffectiveTheme() === 'dark'
                  ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                  : 'bg-blue-100 text-blue-700 border border-blue-200'
              }`}>
                {team.team_type === 'Nazionale' ? '🌍' : team.team_type === 'Fantasy' ? '✨' : '⚽'} {team.team_type}
              </span>
            </div>

            {/* Country */}
            {team.country && (
              <div className={`text-sm mb-1 ${
                getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                📍 {team.country}
              </div>
            )}

            {/* Colors */}
            <div className="flex items-center gap-2">
              <span className={`text-xs ${
                getEffectiveTheme() === 'dark' ? 'text-gray-500' : 'text-gray-500'
              }`}>
                Colori:
              </span>
              <div
                className="w-6 h-6 rounded-full border-2 border-white shadow-sm"
                style={{ backgroundColor: team.base_color || team.primary_color || '#FFFFFF' }}
                title="Colore Base"
              />
              <div
                className="w-6 h-6 rounded-full border-2 border-white shadow-sm"
                style={{ backgroundColor: team.inner_color || team.secondary_color || '#000000' }}
                title="Colore Interno"
              />
            </div>
          </div>

          {/* Statistics Section - Fixed width */}
          <div className="flex-shrink-0 mr-6">
            <div className="flex items-center gap-6">
              {/* Vittorie */}
              <div className="text-center">
                <div className="text-2xl font-bold text-green-500">
                  {team.wins || 0}
                </div>
                <div className={`text-xs uppercase tracking-wide ${
                  getEffectiveTheme() === 'dark' ? 'text-gray-500' : 'text-gray-400'
                }`}>
                  V
                </div>
              </div>

              {/* Pareggi */}
              <div className="text-center">
                <div className={`text-2xl font-bold ${
                  getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {team.draws || 0}
                </div>
                <div className={`text-xs uppercase tracking-wide ${
                  getEffectiveTheme() === 'dark' ? 'text-gray-500' : 'text-gray-400'
                }`}>
                  P
                </div>
              </div>

              {/* Sconfitte */}
              <div className="text-center">
                <div className="text-2xl font-bold text-red-500">
                  {team.losses || 0}
                </div>
                <div className={`text-xs uppercase tracking-wide ${
                  getEffectiveTheme() === 'dark' ? 'text-gray-500' : 'text-gray-400'
                }`}>
                  S
                </div>
              </div>
            </div>
          </div>

          {/* Win Percentage - Fixed width */}
          <div className="flex-shrink-0 mr-6">
            <div className={`text-center px-4 py-2 rounded-lg ${
              (team.win_rate || 0) >= 60
                ? 'bg-green-500/10 border border-green-500/20'
                : (team.win_rate || 0) >= 40
                ? 'bg-yellow-500/10 border border-yellow-500/20'
                : 'bg-red-500/10 border border-red-500/20'
            }`}>
              <div className={`text-2xl font-bold ${
                (team.win_rate || 0) >= 60
                  ? 'text-green-500'
                  : (team.win_rate || 0) >= 40
                  ? 'text-yellow-500'
                  : 'text-red-500'
              }`}>
                {(team.win_rate || 0).toFixed(0)}%
              </div>
              <div className={`text-xs uppercase tracking-wide ${
                getEffectiveTheme() === 'dark' ? 'text-gray-500' : 'text-gray-400'
              }`}>
                Vittorie
              </div>
            </div>
          </div>

          {/* Action Buttons - Fixed width */}
          <div className="flex-shrink-0 flex items-center gap-2">
            {/* Pulsante Modifica - Desktop */}
            <div className="flex-shrink-0">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingTeam(team);
                  setTeamFormData({
                    name: team.name || '',
                    photo_url: team.photo_url || '',
                    base_color: team.base_color || team.primary_color || '#FFFFFF',
                    inner_color: team.inner_color || team.secondary_color || '#000000',
                    country: team.country || '',
                    team_type: team.team_type || 'Club',
                    notes: team.notes || ''
                  });
                  setShowTeamForm(true);
                }}
                className="group/btn bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white px-3 py-2 rounded-lg transition-all duration-200 hover:shadow-lg hover:scale-105 font-medium text-sm flex items-center space-x-1"
              >
                <span className="group-hover/btn:rotate-12 transition-transform duration-200">✏️</span>
                <span>Modifica</span>
              </button>
            </div>

            {/* Pulsante Elimina - Desktop */}
            <div className="flex-shrink-0">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  showConfirm({
                    title: 'Elimina Squadra',
                    message: `Sei sicuro di voler eliminare "${team.name}"?`,
                    confirmText: '🗑️ Elimina',
                    cancelText: 'Annulla',
                    variant: 'danger',
                    onConfirm: () => deleteMuseumTeam(team.id)
                  });
                }}
                className="group/btn bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-3 py-2 rounded-lg transition-all duration-200 hover:shadow-lg hover:scale-105 font-medium text-sm flex items-center space-x-1"
              >
                <span className="group-hover/btn:rotate-12 transition-transform duration-200">🗑️</span>
                <span>Elimina</span>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Layout (below md): Two Row Layout */}
        <div className="md:hidden p-4">
          {/* Row 1: Photo + Name + Category Badge */}
          <div className="flex items-center mb-3">
            {/* Photo - Smaller on mobile */}
            <div className="relative flex-shrink-0 mr-3">
              <div className="w-16 h-16 rounded-lg overflow-hidden border border-gray-200/50 shadow-sm">
                {team.photo_url ? (
                  <img
                    src={team.photo_url}
                    alt={team.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    style={{ 
                      width: '64px', 
                      height: '64px',
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  <div className={`w-full h-full flex items-center justify-center ${
                    getEffectiveTheme() === 'dark' ? 'bg-gray-700' : 'bg-gray-100'
                  }`}>
                    <span className="text-xl">⚽</span>
                  </div>
                )}
              </div>
            </div>

            {/* Name + Badge */}
            <div className="flex-1 min-w-0">
              <h3 className={`text-base font-bold truncate mb-1 ${
                getEffectiveTheme() === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                {team.name}
              </h3>
              <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                team.team_type === 'Nazionale'
                  ? getEffectiveTheme() === 'dark'
                    ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                    : 'bg-green-100 text-green-700 border border-green-200'
                  : team.team_type === 'Fantasy'
                  ? getEffectiveTheme() === 'dark'
                    ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                    : 'bg-purple-100 text-purple-700 border border-purple-200'
                  : getEffectiveTheme() === 'dark'
                  ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                  : 'bg-blue-100 text-blue-700 border border-blue-200'
              }`}>
                {team.team_type === 'Nazionale' ? '🌍' : team.team_type === 'Fantasy' ? '✨' : '⚽'} {team.team_type}
              </span>
            </div>
          </div>

          {/* Row 2: Stats + Actions */}
          <div className="flex items-center justify-between">
            {/* Stats */}
            <div className="flex items-center gap-3">
              <div className="text-center">
                <div className="text-lg font-bold text-green-500">{team.wins || 0}</div>
                <div className={`text-xs ${getEffectiveTheme() === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>V</div>
              </div>
              <div className="text-center">
                <div className={`text-lg font-bold ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{team.draws || 0}</div>
                <div className={`text-xs ${getEffectiveTheme() === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>P</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-red-500">{team.losses || 0}</div>
                <div className={`text-xs ${getEffectiveTheme() === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>S</div>
              </div>
              <div className={`ml-2 px-2 py-1 rounded ${
                (team.win_rate || 0) >= 60
                  ? 'bg-green-500/10 text-green-500'
                  : (team.win_rate || 0) >= 40
                  ? 'bg-yellow-500/10 text-yellow-500'
                  : 'bg-red-500/10 text-red-500'
              }`}>
                <div className="text-sm font-bold">{(team.win_rate || 0).toFixed(0)}%</div>
              </div>
            </div>

            {/* Action Buttons - Mobile */}
            <div className="flex gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingTeam(team);
                  setTeamFormData({
                    name: team.name || '',
                    photo_url: team.photo_url || '',
                    base_color: team.base_color || team.primary_color || '#FFFFFF',
                    inner_color: team.inner_color || team.secondary_color || '#000000',
                    country: team.country || '',
                    team_type: team.team_type || 'Club',
                    notes: team.notes || ''
                  });
                  setShowTeamForm(true);
                }}
                className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-lg transition-colors text-sm"
              >
                ✏️
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  showConfirm({
                    title: 'Elimina Squadra',
                    message: `Sei sicuro di voler eliminare "${team.name}"?`,
                    confirmText: '🗑️ Elimina',
                    cancelText: 'Annulla',
                    variant: 'danger',
                    onConfirm: () => deleteMuseumTeam(team.id)
                  });
                }}
                className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg transition-colors text-sm"
              >
                🗑️
              </button>
            </div>
          </div>
        </div>

        {/* Subtle Hover Effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
      </>
    );
  };

  // 🔍 MUSEUM FILTER FUNCTION - Enhanced for new design
  const getFilteredMuseumTeams = () => {
    if (!museumTeams) return [];
    
    let filtered = museumTeams.filter(team => {
      const matchesSearch = !museumAdvancedFilter.search || 
        team.name?.toLowerCase().includes(museumAdvancedFilter.search.toLowerCase()) ||
        team.country?.toLowerCase().includes(museumAdvancedFilter.search.toLowerCase());
      
      const matchesCountry = !museumAdvancedFilter.country || 
        team.country === museumAdvancedFilter.country;
      
      // FIX: Usare team_type invece di category
      const matchesCategory = !museumAdvancedFilter.category || 
        team.team_type === museumAdvancedFilter.category;
      
      return matchesSearch && matchesCountry && matchesCategory;
    });

    // Sort results - case-insensitive alphabetical order
    filtered.sort((a, b) => {
      switch (museumAdvancedFilter.sortBy) {
        case 'win_rate':
          return (b.win_rate || 0) - (a.win_rate || 0);
        case 'total_matches':
          return (b.total_matches || 0) - (a.total_matches || 0);
        case 'name':
        default:
          // Case-insensitive alphabetical ordering
          return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
      }
    });

    return filtered;
  };

  const checkTrophiesProgress = async () => {
    setLoadingStates(prev => ({ ...prev, trophies: true }));
    showToastNotification('🔄 Verifica progressi in corso...', 'info');
    
    try {
      console.log('🏆 Checking trophies progress...');
      const response = await makeAuthenticatedRequest(`${BACKEND_URL}/api/trophies/check`, {
        method: 'POST'
      });
      
      if (response && response.ok) {
        const result = await response.json();
        console.log('✅ Trophies check result:', result);
        
        // Reload trophies data efficiently
        await loadTrophies();
        
        if (result.newly_earned && result.newly_earned.length > 0) {
          const newTrophyNames = result.newly_earned.map(t => t.trophy_id).join(', ');
          showToastNotification(`🏆 Nuovo Trofeo Sbloccato: ${newTrophyNames}!`, 'success');
        } else {
          showToastNotification('✅ Progressi aggiornati!', 'success');
        }
      } else {
        showToastNotification('❌ Errore nel verificare i progressi', 'error');
      }
    } catch (error) {
      console.error('Error checking trophies:', error);
      showToastNotification('❌ Errore di connessione', 'error');
    } finally {
      setLoadingStates(prev => ({ ...prev, trophies: false }));
    }
  };

  // Export functions
  const exportToCSV = (data, filename) => {
    const csv = Papa.unparse(data);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportAdvancedReport = () => {
    try {
      console.log('🔍 PDF Export: Starting PDF generation...');
      console.log('📊 Data available:', { 
        hasGeneralStats: !!generalStats, 
        hasStreaks: !!streaks, 
        hasMatches: !!matches,
        matchesCount: matches?.length || 0 
      });

      const doc = new jsPDF();
      const today = new Date().toLocaleDateString('it-IT');
      
      // Enhanced PDF with all stats
      doc.setFontSize(24);
      doc.text('📊 REPORT COMPLETO SUBBUTEO', 20, 25);
      doc.setFontSize(12);
      doc.text(`Generato il ${today}`, 20, 35);

      let yPosition = 50;

      // General Stats with safety checks
      if (generalStats && generalStats.total_matches !== undefined) {
        console.log('✅ Adding General Stats to PDF');
        doc.setFontSize(16);
        doc.text('🏆 Statistiche Generali', 20, yPosition);
        yPosition += 5;

        const totalMatches = generalStats.total_matches || 0;
        const goalsPerMatch = totalMatches > 0 ? (generalStats.goals_scored/totalMatches).toFixed(1) : '0.0';
        const goalsConcededPerMatch = totalMatches > 0 ? (generalStats.goals_conceded/totalMatches).toFixed(1) : '0.0';

        autoTable(doc, {
          startY: yPosition,
          head: [['Statistica', 'Valore', 'Dettaglio']],
          body: [
            ['Partite Totali', (generalStats.total_matches || 0).toString(), ''],
            ['Vittorie', (generalStats.wins || 0).toString(), `${generalStats.win_percentage || 0}%`],
            ['Pareggi', (generalStats.draws || 0).toString(), `${generalStats.draw_percentage || 0}%`],
            ['Sconfitte', (generalStats.losses || 0).toString(), `${generalStats.loss_percentage || 0}%`],
            ['Goal Fatti', (generalStats.goals_scored || 0).toString(), `${goalsPerMatch}/partita`],
            ['Goal Subiti', (generalStats.goals_conceded || 0).toString(), `${goalsConcededPerMatch}/partita`],
          ],
          margin: { left: 20 }
        });
        yPosition = doc.lastAutoTable.finalY + 15;
      } else {
        console.log('⚠️ No general stats available, adding placeholder');
        doc.setFontSize(16);
        doc.text('🏆 Statistiche Generali', 20, yPosition);
        doc.setFontSize(12);
        doc.text('Nessuna statistica disponibile - Aggiungi alcune partite!', 20, yPosition + 10);
        yPosition += 25;
      }

      // Matches summary if available
      if (matches && matches.length > 0) {
        console.log('✅ Adding Matches Summary to PDF');
        doc.setFontSize(16);
        doc.text('⚽ Ultime Partite', 20, yPosition);
        yPosition += 5;

        const recentMatches = matches.slice(-5).reverse(); // Last 5 matches
        const matchesData = recentMatches.map(match => {
          // Considera i piazzati per l'esito
          let esito;
          if (match.has_piazzati && match.piazzati_miei !== null && match.piazzati_avversario !== null) {
            esito = match.piazzati_miei > match.piazzati_avversario ? '🏆 V 🎯' : 
                    match.piazzati_miei < match.piazzati_avversario ? '❌ S 🎯' : '🤝 P 🎯';
          } else {
            esito = match.my_goals > match.opponent_goals ? '🏆 V' : 
                    match.my_goals < match.opponent_goals ? '❌ S' : '🤝 P';
          }
          return [
            new Date(match.match_date).toLocaleDateString('it-IT'),
            `${match.my_team} vs ${match.opponent_team}`,
            match.has_piazzati ? `${match.my_goals}-${match.opponent_goals} (🎯${match.piazzati_miei}-${match.piazzati_avversario})` : `${match.my_goals}-${match.opponent_goals}`,
            esito
          ];
        });

        autoTable(doc, {
          startY: yPosition,
          head: [['Data', 'Partita', 'Risultato', 'Esito']],
          body: matchesData,
          margin: { left: 20 }
        });
        yPosition = doc.lastAutoTable.finalY + 15;
      }

      // Streaks with safety checks
      if (streaks && yPosition < 250) {
        console.log('✅ Adding Streaks to PDF');
        doc.setFontSize(16);
        doc.text('🔥 Serie e Forma', 20, yPosition);
        yPosition += 5;

        autoTable(doc, {
          startY: yPosition,
          head: [['Tipo', 'Valore']],
          body: [
            ['Serie Attuale', `${streaks.current_streak?.count || 0} ${streaks.current_streak?.type || 'nessuna'}`],
            ['Miglior Serie Vincente', `${streaks.longest_win_streak || 0} partite`],
            ['Peggior Serie Perdente', `${streaks.longest_loss_streak || 0} partite`],
            ['Ultime 5 Partite', streaks.last_5_games?.join('-') || 'N/A']
          ],
          margin: { left: 20 }
        });
      }

      console.log('💾 Saving PDF...');
      doc.save('report-completo-subbuteo.pdf');
      console.log('✅ PDF Export completed successfully!');
      
      // Show success notification
      showToastNotification('📄 Report PDF scaricato con successo!', 'success');

    } catch (error) {
      console.error('❌ PDF Export Error:', error);
      showToastNotification('Errore durante la creazione del PDF: ' + error.message, 'error');
      alert('❌ Errore durante la creazione del PDF:\n\n' + error.message + '\n\nControlla la console per maggiori dettagli.');
    }
  };

  const exportMatchesToCSV = () => {
    const matchData = matches.map(match => ({
      Data: formatDate(match.match_date),
      'Squadra Mia': match.my_team,
      'Squadra Avversario': match.opponent_team,
      'Nome Avversario': match.opponent_name,
      'Goal Miei': match.my_goals,
      'Goal Avversario': match.opponent_goals,
      'Piazzati': match.has_piazzati ? `${match.piazzati_miei}-${match.piazzati_avversario}` : '',
      Risultato: getMatchResult(match.my_goals, match.opponent_goals, match),
      Torneo: match.tournament || 'Amichevole'
    }));
    exportToCSV(matchData, 'partite-subbuteo.csv');
  };

  const exportCompleteData = () => {
    const completeData = {
      partite: matches,
      statistiche_generali: generalStats,
      statistiche_avversari: opponentStats,
      performance_squadre: teamPerformance,
      serie: streaks,
      achievements: achievements
    };
    
    const jsonString = JSON.stringify(completeData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'backup-completo-subbuteo.json');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Chart data functions (existing + new)
  const getMonthlyTrendData = () => {
    const sortedMonthly = [...monthlyStats].sort((a, b) => a.month_key.localeCompare(b.month_key));
    
    return {
      labels: sortedMonthly.map(stat => stat.month),
      datasets: [
        {
          label: 'Vittorie',
          data: sortedMonthly.map(stat => stat.wins),
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(34, 197, 94, 0.2)',
          tension: 0.4,
        },
        {
          label: 'Pareggi',
          data: sortedMonthly.map(stat => stat.draws),
          borderColor: 'rgb(234, 179, 8)',
          backgroundColor: 'rgba(234, 179, 8, 0.2)',
          tension: 0.4,
        },
        {
          label: 'Sconfitte',
          data: sortedMonthly.map(stat => stat.losses),
          borderColor: 'rgb(239, 68, 68)',
          backgroundColor: 'rgba(239, 68, 68, 0.2)',
          tension: 0.4,
        }
      ]
    };
  };

  const getTeamPerformanceChartData = () => {
    const topTeams = teamPerformance.slice(0, 8);
    
    return {
      labels: topTeams.map(team => team.team),
      datasets: [
        {
          label: 'Percentuale Vittorie',
          data: topTeams.map(team => team.win_percentage),
          backgroundColor: topTeams.map(team => 
            team.win_percentage >= 70 ? 'rgba(34, 197, 94, 0.8)' :
            team.win_percentage >= 50 ? 'rgba(234, 179, 8, 0.8)' :
            'rgba(239, 68, 68, 0.8)'
          ),
        }
      ]
    };
  };

  const getWeekdayPerformanceData = () => {
    if (!temporalAnalysis?.weekday_analysis) return null;
    
    return {
      labels: temporalAnalysis.weekday_analysis.map(day => day.day),
      datasets: [{
        label: '% Vittorie per Giorno',
        data: temporalAnalysis.weekday_analysis.map(day => day.win_percentage),
        backgroundColor: 'rgba(59, 130, 246, 0.2)',
        borderColor: 'rgb(59, 130, 246)',
        borderWidth: 2,
        pointBackgroundColor: 'rgb(59, 130, 246)',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: 'rgb(59, 130, 246)'
      }]
    };
  };

  // Chart cleanup utility
  const chartCleanupRef = React.useRef(new Set());
  
  const safeChartRender = (ChartComponent, data, options, containerId) => {
    if (!data) return null;
    
    try {
      return React.createElement(ChartComponent, {
        data,
        options: {
          ...options,
          onDestroy: () => {
            chartCleanupRef.current.delete(containerId);
          },
          plugins: {
            ...options.plugins,
            legend: {
              ...options.plugins?.legend,
              labels: {
                ...options.plugins?.legend?.labels,
                usePointStyle: true,
              }
            }
          }
        },
        key: containerId,
      });
    } catch (error) {
      console.warn(`Chart render error for ${containerId}:`, error);
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-4xl mb-2">📊</div>
            <div className="text-gray-500">Errore caricamento grafico</div>
          </div>
        </div>
      );
    }
  };

  const getResultsDistributionData = () => {
    if (!generalStats) return null;
    
    return {
      labels: ['Vittorie', 'Pareggi', 'Sconfitte'],
      datasets: [
        {
          data: [generalStats.wins, generalStats.draws, generalStats.losses],
          backgroundColor: [
            'rgba(34, 197, 94, 0.8)',
            'rgba(234, 179, 8, 0.8)',
            'rgba(239, 68, 68, 0.8)'
          ],
          borderColor: [
            'rgb(34, 197, 94)',
            'rgb(234, 179, 8)',
            'rgb(239, 68, 68)'
          ],
          borderWidth: 2,
          hoverOffset: 4
        }
      ]
    };
  };

  // Helper: Determina il vero vincitore considerando i piazzati
  const getMatchOutcome = (match) => {
    // Se ha piazzati, il vincitore è determinato dai piazzati
    if (match.has_piazzati && match.piazzati_miei !== null && match.piazzati_avversario !== null) {
      if (match.piazzati_miei > match.piazzati_avversario) return 'win';
      if (match.piazzati_miei < match.piazzati_avversario) return 'loss';
      return 'draw'; // Raro: pareggio anche ai piazzati
    }
    // Altrimenti usa il risultato normale
    if (match.my_goals > match.opponent_goals) return 'win';
    if (match.my_goals < match.opponent_goals) return 'loss';
    return 'draw';
  };

  // Helper: Conta vittorie/pareggi/sconfitte considerando i piazzati
  const countMatchResults = (matchList) => {
    let wins = 0, draws = 0, losses = 0;
    matchList.forEach(m => {
      const outcome = getMatchOutcome(m);
      if (outcome === 'win') wins++;
      else if (outcome === 'loss') losses++;
      else draws++;
    });
    return { wins, draws, losses };
  };

  const getMatchResult = (myGoals, opponentGoals, match = null) => {
    // Se abbiamo l'oggetto match completo, usa la logica piazzati
    if (match && match.has_piazzati && match.piazzati_miei !== null && match.piazzati_avversario !== null) {
      if (match.piazzati_miei > match.piazzati_avversario) return 'Vittoria 🎯';
      if (match.piazzati_miei < match.piazzati_avversario) return 'Sconfitta 🎯';
      return 'Pareggio 🎯';
    }
    // Logica normale
    if (myGoals > opponentGoals) return 'Vittoria';
    if (myGoals < opponentGoals) return 'Sconfitta';
    return 'Pareggio';
  };

  const getResultColor = (myGoals, opponentGoals, match = null) => {
    // Se abbiamo l'oggetto match completo, usa la logica piazzati
    if (match && match.has_piazzati && match.piazzati_miei !== null && match.piazzati_avversario !== null) {
      if (match.piazzati_miei > match.piazzati_avversario) return 'text-green-600 bg-green-50';
      if (match.piazzati_miei < match.piazzati_avversario) return 'text-red-600 bg-red-50';
      return 'text-yellow-600 bg-yellow-50';
    }
    // Logica normale
    if (myGoals > opponentGoals) return 'text-green-600 bg-green-50';
    if (myGoals < opponentGoals) return 'text-red-600 bg-red-50';
    return 'text-yellow-600 bg-yellow-50';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT');
  };

  const getStreakIcon = (type) => {
    switch (type) {
      case 'win': return '🔥';
      case 'loss': return '❄️';
      case 'draw': return '🤝';
      default: return '⚽';
    }
  };

  // Helper function to get accent color classes
  const getAccentColor = (intensity = 500, prefix = 'text') => {
    const accentColor = settings?.accent_color || 'green';
    const colorMap = {
      green: `${prefix}-green-${intensity}`,
      blue: `${prefix}-blue-${intensity}`,
      red: `${prefix}-red-${intensity}`,
      purple: `${prefix}-purple-${intensity}`,
      orange: `${prefix}-orange-${intensity}`,
      pink: `${prefix}-pink-${intensity}`
    };
    return colorMap[accentColor] || `${prefix}-green-${intensity}`;
  };

  // Helper function to get dashboard card size
  const getDashboardCardSize = () => {
    if (!settings) return 'p-4';
    return settings.dashboard_style === 'compact' ? 'p-3' : 'p-4 sm:p-6';
  };

  // 🎨 SIDEBAR & NAVIGATION STATE
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // 📱 BOTTOM TAB BAR - Mobile Main Navigation (5 icons)

  return (
    <div className={`min-h-screen transition-all duration-1000 ${themeTransition ? 'opacity-75' : 'opacity-100'} ${
      getEffectiveTheme() === 'dark' ? 'bg-gray-900 text-white' : 'bg-gradient-to-br from-slate-50 to-slate-100'
    }`}>
      
      {/* 🖥️ DESKTOP SIDEBAR */}
      <aside className={`hidden md:block fixed left-0 top-0 h-full transition-all duration-300 z-40 flex flex-col ${
        sidebarCollapsed ? 'w-20' : 'w-64'
      } ${getEffectiveTheme() === 'dark' ? 'bg-gray-900/95 border-r border-gray-800' : 'bg-white/80 border-r border-slate-200'} backdrop-blur-xl`}>
        
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-200 flex-shrink-0">
          {!sidebarCollapsed && (
            <div className="flex items-center space-x-2">
              <span className="text-2xl">⚽</span>
              <span className={`font-bold text-sm ${getEffectiveTheme() === 'dark' ? 'text-white' : 'text-slate-800'}`}>MY SUBBUTEO</span>
            </div>
          )}
          {sidebarCollapsed && <span className="text-2xl mx-auto">⚽</span>}
        </div>

        <nav className="overflow-y-auto py-4 px-2" style={{maxHeight: 'calc(100vh - 200px)', scrollbarWidth: 'thin', scrollbarColor: getEffectiveTheme() === 'dark' ? '#4b5563 #1f2937' : '#cbd5e1 #f1f5f9'}}>
          {sidebarGroups.map((group, groupIndex) => (
            <div key={group.id} className="mb-4">
              {group.label && !sidebarCollapsed && (<div className="px-3 mb-2"><h3 className={`text-xs font-bold tracking-wide uppercase ${getEffectiveTheme() === 'dark' ? 'text-gray-500' : 'text-slate-400'}`}>{group.label}</h3></div>)}
              <div className="space-y-1">
                {group.items.map(item => (
                  <button key={item.id} onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }} className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-all duration-200 group ${activeTab === item.id ? getEffectiveTheme() === 'dark' ? 'bg-blue-500/20 text-blue-300 shadow-lg shadow-blue-500/20' : 'bg-gradient-to-r from-blue-100 to-purple-100 text-blue-700 shadow-md' : getEffectiveTheme() === 'dark' ? 'text-gray-400 hover:bg-gray-800 hover:text-white' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}`} title={sidebarCollapsed ? item.label : ''}>
                    <span className="text-xl flex-shrink-0">{item.icon}</span>
                    {!sidebarCollapsed && (<span className="text-sm font-medium truncate">{item.label}</span>)}
                    {activeTab === item.id && !sidebarCollapsed && (<span className="ml-auto">●</span>)}
                  </button>
                ))}
              </div>
              {groupIndex < sidebarGroups.length - 1 && (<div className={`my-3 border-t ${getEffectiveTheme() === 'dark' ? 'border-gray-800' : 'border-slate-200'}`} />)}
            </div>
          ))}
        </nav>

        {!sidebarCollapsed && (
          <div className="p-4 border-t border-slate-200 flex-shrink-0">
            <div className={`flex items-center space-x-3 p-3 rounded-lg ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-gradient-to-r from-blue-50 to-purple-50'}`}>
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-400 to-purple-400 flex items-center justify-center text-white font-bold">{user?.name?.charAt(0) || 'U'}</div>
              <div className="flex-1 min-w-0">
                <div className={`text-sm font-semibold truncate ${getEffectiveTheme() === 'dark' ? 'text-white' : 'text-slate-800'}`}>{user?.name || 'User'}</div>
                <div className="flex items-center space-x-1">
                  {subscriptionStatus?.plan_type === 'owner' ? (<span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">👑 Owner</span>) : (<span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">{subscriptionStatus?.days_remaining}g</span>)}
                  <span className="text-xs">{getEffectiveTheme() === 'dark' ? '🌙' : '☀️'}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </aside>

      {sidebarOpen && (
        <>
          <div className="md:hidden fixed inset-0 bg-black/50 z-40 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
          <aside className={`md:hidden fixed left-0 top-0 h-full w-64 z-50 flex flex-col transform transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} ${getEffectiveTheme() === 'dark' ? 'bg-gray-900 border-r border-gray-800' : 'bg-white border-r border-slate-200'}`}>
            <div className="h-16 flex items-center justify-between px-4 border-b border-slate-200 flex-shrink-0">
              <div className="flex items-center space-x-2"><span className="text-2xl">⚽</span><span className={`font-bold text-sm ${getEffectiveTheme() === 'dark' ? 'text-white' : 'text-slate-800'}`}>MY SUBBUTEO</span></div>
              <button onClick={() => setSidebarOpen(false)} className="p-2 rounded-lg hover:bg-slate-100">✕</button>
            </div>
            <nav className="overflow-y-auto py-4 px-2" style={{maxHeight: 'calc(100vh - 200px)', scrollbarWidth: 'thin', scrollbarColor: getEffectiveTheme() === 'dark' ? '#4b5563 #1f2937' : '#cbd5e1 #f1f5f9'}}>
              {sidebarGroups.map((group, groupIndex) => (<div key={group.id} className="mb-4">{group.label && (<div className="px-3 mb-2"><h3 className={`text-xs font-bold tracking-wide uppercase ${getEffectiveTheme() === 'dark' ? 'text-gray-500' : 'text-slate-400'}`}>{group.label}</h3></div>)}<div className="space-y-1">{group.items.map(item => (<button key={item.id} onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }} className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-all ${activeTab === item.id ? 'bg-gradient-to-r from-blue-100 to-purple-100 text-blue-700' : 'text-slate-600 hover:bg-slate-100'}`}><span className="text-xl">{item.icon}</span><span className="text-sm font-medium">{item.label}</span></button>))}</div>{groupIndex < sidebarGroups.length - 1 && (<div className="my-3 border-t border-slate-200" />)}</div>))}
            </nav>
            <div className="p-4 border-t border-slate-200 flex-shrink-0"><div className="flex items-center space-x-3 p-3 rounded-lg bg-gradient-to-r from-blue-50 to-purple-50"><div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-400 to-purple-400 flex items-center justify-center text-white font-bold">{user?.name?.charAt(0) || 'U'}</div><div className="flex-1"><div className="text-sm font-semibold text-slate-800">{user?.name || 'User'}</div><div className="text-xs text-slate-600">{subscriptionStatus?.days_remaining}g</div></div></div></div>
          </aside>
        </>
      )}

      <div className={`transition-all duration-300 ${sidebarCollapsed ? 'md:ml-20' : 'md:ml-64'}`}>
        
        <div className={`md:hidden sticky top-0 z-30 transition-colors duration-300 ${getEffectiveTheme() === 'dark' ? 'bg-gray-900/95 border-b border-gray-800' : 'bg-white/95 border-b border-slate-200'} backdrop-blur-xl`}>
          <div className="flex items-center justify-between px-4 h-16">
            <button onClick={() => setSidebarOpen(true)} className="p-2 rounded-lg hover:bg-slate-100"><svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg></button>
            <div className="flex items-center space-x-2"><span className="text-xl">⚽</span><span className="font-bold text-sm">MY SUBBUTEO</span></div>
            <div className="flex items-center space-x-2">
              {/* Premium Button Mobile */}
              {subscriptionStatus && subscriptionStatus.plan_type !== 'premium' && subscriptionStatus.plan_type !== 'owner' && (
                <button 
                  onClick={() => setShowUpgradeModal(true)} 
                  className="px-2 py-1 bg-gradient-to-r from-green-500 to-green-600 text-white text-xs font-medium rounded-lg shadow-sm"
                >
                  💎
                </button>
              )}
              {subscriptionStatus && (<span className={`text-xs px-2 py-1 rounded-full ${subscriptionStatus.plan_type === 'owner' ? 'bg-amber-100 text-amber-700' : subscriptionStatus.plan_type === 'premium' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>{subscriptionStatus.plan_type === 'owner' ? '👑' : subscriptionStatus.plan_type === 'premium' ? '💎' : `${subscriptionStatus.days_remaining}g`}</span>)}
              <button onClick={onLogout} className="p-2 rounded-lg hover:bg-slate-100 text-red-600">🚪</button>
            </div>
          </div>
        </div>

        <div className={`hidden md:block transition-colors duration-300 ${getEffectiveTheme() === 'dark' ? 'bg-gray-900/50 border-b border-gray-800' : 'bg-white/50 border-b border-slate-200'} backdrop-blur-xl sticky top-0 z-20`}>
          <div className="px-6 h-16 flex items-center justify-between">
            <div><h1 className={`text-xl font-bold ${getEffectiveTheme() === 'dark' ? 'text-white' : 'text-slate-800'}`}>{tabs.find(t => t.id === activeTab)?.label || 'Dashboard'}</h1></div>
            <div className="flex items-center space-x-3">
              {subscriptionStatus && subscriptionStatus.plan_type !== 'owner' && subscriptionStatus.plan_type !== 'premium' && (<button onClick={() => setShowUpgradeModal(true)} className="px-4 py-2 bg-gradient-to-r from-green-400 to-blue-400 hover:from-green-500 hover:to-blue-500 text-white text-sm font-medium rounded-lg transition-all shadow-md hover:shadow-lg">💎 Premium</button>)}
              {user?.plan_type === 'owner' && (<button onClick={onShowAdmin} className="px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors text-sm font-medium shadow-md">🔐 Admin</button>)}
              <button onClick={onLogout} className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors text-sm font-medium shadow-md">🚪 Logout</button>
            </div>
          </div>
        </div>

      <div className={`hidden transition-colors duration-300 ${
        getEffectiveTheme() === 'dark' ? 'bg-gray-800 text-white' : 'bg-green-700 text-white'
      }`}>
        <div className="max-w-7xl mx-auto px-4 py-4 sm:py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              {/* Profile Image */}
              {settings?.profile_image && (
                <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-white/30">
                  <img 
                    src={settings.profile_image} 
                    alt="Profile" 
                    className="w-full h-full object-cover"
                    onError={(e) => {e.target.style.display = 'none'}}
                  />
                </div>
              )}
              
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold">
                  ⚽ Statistiche Subbuteo Pro
                  {settings?.player_name && settings.player_name !== 'Giocatore' && (
                    <span className="text-lg font-normal ml-2 opacity-90">
                      - {settings.player_name}
                    </span>
                  )}
                </h1>
                <p className="text-green-100 mt-1 sm:mt-2 text-sm sm:text-base">
                  Dashboard avanzato con AI e analytics
                  {streaks?.current_streak && (
                    <span className="ml-2">
                      {getStreakIcon(streaks.current_streak.type)} 
                      {streaks.current_streak.count} {streaks.current_streak.type === 'win' ? 'vittorie' : 
                       streaks.current_streak.type === 'loss' ? 'sconfitte' : 'pareggi'} di fila
                    </span>
                  )}
                </p>
              </div>
            </div>
            
            {/* Desktop Actions */}
            <div className="hidden md:flex items-center space-x-4">
              {/* Subscription Status Badge */}
              {subscriptionStatus && (
                <div className="flex items-center space-x-2">
                  {subscriptionStatus.plan_type === 'owner' ? (
                    <span className="px-3 py-1 bg-gold-100 text-gold-800 text-xs font-medium rounded-full border border-gold-200">
                      👑 Owner ({subscriptionStatus.days_remaining}g)
                    </span>
                  ) : subscriptionStatus.plan_type === 'premium' ? (
                    <span className={`px-3 py-1 text-xs font-medium rounded-full border ${
                      subscriptionStatus.days_remaining <= 30 ? 'bg-orange-100 text-orange-800 border-orange-200' :
                      subscriptionStatus.days_remaining <= 60 ? 'bg-yellow-100 text-yellow-800 border-yellow-200' :
                      'bg-green-100 text-green-800 border-green-200'
                    }`}>
                      💎 Premium ({subscriptionStatus.days_remaining}g)
                    </span>
                  ) : subscriptionStatus.plan_type === 'free_trial' ? (
                    <div className="flex items-center space-x-2">
                      <span className={`px-3 py-1 text-xs font-medium rounded-full border ${
                        subscriptionStatus.days_remaining <= 3 ? 'bg-red-100 text-red-800 border-red-200' :
                        subscriptionStatus.days_remaining <= 7 ? 'bg-yellow-100 text-yellow-800 border-yellow-200' :
                        'bg-blue-100 text-blue-800 border-blue-200'
                      }`}>
                        🆓 Trial ({subscriptionStatus.days_remaining}g)
                      </span>
                      {subscriptionStatus.days_remaining <= 7 && (
                        <button
                          onClick={() => setShowUpgradeModal(true)}
                          className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-xs font-medium rounded-full transition-colors"
                        >
                          Upgrade €5/anno
                        </button>
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowUpgradeModal(true)}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      💎 Premium €5/anno
                    </button>
                  )}
                </div>
              )}

              {/* Days Remaining Badge (separate from Premium button) */}
              {subscriptionStatus && subscriptionStatus.days_remaining !== undefined && (
                <span className={`px-3 py-1 text-xs font-medium rounded-full border ${
                  subscriptionStatus.plan_type === 'owner' ? 'bg-gold-100 text-gold-800 border-gold-200' :
                  subscriptionStatus.plan_type === 'premium' ? (
                    subscriptionStatus.days_remaining <= 30 ? 'bg-orange-100 text-orange-800 border-orange-200' :
                    subscriptionStatus.days_remaining <= 60 ? 'bg-yellow-100 text-yellow-800 border-yellow-200' :
                    'bg-green-100 text-green-800 border-green-200'
                  ) : (
                    subscriptionStatus.days_remaining <= 3 ? 'bg-red-100 text-red-800 border-red-200' :
                    subscriptionStatus.days_remaining <= 7 ? 'bg-yellow-100 text-yellow-800 border-yellow-200' :
                    'bg-blue-100 text-blue-800 border-blue-200'
                  )
                }`}>
                  {subscriptionStatus.plan_type === 'owner' ? '👑' : 
                   subscriptionStatus.plan_type === 'premium' ? '💎' : '🆓'} {subscriptionStatus.days_remaining}g
                </span>
              )}

              {/* Admin Dashboard Button (only for owners) */}
              {user?.plan_type === 'owner' && (
                <button
                  onClick={onShowAdmin}
                  className="flex items-center space-x-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors duration-200 text-sm font-medium"
                >
                  <span>🔐</span>
                  <span>Admin</span>
                </button>
              )}

              {/* PWA Install Button */}
              {showInstallPrompt && (
                <button
                  onClick={installPWA}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200 text-sm font-medium"
                >
                  <span>📱</span>
                  <span>Installa App</span>
                </button>
              )}

              {/* Logout Button */}
              <button
                onClick={onLogout}
                className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors duration-200 text-sm font-medium"
              >
                <span>🚪</span>
                <span>Logout</span>
              </button>
            </div>
            
            {/* Smart Hamburger Menu Button */}
            <div className="relative">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-white p-2 rounded-md hover:bg-green-800 transition-colors duration-200"
                type="button"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {mobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
                
                {/* Notification Badge */}
                {notifications && notifications.length > 0 && (
                  <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {notifications.length}
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 🍔 SMART HAMBURGER DROPDOWN MENU */}
      {mobileMenuOpen && (
        <div className="relative z-50">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={() => setMobileMenuOpen(false)}
          />
          
          {/* Menu Panel */}
          <div className={`absolute right-4 top-0 w-80 max-w-[calc(100vw-2rem)] rounded-lg shadow-2xl border animate-in slide-in-from-top-2 duration-200 ${
            getEffectiveTheme() === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            
            {/* Header Section */}
            {menuContext && (
              <div className={`p-4 border-b ${getEffectiveTheme() === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex items-center space-x-3">
                  {settings?.profile_image ? (
                    <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-green-500">
                      <img src={settings.profile_image} alt="Profile" className="w-full h-full object-cover" />
                    </div>
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center text-white font-bold">
                      {menuContext.user.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1">
                    <div className={`font-semibold ${getEffectiveTheme() === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {menuContext.greeting}, {menuContext.user}!
                    </div>
                    <div className={`text-sm ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      {menuContext.suggestion}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Stats Section */}
            {quickStats && (
              <div className={`p-4 border-b ${getEffectiveTheme() === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                <h3 className={`text-sm font-semibold mb-3 ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  📊 Stats Rapide
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className={`p-3 rounded-lg ${getEffectiveTheme() === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <div className="text-lg font-bold text-green-600">{quickStats.today.matches}</div>
                    <div className={`text-xs ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Oggi</div>
                  </div>
                  <div className={`p-3 rounded-lg ${getEffectiveTheme() === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <div className="text-lg font-bold text-blue-600">{quickStats.week.winRate}%</div>
                    <div className={`text-xs ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Win Settimana</div>
                  </div>
                </div>
                
                {/* Current Streak */}
                {quickStats.streak.count > 0 && (
                  <div className={`mt-3 p-3 rounded-lg ${getEffectiveTheme() === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <div className="flex items-center space-x-2">
                      <span className="text-xl">{quickStats.streak.emoji}</span>
                      <div>
                        <div className="font-semibold text-sm">
                          {quickStats.streak.count} {quickStats.streak.type === 'win' ? 'Vittorie' : 
                           quickStats.streak.type === 'loss' ? 'Sconfitte' : 'Pareggi'} di fila
                        </div>
                        <div className={`text-xs ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                          Streak attuale
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Notifications Section */}
            {notifications && notifications.length > 0 && (
              <div className={`p-4 border-b ${getEffectiveTheme() === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                <h3 className={`text-sm font-semibold mb-3 ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  🔔 Notifiche ({notifications.length})
                </h3>
                <div className="space-y-2">
                  {notifications.slice(0, 3).map(notification => (
                    <div 
                      key={notification.id}
                      onClick={notification.action}
                      className={`p-3 rounded-lg cursor-pointer transition-colors hover:bg-opacity-80 ${
                        notification.type === 'challenge' ? 'bg-yellow-100 hover:bg-yellow-200' :
                        notification.type === 'reminder' ? 'bg-blue-100 hover:bg-blue-200' :
                        'bg-green-100 hover:bg-green-200'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <span className="text-lg">{notification.icon}</span>
                        <div className="flex-1">
                          <div className="font-medium text-sm text-gray-900">{notification.title}</div>
                          <div className="text-xs text-gray-600">{notification.subtitle}</div>
                        </div>
                        {notification.count && (
                          <div className="bg-orange-500 text-white text-xs px-2 py-1 rounded-full">
                            {notification.count}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Actions Section */}
            <div className={`p-4 border-b ${getEffectiveTheme() === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-sm font-semibold mb-3 ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                ⚡ Azioni Rapide
              </h3>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => handleQuickAction('add_match')}
                  className="flex items-center space-x-2 p-3 rounded-lg bg-green-500 text-white hover:bg-green-600 transition-colors"
                >
                  <span>➕</span>
                  <span className="text-sm font-medium">Partita</span>
                </button>
                <button
                  onClick={() => handleQuickAction('add_team')}
                  className="flex items-center space-x-2 p-3 rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition-colors"
                >
                  <span>🏟️</span>
                  <span className="text-sm font-medium">Squadra</span>
                </button>
                <button
                  onClick={() => handleQuickAction('view_stats')}
                  className="flex items-center space-x-2 p-3 rounded-lg bg-purple-500 text-white hover:bg-purple-600 transition-colors"
                >
                  <span>📊</span>
                  <span className="text-sm font-medium">Stats</span>
                </button>
                <button
                  onClick={() => handleQuickAction('view_challenges')}
                  className="flex items-center space-x-2 p-3 rounded-lg bg-orange-500 text-white hover:bg-orange-600 transition-colors"
                >
                  <span>🎯</span>
                  <span className="text-sm font-medium">Sfide</span>
                </button>
                <button
                  onClick={() => setShowCalendar(true)}
                  className="flex items-center space-x-2 p-3 rounded-lg bg-green-500 text-white hover:bg-green-600 transition-colors"
                >
                  <span>📅</span>
                  <span className="text-sm font-medium">Calendario Tornei</span>
                </button>
              </div>
            </div>

            {/* Recent Activity Section */}
            {recentActivity && recentActivity.length > 0 && (
              <div className={`p-4 border-b ${getEffectiveTheme() === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                <h3 className={`text-sm font-semibold mb-3 ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  📝 Attività Recenti
                </h3>
                <div className="space-y-2">
                  {recentActivity.slice(0, 3).map(activity => (
                    <div key={activity.id} className={`p-3 rounded-lg ${getEffectiveTheme() === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <div className="flex items-center space-x-3">
                        <span className="text-lg">{activity.icon}</span>
                        <div className="flex-1">
                          <div className={`font-medium text-sm ${getEffectiveTheme() === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            {activity.title}
                          </div>
                          <div className={`text-xs ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                            {activity.subtitle}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tools & Settings Section */}
            <div className="p-4">
              <h3 className={`text-sm font-semibold mb-3 ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                🛠️ Tools & Impostazioni
              </h3>
              <div className="space-y-2">
                {/* Premium Upgrade Button - Mobile */}
                {subscriptionStatus && subscriptionStatus.plan_type !== 'premium' && subscriptionStatus.plan_type !== 'owner' && (
                  <button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      setShowUpgradeModal(true);
                    }}
                    className="w-full flex items-center justify-between p-3 rounded-lg bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700 transition-colors shadow-md"
                  >
                    <div className="flex items-center space-x-3">
                      <span className="text-xl">💎</span>
                      <div className="text-left">
                        <div className="text-sm font-bold">Passa a Premium</div>
                        <div className="text-xs opacity-90">Solo €5/anno</div>
                      </div>
                    </div>
                    <span className="text-lg">→</span>
                  </button>
                )}
                
                <button
                  onClick={() => handleQuickAction('export_pdf')}
                  className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-colors ${
                    getEffectiveTheme() === 'dark' ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  <span>📄</span>
                  <span className="text-sm">Esporta PDF</span>
                </button>
                <button
                  onClick={() => handleQuickAction('backup')}
                  className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-colors ${
                    getEffectiveTheme() === 'dark' ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  <span>💾</span>
                  <span className="text-sm">Backup Completo</span>
                </button>
                <button
                  onClick={() => handleQuickAction('settings')}
                  className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-colors ${
                    getEffectiveTheme() === 'dark' ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  <span>⚙️</span>
                  <span className="text-sm">Settings Pro</span>
                </button>
                
                {/* System Actions & Logout */}
                <div className="border-t pt-3 mt-3 space-y-2">
                  {/* Admin Dashboard Button (only for owners) */}
                  {user?.plan_type === 'owner' && (
                    <button
                      onClick={() => {
                        setMobileMenuOpen(false);
                        onShowAdmin();
                      }}
                      className="w-full flex items-center space-x-3 p-3 rounded-lg bg-purple-600 hover:bg-purple-700 text-white transition-colors"
                    >
                      <span>🔐</span>
                      <span className="text-sm font-medium">Dashboard Admin</span>
                    </button>
                  )}
                  
                  <button
                    onClick={() => handleQuickAction('logout')}
                    className="w-full flex items-center space-x-3 p-3 rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors"
                  >
                    <span>🚪</span>
                    <span className="text-sm font-medium">Logout</span>
                  </button>
                </div>
                
                {/* Advanced Theme Control */}
                <div className="space-y-2">
                  <div className={`text-xs font-semibold uppercase tracking-wide ${
                    getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    🎨 Tema App
                  </div>
                  
                  {/* Theme Options */}
                  <div className="grid grid-cols-3 gap-1">
                    <button
                      onClick={() => {
                        setTheme('light');
                        localStorage.setItem('subbuteo_theme', 'light');
                      }}
                      className={`p-2 rounded-md text-xs font-medium transition-colors ${
                        theme === 'light' 
                          ? 'bg-yellow-400 text-yellow-900 shadow-md' 
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      ☀️ Chiaro
                    </button>
                    
                    <button
                      onClick={() => {
                        setTheme('auto');
                        localStorage.setItem('subbuteo_theme', 'auto');
                        initializeDynamicTheme();
                      }}
                      className={`p-2 rounded-md text-xs font-medium transition-colors ${
                        theme === 'auto'
                          ? 'bg-gradient-to-r from-yellow-400 to-blue-500 text-white shadow-md'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      🌅 Auto
                    </button>
                    
                    <button
                      onClick={() => {
                        setTheme('dark');
                        localStorage.setItem('subbuteo_theme', 'dark');
                      }}
                      className={`p-2 rounded-md text-xs font-medium transition-colors ${
                        getEffectiveTheme() === 'dark'
                          ? 'bg-gray-800 text-gray-200 shadow-md'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      🌙 Scuro
                    </button>
                  </div>
                  
                  {/* Theme Info */}
                  <div className={`text-xs ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    {theme === 'auto' ? (
                      <div className="space-y-1">
                        <div className="flex justify-between">
                          <span>Tema attuale:</span>
                          <span className="font-medium">
                            {currentAutoTheme === 'light' ? '☀️ Chiaro' : '🌙 Scuro'}
                          </span>
                        </div>
                        {sunTimes && (
                          <div className="flex justify-between">
                            <span>Prossimo cambio:</span>
                            <span className="font-mono text-xs">
                              {new Date().getHours() < 12 
                                ? `🌆 ${formatTimeToString(sunTimes.sunset)}`
                                : `🌅 ${formatTimeToString(sunTimes.sunrise)}`
                              }
                            </span>
                          </div>
                        )}
                        {userLocation && (
                          <div className="text-xs opacity-75">
                            📍 {userLocation.lat.toFixed(2)}, {userLocation.lng.toFixed(2)}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div>Tema manuale: {theme === 'light' ? '☀️ Chiaro' : '🌙 Scuro'}</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* OLD Navigation - HIDDEN (replaced by sidebar) */}
      <div className="hidden">
        <div className="max-w-7xl mx-auto px-4">
          {/* Desktop Navigation */}
          <div className="hidden md:flex overflow-x-auto space-x-4 py-2">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-shrink-0 py-2 px-3 border-b-2 font-medium text-xs transition-colors ${
                  activeTab === tab.id
                    ? 'border-green-500 text-green-600'
                    : `border-transparent hover:text-gray-700 ${
                        getEffectiveTheme() === 'dark' ? 'text-gray-300 hover:text-gray-200' : 'text-gray-500'
                      }`
                }`}
              >
                <span className="mr-1">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
            
            {/* Desktop Calendar Button */}
            <button
              onClick={() => setShowCalendar(true)}
              className="flex-shrink-0 py-2 px-3 border-b-2 border-transparent font-medium text-xs transition-colors text-green-600 hover:text-green-700 hover:border-green-300"
              title="Apri Calendario Tornei"
            >
              <span className="mr-1">📅</span>
              Calendario
            </button>
          </div>

          {/* Mobile Tabs Bar */}
          <div className="md:hidden flex overflow-x-auto py-2 space-x-1 scrollbar-hide">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  setMobileMenuOpen(false);
                }}
                className={`flex-shrink-0 py-2 px-3 text-xs font-medium rounded-full transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-green-100 text-green-700 border border-green-200'
                    : `hover:text-gray-700 ${
                        getEffectiveTheme() === 'dark' 
                          ? 'text-gray-300 hover:text-gray-200 bg-gray-700' 
                          : 'text-gray-500 bg-gray-100'
                      }`
                }`}
              >
                <span className="mr-1">{tab.icon}</span>
                {tab.shortLabel}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-4 sm:py-6 pb-24 md:pb-6">
        {loading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className={`p-6 rounded-lg ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Caricamento...</p>
            </div>
          </div>
        )}

        {/* DASHBOARD TAB 🏠 */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold">🏠 Dashboard Principale</h2>
                <p className={getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-600'}>
                  Panoramica completa delle tue performance
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={exportAdvancedReport}
                  className="bg-red-600 text-white px-3 py-2 rounded-md hover:bg-red-700 transition-colors text-sm font-medium"
                >
                  📄 Report PDF
                </button>
                <button
                  onClick={exportCompleteData}
                  className="bg-purple-600 text-white px-3 py-2 rounded-md hover:bg-purple-700 transition-colors text-sm font-medium"
                >
                  💾 Backup Completo
                </button>
              </div>
            </div>

            {/* Quick Stats Cards - Pastel Theme with Animations */}
            {generalStats && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                {/* Card 1: Partite - Pastel Blue */}
                <div className={`${getDashboardCardSize()} rounded-lg shadow-md border-2 animate-fade-in-up ${
                  getEffectiveTheme() === 'dark' 
                    ? 'bg-gray-800 border-blue-400/30' 
                    : 'bg-blue-50/50 border-blue-200 hover:shadow-blue-200/50'
                } transition-all duration-300 hover:scale-105`} style={{animationDelay: '0.1s'}}>
                  <div className={`text-2xl sm:text-3xl font-bold ${
                    getEffectiveTheme() === 'dark' ? 'text-blue-400' : 'text-blue-600'
                  }`}>{generalStats.total_matches}</div>
                  <div className={`text-sm ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-blue-700/70'}`}>Partite</div>
                </div>
                
                {/* Card 2: Vittorie - Pastel Green */}
                <div className={`${getDashboardCardSize()} rounded-lg shadow-md border-2 animate-fade-in-up ${
                  getEffectiveTheme() === 'dark' 
                    ? 'bg-gray-800 border-green-400/30' 
                    : 'bg-green-50/50 border-green-200 hover:shadow-green-200/50'
                } transition-all duration-300 hover:scale-105`} style={{animationDelay: '0.2s'}}>
                  <div className={`text-2xl sm:text-3xl font-bold ${
                    getEffectiveTheme() === 'dark' ? 'text-green-400' : 'text-green-600'
                  }`}>{generalStats.win_percentage}%</div>
                  <div className={`text-sm ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-green-700/70'}`}>Vittorie</div>
                </div>
                
                {/* Card 3: Goal Fatti - Pastel Purple */}
                <div className={`${getDashboardCardSize()} rounded-lg shadow-md border-2 animate-fade-in-up ${
                  getEffectiveTheme() === 'dark' 
                    ? 'bg-gray-800 border-purple-400/30' 
                    : 'bg-purple-50/50 border-purple-200 hover:shadow-purple-200/50'
                } transition-all duration-300 hover:scale-105`} style={{animationDelay: '0.3s'}}>
                  <div className={`text-2xl sm:text-3xl font-bold ${
                    getEffectiveTheme() === 'dark' ? 'text-purple-400' : 'text-purple-600'
                  }`}>{generalStats.goals_scored}</div>
                  <div className={`text-sm ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-purple-700/70'}`}>Goal Fatti</div>
                </div>
                
                {/* Card 4: Diff. Reti - Pastel Orange */}
                <div className={`${getDashboardCardSize()} rounded-lg shadow-md border-2 animate-fade-in-up ${
                  getEffectiveTheme() === 'dark' 
                    ? 'bg-gray-800 border-orange-400/30' 
                    : 'bg-orange-50/50 border-orange-200 hover:shadow-orange-200/50'
                } transition-all duration-300 hover:scale-105`} style={{animationDelay: '0.4s'}}>
                  <div className={`text-2xl sm:text-3xl font-bold ${
                    getEffectiveTheme() === 'dark' ? 'text-orange-400' : 'text-orange-600'
                  }`}>
                    {generalStats.goals_scored - generalStats.goals_conceded > 0 ? '+' : ''}
                    {generalStats.goals_scored - generalStats.goals_conceded}
                  </div>
                  <div className={`text-sm ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-orange-700/70'}`}>Diff. Reti</div>
                </div>
              </div>
            )}


            {/* 📅 PROSSIMI EVENTI - New Card */}
            {calendarEvents.length > 0 && (
              <div className={`p-5 rounded-lg shadow-md border-2 animate-fade-in-up ${
                getEffectiveTheme() === 'dark' 
                  ? 'bg-gray-800 border-orange-400/30' 
                  : 'bg-orange-50/50 border-orange-200 hover:shadow-orange-200/50'
              } transition-all duration-300`} style={{animationDelay: '0.45s'}}>
                <div className="flex justify-between items-center mb-4">
                  <h3 className={`text-lg font-semibold ${
                    getEffectiveTheme() === 'dark' ? 'text-orange-400' : 'text-orange-600'
                  }`}>
                    📅 Prossimi Eventi
                  </h3>
                  <button
                    onClick={() => setActiveTab('calendar')}
                    className="text-sm text-orange-600 hover:text-orange-700 font-medium hover:underline"
                  >
                    Vedi tutti →
                  </button>
                </div>
                
                <div className="space-y-3">
                  {calendarEvents
                    .filter(event => {
                      const eventDate = new Date(event.date);
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      return eventDate >= today;
                    })
                    .sort((a, b) => new Date(a.date) - new Date(b.date))
                    .slice(0, 5)
                    .map((event, index) => {
                      const eventDate = new Date(event.date);
                      const isToday = eventDate.toDateString() === new Date().toDateString();
                      
                      // Icon based on category
                      const categoryIcon = {
                        'training': '🏃',
                        'club_tournament': '🏆',
                        'national_tournament': '🥇'
                      }[event.category] || '📅';
                      
                      return (
                        <div
                          key={event.id}
                          className={`p-3 rounded-lg border ${
                            isToday 
                              ? getEffectiveTheme() === 'dark'
                                ? 'bg-red-900/30 border-red-500'
                                : 'bg-red-50 border-red-300'
                              : getEffectiveTheme() === 'dark'
                                ? 'bg-gray-700/50 border-gray-600'
                                : 'bg-white border-orange-200'
                          } hover:shadow-md transition-all cursor-pointer`}
                          onClick={() => setActiveTab('calendar')}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-lg">{categoryIcon}</span>
                                <span className={`text-sm font-bold ${
                                  getEffectiveTheme() === 'dark' ? 'text-white' : 'text-gray-900'
                                }`}>
                                  {event.name}
                                </span>
                              </div>
                              <div className={`text-xs ${
                                getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'
                              }`}>
                                🗓️ {new Date(event.date).toLocaleDateString('it-IT', { 
                                  weekday: 'short', 
                                  day: 'numeric', 
                                  month: 'short' 
                                })}
                                {event.time && ` • 🕐 ${event.time}`}
                              </div>
                            </div>
                            {isToday && (
                              <span className="px-2 py-1 bg-red-500 text-white text-xs font-bold rounded-full">
                                OGGI
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  
                  {calendarEvents.filter(event => {
                    const eventDate = new Date(event.date);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return eventDate >= today;
                  }).length === 0 && (
                    <div className={`text-center py-4 ${
                      getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      <p className="text-sm">Nessun evento in programma</p>
                      <button
                        onClick={() => setShowAddEventModal(true)}
                        className="mt-2 text-sm text-orange-600 hover:text-orange-700 font-medium hover:underline"
                      >
                        ➕ Aggiungi evento
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}


            {/* Streaks and Recent Form - Pastel Theme with Animation */}
            {streaks && (
              <div className={`${getDashboardCardSize()} rounded-lg shadow-md border-2 animate-fade-in-up ${
                getEffectiveTheme() === 'dark' 
                  ? 'bg-gray-800 border-red-400/30' 
                  : 'bg-red-50/50 border-red-200 hover:shadow-red-200/50'
              } transition-all duration-300`} style={{animationDelay: '0.5s'}}>
                <h3 className={`text-lg font-semibold mb-4 ${
                  getEffectiveTheme() === 'dark' ? 'text-red-400' : 'text-red-600'
                }`}>🔥 Serie e Forma Attuale</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-3xl mb-2">
                      {getStreakIcon(streaks.current_streak?.type)}
                    </div>
                    <div className={`font-bold text-lg ${getAccentColor(600)}`}>
                      {streaks.current_streak?.count || 0} {streaks.current_streak?.type === 'win' ? 'Vittorie' : 
                       streaks.current_streak?.type === 'loss' ? 'Sconfitte' : 'Pareggi'}
                    </div>
                    <div className={`text-sm ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Serie Attuale</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl mb-2">🏆</div>
                    <div className={`font-bold text-lg ${getAccentColor(600)}`}>{streaks.longest_win_streak}</div>
                    <div className={`text-sm ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Record Vittorie</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl mb-2">
                      {streaks.last_5_games?.map((game, i) => (
                        <span key={i} className={`mx-0.5 ${
                          game === 'W' ? getAccentColor(500) : 
                          game === 'L' ? 'text-red-500' : 'text-yellow-500'
                        }`}>
                          {game === 'W' ? '●' : game === 'L' ? '●' : '●'}
                        </span>
                      )) || ''}
                    </div>
                    <div className="font-bold text-lg">
                      {streaks.last_5_games?.join('-') || 'N/A'}
                    </div>
                    <div className={`text-sm ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Ultime 5 Partite</div>
                  </div>
                </div>
              </div>
            )}

            {/* AI Predictions and Suggestions - Pastel Theme */}
            {predictions && predictions.suggestions?.length > 0 && (
              <div className={`p-4 rounded-lg shadow-md border-2 ${
                getEffectiveTheme() === 'dark' 
                  ? 'bg-gray-800 border-cyan-400/30' 
                  : 'bg-cyan-50/50 border-cyan-200 hover:shadow-cyan-200/50'
              } transition-all duration-300`}>
                <h3 className={`text-lg font-semibold mb-3 flex items-center ${
                  getEffectiveTheme() === 'dark' ? 'text-cyan-400' : 'text-cyan-600'
                }`}>
                  🤖 Consigli dell'AI
                </h3>
                <div className="space-y-2">
                  {predictions.suggestions.map((suggestion, index) => (
                    <div key={index} className={`p-3 rounded ${getEffectiveTheme() === 'dark' ? 'bg-gray-700' : 'bg-white'}`}>
                      {suggestion}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Goals Tracking - Pastel Theme */}
            {goalsTracking?.progress && (
              <div className={`${getDashboardCardSize()} rounded-lg shadow-md border-2 ${
                getEffectiveTheme() === 'dark' 
                  ? 'bg-gray-800 border-pink-400/30' 
                  : 'bg-pink-50/50 border-pink-200 hover:shadow-pink-200/50'
              } transition-all duration-300`}>
                <h3 className={`text-lg font-semibold mb-4 ${
                  getEffectiveTheme() === 'dark' ? 'text-pink-400' : 'text-pink-600'
                }`}>🎯 I Tuoi Obiettivi</h3>
                <div className="space-y-4">
                  {Object.entries(goalsTracking.progress).map(([key, goal]) => (
                    <div key={key}>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium">
                          {key === 'monthly_matches' ? '🏐 Partite Mensili' :
                           key === 'win_percentage' ? '🏆 % Vittorie' : '⚽ Goal Mensili'}
                        </span>
                        <span className="text-sm">{goal.current}/{goal.target}</span>
                      </div>
                      <div className={`w-full rounded-full h-2 ${getEffectiveTheme() === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`}>
                        <div 
                          className={`h-2 rounded-full transition-all duration-500 ${
                            goal.achieved ? getAccentColor(500, 'bg') : 'bg-blue-500'
                          }`}
                          style={{ width: `${Math.min(goal.percentage, 100)}%` }}
                        ></div>
                      </div>
                      {goal.achieved && <div className={`${getAccentColor(600)} text-xs mt-1`}>✓ Obiettivo raggiunto!</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Matches - Pastel Theme */}
            <div className={`${getDashboardCardSize()} rounded-lg shadow-md border-2 ${
              getEffectiveTheme() === 'dark' 
                ? 'bg-gray-800 border-yellow-400/30' 
                : 'bg-yellow-50/50 border-yellow-200 hover:shadow-yellow-200/50'
            } transition-all duration-300`}>
              <h3 className={`text-lg font-semibold mb-4 ${
                getEffectiveTheme() === 'dark' ? 'text-yellow-400' : 'text-yellow-600'
              }`}>📝 Partite Recenti</h3>
              <div className="space-y-2">
                {matches.slice(0, 5).map((match, index) => (
                  <div key={index} className={`p-3 rounded border ${
                    getEffectiveTheme() === 'dark' ? 'border-gray-700 bg-gray-750' : 'border-gray-100'
                  }`}>
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="font-medium">
                          {match.my_team} vs {match.opponent_team}
                        </div>
                        <div className={`text-sm ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                          vs {match.opponent_name} • {formatDate(match.match_date)}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-lg flex items-center justify-end gap-1">
                          {match.my_goals} - {match.opponent_goals}
                          {match.has_piazzati && (
                            <span className="text-yellow-500 text-sm" title={`Piazzati: ${match.piazzati_miei || 0}-${match.piazzati_avversario || 0}`}>
                              ({match.piazzati_miei}-{match.piazzati_avversario} p)
                            </span>
                          )}
                        </div>
                        <div className={`text-xs px-2 py-1 rounded inline-flex items-center gap-1 ${getResultColor(match.my_goals, match.opponent_goals, match)}`}>
                          {getMatchResult(match.my_goals, match.opponent_goals, match)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Add Match Tab */}
        {activeTab === 'add-match' && (
          <div className={`rounded-lg shadow p-4 sm:p-6 ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
            <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">➕ Aggiungi Nuova Partita</h2>
            
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                {/* My Team - Back to Clean Input */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    getEffectiveTheme() === 'dark' ? 'text-gray-200' : 'text-gray-700'
                  }`}>
                    La Mia Squadra
                  </label>
                  <input
                    type="text"
                    value={formData.my_team}
                    onChange={(e) => setFormData({...formData, my_team: e.target.value})}
                    list="teams-list"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                      getEffectiveTheme() === 'dark' 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300'
                    }`}
                    placeholder="es. Juventus, Milan, Barcelona..."
                    required
                  />
                  <datalist id="teams-list">
                    {teams.map(team => (
                      <option key={team} value={team} />
                    ))}
                  </datalist>
                </div>

                {/* Opponent Team - Uses Unique Opponent Teams from Matches */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    getEffectiveTheme() === 'dark' ? 'text-gray-200' : 'text-gray-700'
                  }`}>
                    Squadra Avversario
                  </label>
                  <input
                    type="text"
                    value={formData.opponent_team}
                    onChange={(e) => setFormData({...formData, opponent_team: e.target.value})}
                    list="opponent-teams-list"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                      getEffectiveTheme() === 'dark' 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300'
                    }`}
                    placeholder="es. Real Madrid, Liverpool, PSG..."
                    required
                  />
                  <datalist id="opponent-teams-list">
                    {[...new Set(matches.map(match => match.opponent_team).filter(team => team))].map(team => (
                      <option key={team} value={team} />
                    ))}
                  </datalist>
                </div>

                {/* Opponent Name */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    getEffectiveTheme() === 'dark' ? 'text-gray-200' : 'text-gray-700'
                  }`}>
                    Nome Avversario
                  </label>
                  <input
                    type="text"
                    value={formData.opponent_name}
                    onChange={(e) => setFormData({...formData, opponent_name: e.target.value})}
                    list="players-list"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                      getEffectiveTheme() === 'dark' 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300'
                    }`}
                    required
                  />
                  <datalist id="players-list">
                    {[...new Set(matches.map(match => match.opponent_name).filter(name => name))].map(player => (
                      <option key={player} value={player} />
                    ))}
                  </datalist>
                </div>

                {/* Tournament - Uses Unique Tournament Names from Matches */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    getEffectiveTheme() === 'dark' ? 'text-gray-200' : 'text-gray-700'
                  }`}>
                    Torneo/Campionato
                  </label>
                  <input
                    type="text"
                    value={formData.tournament}
                    onChange={(e) => setFormData({...formData, tournament: e.target.value})}
                    list="tournaments-list"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                      getEffectiveTheme() === 'dark' 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300'
                    }`}
                    placeholder="Lascia vuoto per amichevole"
                  />
                  <datalist id="tournaments-list">
                    {[...new Set(matches.map(match => match.tournament).filter(tournament => tournament))].map(tournament => (
                      <option key={tournament} value={tournament} />
                    ))}
                  </datalist>
                </div>

                {/* Goals Section */}
                <div className="sm:col-span-2">
                  <div className="flex space-x-4">
                    <div className="flex-1">
                      <label className={`block text-sm font-medium mb-2 ${
                        getEffectiveTheme() === 'dark' ? 'text-gray-200' : 'text-gray-700'
                      }`}>
                        I Miei Goal
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={formData.my_goals}
                        onChange={(e) => setFormData({...formData, my_goals: e.target.value})}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-center text-lg font-bold ${
                          getEffectiveTheme() === 'dark' 
                            ? 'bg-gray-700 border-gray-600 text-white' 
                            : 'bg-white border-gray-300'
                        }`}
                        required
                      />
                    </div>
                    
                    <div className="flex items-end pb-2">
                      <span className="text-2xl font-bold text-gray-400">-</span>
                    </div>
                    
                    <div className="flex-1">
                      <label className={`block text-sm font-medium mb-2 ${
                        getEffectiveTheme() === 'dark' ? 'text-gray-200' : 'text-gray-700'
                      }`}>
                        Goal Avversario
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={formData.opponent_goals}
                        onChange={(e) => setFormData({...formData, opponent_goals: e.target.value})}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-center text-lg font-bold ${
                          getEffectiveTheme() === 'dark' 
                            ? 'bg-gray-700 border-gray-600 text-white' 
                            : 'bg-white border-gray-300'
                        }`}
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* PIAZZATI (Shootouts) Section */}
                <div className="sm:col-span-2">
                  <div className={`p-4 rounded-lg border ${
                    formData.has_piazzati 
                      ? (getEffectiveTheme() === 'dark' ? 'border-yellow-500 bg-yellow-900/20' : 'border-yellow-400 bg-yellow-50')
                      : (getEffectiveTheme() === 'dark' ? 'border-gray-600' : 'border-gray-200')
                  }`}>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.has_piazzati}
                        onChange={(e) => setFormData({
                          ...formData, 
                          has_piazzati: e.target.checked,
                          piazzati_miei: e.target.checked ? formData.piazzati_miei : '',
                          piazzati_avversario: e.target.checked ? formData.piazzati_avversario : ''
                        })}
                        className="w-5 h-5 text-yellow-500 rounded focus:ring-yellow-500"
                        data-testid="piazzati-checkbox"
                      />
                      <span className={`ml-3 font-medium ${
                        getEffectiveTheme() === 'dark' ? 'text-gray-200' : 'text-gray-700'
                      }`}>
                        🎯 Partita decisa ai piazzati
                      </span>
                    </label>
                    
                    {formData.has_piazzati && (
                      <div className="mt-4">
                        <div className="flex items-center justify-center space-x-4">
                          <div className="text-center">
                            <label className={`block text-xs font-medium mb-1 ${
                              getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-600'
                            }`}>
                              I Miei Piazzati
                            </label>
                            <input
                              type="number"
                              min="0"
                              value={formData.piazzati_miei}
                              onChange={(e) => setFormData({...formData, piazzati_miei: e.target.value})}
                              className={`w-20 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 text-center text-lg font-bold ${
                                getEffectiveTheme() === 'dark' 
                                  ? 'bg-gray-700 border-gray-600 text-white' 
                                  : 'bg-white border-gray-300'
                              }`}
                              placeholder="0"
                              data-testid="piazzati-miei-input"
                            />
                          </div>
                          
                          <div className="flex items-end pb-2">
                            <span className="text-2xl font-bold text-yellow-500">-</span>
                          </div>
                          
                          <div className="text-center">
                            <label className={`block text-xs font-medium mb-1 ${
                              getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-600'
                            }`}>
                              Piazzati Avversario
                            </label>
                            <input
                              type="number"
                              min="0"
                              value={formData.piazzati_avversario}
                              onChange={(e) => setFormData({...formData, piazzati_avversario: e.target.value})}
                              className={`w-20 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 text-center text-lg font-bold ${
                                getEffectiveTheme() === 'dark' 
                                  ? 'bg-gray-700 border-gray-600 text-white' 
                                  : 'bg-white border-gray-300'
                              }`}
                              placeholder="0"
                              data-testid="piazzati-avversario-input"
                            />
                          </div>
                        </div>
                        <p className={`text-xs text-center mt-2 ${
                          getEffectiveTheme() === 'dark' ? 'text-yellow-400' : 'text-yellow-600'
                        }`}>
                          Inserisci il risultato dei tiri piazzati
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Match Date */}
                <div className="sm:col-span-2">
                  <label className={`block text-sm font-medium mb-2 ${
                    getEffectiveTheme() === 'dark' ? 'text-gray-200' : 'text-gray-700'
                  }`}>
                    Data Partita
                  </label>
                  <input
                    type="date"
                    value={formData.match_date}
                    onChange={(e) => setFormData({...formData, match_date: e.target.value})}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                      getEffectiveTheme() === 'dark' 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300'
                    }`}
                    required
                  />
                </div>

                {/* Museum Association Section - Simplified */}
                {museumTeams.length > 0 && (
                  <div className="sm:col-span-2">
                    <div className={`p-4 rounded-lg border-2 border-dashed ${
                      getEffectiveTheme() === 'dark' ? 'border-blue-600 bg-blue-900/10' : 'border-blue-300 bg-blue-50'
                    }`}>
                      <h3 className="text-sm font-semibold mb-2 flex items-center">
                        🏟️ Collegamento Museum
                        <span className={`ml-2 text-xs font-normal ${
                          getEffectiveTheme() === 'dark' ? 'text-blue-300' : 'text-blue-600'
                        }`}>
                          (quale squadra fisica hai usato realmente?)
                        </span>
                      </h3>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className={`block text-xs font-medium mb-1 ${
                        getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Ho usato questa squadra del Museum:
                      </label>
                      <select
                        value={formData.my_museum_team_id}
                        onChange={(e) => setFormData({...formData, my_museum_team_id: e.target.value})}
                        className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          getEffectiveTheme() === 'dark' 
                            ? 'bg-gray-700 border-gray-600 text-white' 
                            : 'bg-white border-gray-300'
                        }`}
                      >
                        <option value="">Nessuna (non nel Museum)</option>
                        {museumTeams.length === 0 ? (
                          <option disabled>⚠️ Museum vuoto - Aggiungi squadre nella sezione Museum</option>
                        ) : (
                          museumTeams.map(team => (
                            <option key={team.id} value={team.id}>
                              {team.name} • {team.team_type || 'Club'}
                              {team.manufacturer && ` • ${team.manufacturer}`}
                            </option>
                          ))
                        )}
                      </select>
                    </div>
                      </div>
                      
                      <div className="mt-3 text-center">
                        <button
                          type="button"
                          onClick={() => setActiveTab('museum')}
                          className={`inline-flex items-center px-3 py-2 text-xs font-medium rounded-lg border transition-colors ${
                            getEffectiveTheme() === 'dark' 
                              ? 'border-gray-600 text-gray-300 hover:bg-gray-700 hover:border-gray-500' 
                              : 'border-gray-300 text-gray-600 hover:bg-gray-50 hover:border-gray-400'
                          }`}
                        >
                          <span className="mr-1">🏟️</span>
                          Gestisci Museum
                        </button>
                        <p className={`text-xs mt-1 ${
                          getEffectiveTheme() === 'dark' ? 'text-gray-500' : 'text-gray-500'
                        }`}>
                          Aggiungi le tue squadre fisiche
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-green-600 text-white py-3 px-4 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 font-medium transition-colors"
                >
                  {loading ? 'Salvando...' : '⚽ Salva Partita'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* IMPORT EXCEL/CSV TAB 📁 */}
        {activeTab === 'import' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold">📁 Import Partite da Excel/CSV</h2>
                <p className={`text-sm ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Importa facilmente le tue partite storiche da file Excel o CSV
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={downloadTemplate}
                  className="bg-blue-600 text-white px-3 py-2 rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  📥 Scarica Template
                </button>
                <button
                  onClick={resetImport}
                  className="bg-gray-500 text-white px-3 py-2 rounded-md hover:bg-gray-600 transition-colors text-sm font-medium"
                >
                  🔄 Reset
                </button>
              </div>
            </div>

            {/* Progress Overlay */}
            {importProgress.show && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className={`p-6 rounded-lg max-w-md w-full mx-4 ${
                  getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'
                }`}>
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <h3 className="text-lg font-semibold mb-2">{importProgress.message}</h3>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${importProgress.percent}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 1: File Upload */}
            {importStep === 'upload' && (
              <div className={`p-6 rounded-lg ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'} border-2 border-dashed ${
                getEffectiveTheme() === 'dark' ? 'border-gray-600' : 'border-gray-300'
              }`}>
                <div className="text-center">
                  <div className="text-6xl mb-4">📁</div>
                  <h3 className="text-xl font-semibold mb-2">Carica il tuo file</h3>
                  <p className={`mb-6 ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    Supporta file CSV, Excel (.xlsx), e Excel (.xls) - Max 10MB
                  </p>
                  
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={(e) => handleFileUpload(e.target.files[0])}
                    className="hidden"
                    id="import-file-input"
                  />
                  <label
                    htmlFor="import-file-input"
                    className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 cursor-pointer transition-colors font-medium"
                  >
                    📂 Seleziona File
                  </label>
                  
                  <div className="mt-6 text-left">
                    <h4 className="font-semibold mb-2">✨ Import Intelligente:</h4>
                    <p className={`text-sm mb-3 ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      Carica qualsiasi file Excel o CSV - il sistema riconosce automaticamente le colonne!
                    </p>
                    <ul className={`text-sm space-y-1 ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      <li>✅ <strong>Giocatori:</strong> "Casa", "Ospite", "Giocatore A/B", "Player 1/2", "Nome"...</li>
                      <li>✅ <strong>Gol:</strong> "Gol Casa", "Gol Ospite", "Punti A/B", "Score"...</li>
                      <li>✅ <strong>Data:</strong> Qualsiasi formato (opzionale)</li>
                      <li>✅ <strong>Torneo:</strong> Opzionale</li>
                    </ul>
                    <p className={`text-xs mt-3 ${getEffectiveTheme() === 'dark' ? 'text-green-400' : 'text-green-600'}`}>
                      💡 Il sistema è flessibile - usa i nomi colonna che preferisci!
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Preview */}
            {importStep === 'preview' && importPreview && (
              <div className="space-y-6">
                {/* Statistics Summary */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className={`p-4 rounded-lg ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
                    <div className="text-2xl font-bold text-blue-600">{importPreview.total_rows}</div>
                    <div className={`text-sm ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>Totale Righe</div>
                  </div>
                  <div className={`p-4 rounded-lg ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
                    <div className="text-2xl font-bold text-green-600">{importPreview.valid_rows}</div>
                    <div className={`text-sm ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>Valide</div>
                  </div>
                  <div className={`p-4 rounded-lg ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
                    <div className="text-2xl font-bold text-yellow-600">{importPreview.warning_rows}</div>
                    <div className={`text-sm ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>Warning</div>
                  </div>
                  <div className={`p-4 rounded-lg ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
                    <div className="text-2xl font-bold text-red-600">{importPreview.error_rows}</div>
                    <div className={`text-sm ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>Errori</div>
                  </div>
                </div>

                {/* Detected Columns + Editor Visuale */}
                <div className={`p-4 rounded-lg ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-semibold">🔍 Mappatura Colonne</h3>
                    <button
                      onClick={() => setShowMappingEditor(!showMappingEditor)}
                      className={`px-3 py-1 rounded-lg text-sm font-medium ${
                        showMappingEditor 
                          ? 'bg-blue-600 text-white' 
                          : getEffectiveTheme() === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                      }`}
                    >
                      {showMappingEditor ? '✓ Editor Attivo' : '✏️ Modifica Mappatura'}
                    </button>
                  </div>
                  
                  {!showMappingEditor ? (
                    // Vista semplice
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                      {Object.entries(customColumnMapping && Object.keys(customColumnMapping).length > 0 ? customColumnMapping : (importPreview.detected_columns || {})).map(([field, column]) => (
                        <div key={field} className="flex justify-between items-center p-2 rounded bg-opacity-50" style={{backgroundColor: getEffectiveTheme() === 'dark' ? 'rgba(59,130,246,0.1)' : 'rgba(59,130,246,0.05)'}}>
                          <span className="font-medium capitalize">{field.replace('_', ' ')}:</span>
                          <span className={getEffectiveTheme() === 'dark' ? 'text-blue-300' : 'text-blue-600'}>{column || '—'}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    // Editor visuale
                    <div className="space-y-3">
                      <p className={`text-sm mb-4 ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        Associa le colonne del tuo file ai campi richiesti:
                      </p>
                      {[
                        { key: 'player_a', label: '👤 Giocatore A (Casa)', required: true },
                        { key: 'player_b', label: '👤 Giocatore B (Ospite)', required: true },
                        { key: 'goals_a', label: '⚽ Gol A', required: true },
                        { key: 'goals_b', label: '⚽ Gol B', required: true },
                        { key: 'match_date', label: '📅 Data', required: false },
                        { key: 'tournament', label: '🏆 Torneo', required: false }
                      ].map(field => (
                        <div key={field.key} className="flex items-center gap-3">
                          <label className={`w-48 text-sm font-medium ${field.required ? '' : 'text-gray-500'}`}>
                            {field.label} {field.required && <span className="text-red-500">*</span>}
                          </label>
                          <select
                            value={(customColumnMapping && customColumnMapping[field.key]) || (importPreview.detected_columns && importPreview.detected_columns[field.key]) || ''}
                            onChange={(e) => {
                              const newMapping = {
                                ...(customColumnMapping && Object.keys(customColumnMapping).length > 0 ? customColumnMapping : importPreview.detected_columns),
                                [field.key]: e.target.value || undefined
                              };
                              if (!e.target.value) delete newMapping[field.key];
                              setCustomColumnMapping(newMapping);
                            }}
                            className={`flex-1 px-3 py-2 rounded-lg border ${
                              getEffectiveTheme() === 'dark' 
                                ? 'bg-gray-700 border-gray-600 text-white' 
                                : 'bg-white border-gray-300'
                            }`}
                          >
                            <option value="">— Non mappato —</option>
                            {(importPreview.sample_data && importPreview.sample_data[0]?.data 
                              ? Object.keys(importPreview.sample_data[0].data)
                              : []
                            ).map(col => (
                              <option key={col} value={col}>{col}</option>
                            ))}
                            {/* Fallback to detected columns */}
                            {Object.values(importPreview.detected_columns || {}).filter(c => 
                              !(importPreview.sample_data && importPreview.sample_data[0]?.data && Object.keys(importPreview.sample_data[0].data).includes(c))
                            ).map(col => (
                              <option key={col} value={col}>{col}</option>
                            ))}
                          </select>
                        </div>
                      ))}
                      <div className="flex gap-2 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <button
                          onClick={() => {
                            setCustomColumnMapping({});
                            setShowMappingEditor(false);
                          }}
                          className="px-4 py-2 text-sm rounded-lg bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600"
                        >
                          🔄 Reset Auto
                        </button>
                        <button
                          onClick={() => setShowMappingEditor(false)}
                          className="px-4 py-2 text-sm rounded-lg bg-green-600 text-white hover:bg-green-700"
                        >
                          ✓ Applica Mappatura
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Suggestions */}
                {importPreview.suggestions && importPreview.suggestions.length > 0 && (
                  <div className={`p-4 rounded-lg border-l-4 border-yellow-500 ${
                    getEffectiveTheme() === 'dark' ? 'bg-yellow-900/20' : 'bg-yellow-50'
                  }`}>
                    <h3 className="text-lg font-semibold mb-3">⚠️ Suggerimenti</h3>
                    <ul className="space-y-1 text-sm">
                      {importPreview.suggestions.map((suggestion, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="mr-2">•</span>
                          <span>{suggestion}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Sample Data Preview */}
                {importPreview.sample_data && importPreview.sample_data.length > 0 && (
                  <div className={`p-4 rounded-lg ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
                    <h3 className="text-lg font-semibold mb-3">📋 Anteprima Dati (prime {importPreview.sample_data.length} righe)</h3>
                    <div className="overflow-x-auto">
                      <table className="min-w-full">
                        <thead>
                          <tr className={getEffectiveTheme() === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}>
                            <th className="px-2 py-2 text-left text-xs font-medium">Riga</th>
                            <th className="px-2 py-2 text-left text-xs font-medium">Status</th>
                            <th className="px-2 py-2 text-left text-xs font-medium">Dati</th>
                            <th className="px-2 py-2 text-left text-xs font-medium">Note</th>
                          </tr>
                        </thead>
                        <tbody>
                          {importPreview.sample_data.slice(0, 10).map((row, idx) => (
                            <tr key={idx} className={getEffectiveTheme() === 'dark' ? 'border-gray-700' : 'border-gray-200'}>
                              <td className="px-2 py-2 text-sm">{row.row_number}</td>
                              <td className="px-2 py-2">
                                <span className={`px-2 py-1 text-xs rounded-full ${
                                  row.status === 'valid' ? 'bg-green-100 text-green-800' :
                                  row.status === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-red-100 text-red-800'
                                }`}>
                                  {row.status}
                                </span>
                              </td>
                              <td className="px-2 py-2 text-xs">
                                {Object.entries(row.data || {}).slice(0, 3).map(([key, value]) => (
                                  <div key={key}><strong>{key}:</strong> {String(value).substring(0, 20)}</div>
                                ))}
                              </td>
                              <td className="px-2 py-2 text-xs">
                                {row.messages && row.messages.length > 0 && (
                                  <ul className="space-y-1">
                                    {row.messages.slice(0, 2).map((msg, i) => (
                                      <li key={i} className="text-xs">{msg}</li>
                                    ))}
                                  </ul>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Import Options */}
                <div className={`p-4 rounded-lg border-l-4 border-blue-500 ${
                  getEffectiveTheme() === 'dark' ? 'bg-blue-900/20' : 'bg-blue-50'
                }`}>
                  <h3 className="text-lg font-semibold mb-3">⚙️ Opzioni di Importazione</h3>
                  <div className="space-y-3">
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="radio"
                        name="importMode"
                        checked={replaceExistingData === true}
                        onChange={() => setReplaceExistingData(true)}
                        className="text-blue-600"
                      />
                      <div>
                        <span className="font-medium text-blue-700">🔄 Sostituisci dati esistenti</span>
                        <p className="text-sm text-gray-600">Cancella tutti i dati attuali e importa solo quelli del file (Consigliato per backup)</p>
                      </div>
                    </label>
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="radio"
                        name="importMode"
                        checked={replaceExistingData === false}
                        onChange={() => setReplaceExistingData(false)}
                        className="text-orange-600"
                      />
                      <div>
                        <span className="font-medium text-orange-700">➕ Aggiungi ai dati esistenti</span>
                        <p className="text-sm text-gray-600">Mantieni i dati attuali e aggiungi quelli del file (Può creare duplicati)</p>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4 pt-4">
                  <button
                    onClick={executeImport}
                    disabled={importPreview.valid_rows === 0}
                    className={`flex-1 py-3 px-6 rounded-lg font-semibold transition-colors ${
                      importPreview.valid_rows > 0
                        ? 'bg-green-600 text-white hover:bg-green-700'
                        : 'bg-gray-400 text-gray-700 cursor-not-allowed'
                    }`}
                  >
                    ✅ Importa {importPreview.valid_rows} Partite
                  </button>
                  <button
                    onClick={resetImport}
                    className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    🔄 Ricomincia
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Result */}
            {importStep === 'result' && importResult && (
              <div className={`p-6 rounded-lg ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
                <div className="text-center">
                  <div className={`text-6xl mb-4 ${
                    importResult.success ? 'text-green-500' : 'text-red-500'
                  }`}>
                    {importResult.success ? '🎉' : '❌'}
                  </div>
                  
                  <h3 className={`text-xl font-semibold mb-2 ${
                    importResult.success ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {importResult.success ? 'Import Completato!' : 'Import Fallito'}
                  </h3>
                  
                  <p className={`mb-6 ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    {importResult.summary}
                  </p>

                  {/* Statistics */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
                    <div className={`p-3 rounded-lg ${getEffectiveTheme() === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <div className="text-lg font-bold">{importResult.total_processed}</div>
                      <div className="text-sm">Totale Elaborate</div>
                    </div>
                    <div className={`p-3 rounded-lg ${getEffectiveTheme() === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <div className="text-lg font-bold text-green-600">{importResult.imported_matches}</div>
                      <div className="text-sm">Importate</div>
                    </div>
                    <div className={`p-3 rounded-lg ${getEffectiveTheme() === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <div className="text-lg font-bold text-red-600">{importResult.skipped_rows}</div>
                      <div className="text-sm">Saltate</div>
                    </div>
                  </div>

                  {/* Errors */}
                  {importResult.errors && importResult.errors.length > 0 && (
                    <div className={`p-4 rounded-lg border-l-4 border-red-500 text-left ${
                      getEffectiveTheme() === 'dark' ? 'bg-red-900/20' : 'bg-red-50'
                    }`}>
                      <h4 className="font-semibold mb-2">❌ Errori riscontrati:</h4>
                      <div className="max-h-40 overflow-y-auto">
                        <ul className="space-y-1 text-sm">
                          {importResult.errors.slice(0, 10).map((error, idx) => (
                            <li key={idx}>
                              <strong>Riga {error.row}:</strong> {error.error}
                            </li>
                          ))}
                          {importResult.errors.length > 10 && (
                            <li className="text-gray-500">... e altri {importResult.errors.length - 10} errori</li>
                          )}
                        </ul>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-4 mt-6">
                    <button
                      onClick={() => setActiveTab('dashboard')}
                      className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                    >
                      🏠 Vai al Dashboard
                    </button>
                    <button
                      onClick={resetImport}
                      className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                    >
                      📁 Nuovo Import
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Info Section */}
            {importStep === 'upload' && (
              <div className={`p-6 rounded-lg border-l-4 border-blue-500 ${
                getEffectiveTheme() === 'dark' ? 'bg-blue-900/20' : 'bg-blue-50'
              }`}>
                <h3 className="text-lg font-semibold mb-3">ℹ️ Come funziona</h3>
                <ol className="space-y-2 text-sm">
                  <li><strong>1. Prepara il file:</strong> Scarica il template e inserisci i tuoi dati</li>
                  <li><strong>2. Carica il file:</strong> Supportiamo CSV ed Excel (.xlsx, .xls)</li>
                  <li><strong>3. Anteprima:</strong> Controlliamo i dati e ti mostriamo eventuali problemi</li>
                  <li><strong>4. Importazione:</strong> Le partite valide vengono aggiunte al tuo database</li>
                  <li><strong>5. Aggiornamento:</strong> Tutte le statistiche vengono ricalcolate automaticamente</li>
                </ol>
                
                <div className="mt-4 p-3 bg-yellow-100 rounded-lg">
                  <p className="text-sm font-medium text-yellow-800">
                    💡 <strong>Consiglio:</strong> Inizia con poche partite per testare il formato, poi importa tutto!
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* 🎨 NEW MODERN MUSEUM TAB - Redesigned for Performance 🏟️ */}
        {activeTab === 'museum' && (
          <div className="space-y-6">
            {/* Modern Header with Glassmorphism Effect */}
            <div className="relative">
              <div className={`
                backdrop-blur-lg border border-opacity-30 rounded-2xl p-6
                ${getEffectiveTheme() === 'dark' 
                  ? 'bg-gray-900/60 border-gray-700/30' 
                  : 'bg-white/80 border-gray-200/30'
                } shadow-lg
              `}>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                      <span className="text-2xl">🏟️</span>
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                        My Subbuteo Museum
                      </h2>
                      <p className={`text-sm ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        {getFilteredMuseumTeams().length} squadre nella collezione
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => {
                        setEditingTeam(null);
                        setTeamFormData({
                          name: '',
                          photo_url: '',
                          base_color: '#FFFFFF',
                          inner_color: '#000000',
                          country: '',
                          team_type: 'Club',
                          notes: ''
                        });
                        setShowTeamForm(true);
                      }}
                      className="group relative bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-3 rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 font-medium"
                    >
                      <span className="flex items-center space-x-2">
                        <span className="text-lg group-hover:rotate-90 transition-transform duration-300">➕</span>
                        <span>Nuova Squadra</span>
                      </span>
                    </button>

                    {/* Diagnostic & Migration Buttons - Owner Only */}
                    {user?.plan_type === 'owner' && (
                      <>
                        <button
                        onClick={() => {
                          console.log('🔍 DEBUG: User data:', user);
                          console.log('🔍 DEBUG: Plan type:', user?.plan_type);
                          diagnoseTeamData();
                        }}
                        className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-2 rounded-lg transition-colors text-xs font-medium flex items-center gap-1"
                        title="Analizza struttura dati squadre"
                      >
                        <span>🔍</span>
                        <span>Debug</span>
                      </button>
                      
                      <button
                        onClick={() => {
                          console.log('🛠️ DEBUG: User data:', user);
                          migrateOldTeams();
                        }}
                        disabled={migrationInProgress}
                        className={`px-3 py-2 rounded-lg transition-colors text-xs font-medium flex items-center gap-1 ${
                          migrationInProgress 
                            ? 'bg-gray-400 text-white cursor-not-allowed'
                            : 'bg-purple-500 hover:bg-purple-600 text-white'
                        }`}
                        title="Migra squadre con formato vecchio"
                      >
                        <span>{migrationInProgress ? '⏳' : '🛠️'}</span>
                        <span>{migrationInProgress ? 'Migrazione...' : 'Migra'}</span>
                      </button>
                      </>
                    )}
                  </div>
                </div>
                
                {/* Enhanced Search and Filters Bar */}
                <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
                  {/* Search Input */}
                  <div className="md:col-span-2">
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                        🔍
                      </span>
                      <input
                        type="text"
                        placeholder="Cerca squadra o paese..."
                        value={museumAdvancedFilter.search}
                        onChange={(e) => setMuseumAdvancedFilter(prev => ({ ...prev, search: e.target.value }))}
                        className={`w-full pl-10 pr-4 py-3 rounded-xl border transition-all duration-200 focus:ring-2 focus:ring-blue-500/20 ${
                          getEffectiveTheme() === 'dark'
                            ? 'bg-gray-800/50 border-gray-600 text-white placeholder-gray-400'
                            : 'bg-gray-50/50 border-gray-200 placeholder-gray-500'
                        }`}
                      />
                    </div>
                  </div>
                  
                  {/* Category Filter */}
                  <select
                    value={museumAdvancedFilter.category}
                    onChange={(e) => setMuseumAdvancedFilter(prev => ({ ...prev, category: e.target.value }))}
                    className={`px-4 py-3 rounded-xl border transition-all duration-200 focus:ring-2 focus:ring-blue-500/20 ${
                      getEffectiveTheme() === 'dark'
                        ? 'bg-gray-800/50 border-gray-600 text-white'
                        : 'bg-gray-50/50 border-gray-200'
                    }`}
                  >
                    <option value="">🌍 Tutte le categorie</option>
                    <option value="Club">⚽ Club</option>
                    <option value="Nazionale">🏆 Nazionale</option>
                    <option value="Fantasy">✨ Fantasy</option>
                  </select>
                  
                  {/* Sort By */}
                  <select
                    value={museumAdvancedFilter.sortBy}
                    onChange={(e) => setMuseumAdvancedFilter(prev => ({ ...prev, sortBy: e.target.value }))}
                    className={`px-4 py-3 rounded-xl border transition-all duration-200 focus:ring-2 focus:ring-blue-500/20 ${
                      getEffectiveTheme() === 'dark'
                        ? 'bg-gray-800/50 border-gray-600 text-white'
                        : 'bg-gray-50/50 border-gray-200'
                    }`}
                  >
                    <option value="name">📝 Nome A-Z</option>
                    <option value="win_rate">📊 % Vittorie</option>
                    <option value="total_matches">⚽ Più Usate</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Modern Horizontal List Layout - Responsive Mobile Fix with Club/National Separation */}
            {getFilteredMuseumTeams().length > 0 ? (
              <div className="space-y-6">
                {(() => {
                  const allTeams = getFilteredMuseumTeams();
                  const clubTeams = allTeams.filter(t => t.team_type === 'Club').sort((a, b) => 
                    (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
                  );
                  const nationalTeams = allTeams.filter(t => t.team_type === 'Nazionale').sort((a, b) => 
                    (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
                  );
                  const otherTeams = allTeams.filter(t => t.team_type !== 'Club' && t.team_type !== 'Nazionale').sort((a, b) => 
                    (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
                  );

                  return (
                    <>
                      {/* Squadre di Club */}
                      {clubTeams.length > 0 && (
                        <div>
                          <div className={`mb-4 pb-2 border-b-2 ${
                            getEffectiveTheme() === 'dark' 
                              ? 'border-blue-500/30' 
                              : 'border-blue-500/20'
                          }`}>
                            <h3 className={`text-lg font-bold flex items-center gap-2 ${
                              getEffectiveTheme() === 'dark' ? 'text-blue-400' : 'text-blue-600'
                            }`}>
                              ⚽ SQUADRE DI CLUB
                              <span className={`text-sm font-normal ${
                                getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-500'
                              }`}>
                                ({clubTeams.length})
                              </span>
                            </h3>
                          </div>
                          <div className="space-y-3">
                            {clubTeams.map((team, index) => (
                              <div
                                key={team.id}
                                className={`group relative rounded-xl overflow-hidden transition-all duration-200 hover:shadow-lg ${
                                  getEffectiveTheme() === 'dark'
                                    ? 'bg-gradient-to-r from-gray-800/90 to-gray-900/90 backdrop-blur-sm border border-gray-700/30 hover:bg-gray-800'
                                    : 'bg-gradient-to-r from-white/90 to-gray-50/90 backdrop-blur-sm border border-gray-200/30 hover:bg-gray-50'
                                } shadow-sm`}
                              >
                                {renderMuseumTeamCard(team)}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Squadre Nazionali */}
                      {nationalTeams.length > 0 && (
                        <div>
                          <div className={`mb-4 pb-2 border-b-2 ${
                            getEffectiveTheme() === 'dark' 
                              ? 'border-green-500/30' 
                              : 'border-green-500/20'
                          }`}>
                            <h3 className={`text-lg font-bold flex items-center gap-2 ${
                              getEffectiveTheme() === 'dark' ? 'text-green-400' : 'text-green-600'
                            }`}>
                              🌍 SQUADRE NAZIONALI
                              <span className={`text-sm font-normal ${
                                getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-500'
                              }`}>
                                ({nationalTeams.length})
                              </span>
                            </h3>
                          </div>
                          <div className="space-y-3">
                            {nationalTeams.map((team, index) => (
                              <div
                                key={team.id}
                                className={`group relative rounded-xl overflow-hidden transition-all duration-200 hover:shadow-lg ${
                                  getEffectiveTheme() === 'dark'
                                    ? 'bg-gradient-to-r from-gray-800/90 to-gray-900/90 backdrop-blur-sm border border-gray-700/30 hover:bg-gray-800'
                                    : 'bg-gradient-to-r from-white/90 to-gray-50/90 backdrop-blur-sm border border-gray-200/30 hover:bg-gray-50'
                                } shadow-sm`}
                              >
                                {renderMuseumTeamCard(team)}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Altre squadre (Fantasy, etc) */}
                      {otherTeams.length > 0 && (
                        <div>
                          <div className={`mb-4 pb-2 border-b-2 ${
                            getEffectiveTheme() === 'dark' 
                              ? 'border-purple-500/30' 
                              : 'border-purple-500/20'
                          }`}>
                            <h3 className={`text-lg font-bold flex items-center gap-2 ${
                              getEffectiveTheme() === 'dark' ? 'text-purple-400' : 'text-purple-600'
                            }`}>
                              ✨ ALTRE SQUADRE
                              <span className={`text-sm font-normal ${
                                getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-500'
                              }`}>
                                ({otherTeams.length})
                              </span>
                            </h3>
                          </div>
                          <div className="space-y-3">
                            {otherTeams.map((team, index) => (
                              <div
                                key={team.id}
                                className={`group relative rounded-xl overflow-hidden transition-all duration-200 hover:shadow-lg ${
                                  getEffectiveTheme() === 'dark'
                                    ? 'bg-gradient-to-r from-gray-800/90 to-gray-900/90 backdrop-blur-sm border border-gray-700/30 hover:bg-gray-800'
                                    : 'bg-gradient-to-r from-white/90 to-gray-50/90 backdrop-blur-sm border border-gray-200/30 hover:bg-gray-50'
                                } shadow-sm`}
                              >
                                {renderMuseumTeamCard(team)}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            ) : (
              /* Empty State - Modern Design */
              <div className={`text-center py-16 rounded-2xl ${
                getEffectiveTheme() === 'dark' 
                  ? 'bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-gray-700/30'
                  : 'bg-gradient-to-br from-gray-50/50 to-white/50 border border-gray-200/30'
              } backdrop-blur-sm`}>
                <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center">
                  <span className="text-4xl">🏟️</span>
                </div>
                <h3 className={`text-xl font-semibold mb-2 ${
                  getEffectiveTheme() === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  Museum Vuoto
                </h3>
                <p className={`text-sm mb-6 max-w-sm mx-auto ${
                  getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  Inizia ad aggiungere le tue squadre del cuore per creare la tua collezione personale
                </p>
                <button
                  onClick={() => {
                    setEditingTeam(null);
                    setTeamFormData({
                      name: '',
                      photo_url: '',
                      base_color: '#FFFFFF',
                      inner_color: '#000000',
                      country: '',
                      team_type: 'Club',
                      notes: ''
                    });
                    setShowTeamForm(true);
                  }}
                  className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-8 py-3 rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl font-medium"
                >
                  ➕ Aggiungi Prima Squadra
                </button>
              </div>
            )}
          </div>
        )}

        {/* Museum Team Form Modal - IMPROVED DESIGN 🎨 */}
        {showTeamForm && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
            <div className={`max-w-3xl w-full max-h-[95vh] overflow-y-auto rounded-xl shadow-2xl border ${
              getEffectiveTheme() === 'dark' 
                ? 'bg-gray-800 border-gray-700' 
                : 'bg-white border-gray-200'
            }`}>
              {/* Header with gradient */}
              <div className={`p-6 border-b ${
                getEffectiveTheme() === 'dark' 
                  ? 'bg-gradient-to-r from-gray-700 to-gray-800 border-gray-600' 
                  : 'bg-gradient-to-r from-green-50 to-blue-50 border-gray-200'
              }`}>
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-blue-500 rounded-full flex items-center justify-center">
                      <span className="text-2xl">🏟️</span>
                    </div>
                    <div>
                      <h3 className={`text-xl font-bold ${
                        getEffectiveTheme() === 'dark' ? 'text-white' : 'text-gray-800'
                      }`}>
                        {editingTeam ? 'Modifica Squadra' : 'Aggiungi al Museum'}
                      </h3>
                      <p className={`text-sm ${
                        getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-600'
                      }`}>
                        {editingTeam ? 'Aggiorna i dettagli della squadra' : 'Crea una nuova squadra per il tuo Museum'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowTeamForm(false)}
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                      getEffectiveTheme() === 'dark' 
                        ? 'text-gray-400 hover:text-white hover:bg-gray-600' 
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <span className="text-xl">✕</span>
                  </button>
                </div>
              </div>

              <div className="p-6">
                <form onSubmit={(e) => { e.preventDefault(); saveMuseumTeam(); }} className="space-y-8">
                  
                  {/* Section 1: Basic Info */}
                  <div className={`p-4 rounded-lg border-l-4 border-green-500 ${
                    getEffectiveTheme() === 'dark' ? 'bg-gray-700/30' : 'bg-green-50'
                  }`}>
                    <h4 className={`font-semibold text-lg mb-4 flex items-center ${
                      getEffectiveTheme() === 'dark' ? 'text-green-300' : 'text-green-700'
                    }`}>
                      <span className="mr-2">📝</span>
                      Informazioni Generali
                    </h4>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      {/* Team Name */}
                      <div>
                        <label className={`block text-sm font-semibold mb-2 ${
                          getEffectiveTheme() === 'dark' ? 'text-gray-200' : 'text-gray-700'
                        }`}>
                          Nome Squadra *
                        </label>
                        <input
                          type="text"
                          required
                          value={teamFormData.name}
                          onChange={(e) => setTeamFormData({...teamFormData, name: e.target.value})}
                          className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-all ${
                            getEffectiveTheme() === 'dark' 
                              ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                              : 'bg-white border-gray-300 placeholder-gray-500'
                          }`}
                          placeholder="Es. Juventus, Brasil, Arsenal..."
                        />
                      </div>

                      {/* Team Type */}
                      <div>
                        <label className={`block text-sm font-semibold mb-2 ${
                          getEffectiveTheme() === 'dark' ? 'text-gray-200' : 'text-gray-700'
                        }`}>
                          Tipo Squadra *
                        </label>
                        <select
                          value={teamFormData.team_type}
                          onChange={(e) => setTeamFormData({...teamFormData, team_type: e.target.value})}
                          className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-all ${
                            getEffectiveTheme() === 'dark' 
                              ? 'bg-gray-700 border-gray-600 text-white' 
                              : 'bg-white border-gray-300'
                          }`}
                        >
                          <option value="Club">🏟️ Club</option>
                          <option value="Nazionale">🌍 Nazionale</option>
                        </select>
                      </div>

                      {/* Country */}
                      <div className="sm:col-span-2">
                        <label className={`block text-sm font-semibold mb-2 ${
                          getEffectiveTheme() === 'dark' ? 'text-gray-200' : 'text-gray-700'
                        }`}>
                          Paese *
                        </label>
                        <input
                          type="text"
                          required
                          value={teamFormData.country}
                          onChange={(e) => setTeamFormData({...teamFormData, country: e.target.value})}
                          className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-all ${
                            getEffectiveTheme() === 'dark' 
                              ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                              : 'bg-white border-gray-300 placeholder-gray-500'
                          }`}
                          placeholder="Es. Italia, Brasile, Inghilterra..."
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section 2: Photo Upload */}
                  <div className={`p-4 rounded-lg border-l-4 border-blue-500 ${
                    getEffectiveTheme() === 'dark' ? 'bg-gray-700/30' : 'bg-blue-50'
                  }`}>
                    <h4 className={`font-semibold text-lg mb-4 flex items-center ${
                      getEffectiveTheme() === 'dark' ? 'text-blue-300' : 'text-blue-700'
                    }`}>
                      <span className="mr-2">📸</span>
                      Foto Squadra
                    </h4>
                    
                    <div className="flex items-center space-x-6">
                      {/* Photo Preview */}
                      <div className="flex-shrink-0">
                        <div className="w-24 h-24 rounded-xl overflow-hidden border-2 border-gray-300 bg-gray-100 flex items-center justify-center">
                          {teamFormData.photo_url ? (
                            <img 
                              src={teamFormData.photo_url} 
                              alt="Team" 
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <span className="text-3xl text-gray-400">🏟️</span>
                          )}
                        </div>
                      </div>

                      {/* Upload Button */}
                      <div className="flex-1">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            if (e.target.files[0]) {
                              const photoUrl = await uploadTeamPhoto(e.target.files[0]);
                              if (photoUrl) {
                                setTeamFormData({...teamFormData, photo_url: photoUrl});
                              }
                            }
                          }}
                          className="hidden"
                          id="team-photo-upload"
                        />
                        <label
                          htmlFor="team-photo-upload"
                          className={`block w-full px-4 py-3 border-2 border-dashed rounded-lg text-center cursor-pointer transition-colors ${
                            getEffectiveTheme() === 'dark' 
                              ? 'border-gray-600 hover:border-gray-500 bg-gray-700/50 hover:bg-gray-700 text-gray-300' 
                              : 'border-gray-300 hover:border-gray-400 bg-gray-50 hover:bg-gray-100 text-gray-600'
                          }`}
                        >
                          <div className="flex flex-col items-center space-y-2">
                            <span className="text-2xl">📁</span>
                            <span className="text-sm font-medium">
                              {teamFormData.photo_url ? 'Cambia Foto' : 'Carica Foto'}
                            </span>
                            <span className="text-xs opacity-75">
                              PNG, JPG fino a 3MB - Compressione automatica
                            </span>
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Colors */}
                  <div className={`p-4 rounded-lg border-l-4 border-purple-500 ${
                    getEffectiveTheme() === 'dark' ? 'bg-gray-700/30' : 'bg-purple-50'
                  }`}>
                    <h4 className={`font-semibold text-lg mb-4 flex items-center ${
                      getEffectiveTheme() === 'dark' ? 'text-purple-300' : 'text-purple-700'
                    }`}>
                      <span className="mr-2">🎨</span>
                      Colori Ufficiali
                    </h4>
                    
                    <div className="grid grid-cols-2 gap-6">
                      <div className="text-center">
                        <label className={`block text-sm font-medium mb-2 ${
                          getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-600'
                        }`}>
                          Colore Base
                        </label>
                        <input
                          type="color"
                          value={teamFormData.base_color}
                          onChange={(e) => setTeamFormData({...teamFormData, base_color: e.target.value})}
                          className="w-full h-12 rounded-lg border-2 border-gray-300 cursor-pointer"
                        />
                      </div>
                      
                      <div className="text-center">
                        <label className={`block text-sm font-medium mb-2 ${
                          getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-600'
                        }`}>
                          Colore Inner
                        </label>
                        <input
                          type="color"
                          value={teamFormData.inner_color}
                          onChange={(e) => setTeamFormData({...teamFormData, inner_color: e.target.value})}
                          className="w-full h-12 rounded-lg border-2 border-gray-300 cursor-pointer"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section 4: Notes */}
                  <div className={`p-4 rounded-lg border-l-4 border-yellow-500 ${
                    getEffectiveTheme() === 'dark' ? 'bg-gray-700/30' : 'bg-yellow-50'
                  }`}>
                    <h4 className={`font-semibold text-lg mb-4 flex items-center ${
                      getEffectiveTheme() === 'dark' ? 'text-yellow-300' : 'text-yellow-700'
                    }`}>
                      <span className="mr-2">📋</span>
                      Note Personali
                    </h4>
                    
                    <textarea
                      value={teamFormData.notes}
                      onChange={(e) => setTeamFormData({...teamFormData, notes: e.target.value})}
                      rows={3}
                      className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-all resize-none ${
                        getEffectiveTheme() === 'dark' 
                          ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                          : 'bg-white border-gray-300 placeholder-gray-500'
                      }`}
                      placeholder="Aggiungi note personali, ricordi, curiosità sulla squadra..."
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t border-gray-200">
                    <button
                      type="submit"
                      className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white py-4 px-6 rounded-lg hover:from-green-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 font-semibold transition-all shadow-lg hover:shadow-xl flex items-center justify-center space-x-2"
                    >
                      <span>{editingTeam ? '💾' : '➕'}</span>
                      <span>{editingTeam ? 'Salva Modifiche' : 'Aggiungi al Museum'}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowTeamForm(false)}
                      className={`px-6 py-4 rounded-lg font-semibold transition-all border-2 ${
                        getEffectiveTheme() === 'dark' 
                          ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
                          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      ❌ Annulla
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Debug Analysis Modal 🔍 */}
        {showDebugModal && debugData && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
            <div className={`max-w-4xl w-full max-h-[90vh] overflow-y-auto rounded-xl shadow-2xl border ${
              getEffectiveTheme() === 'dark' 
                ? 'bg-gray-800 border-gray-700' 
                : 'bg-white border-gray-200'
            }`}>
              {/* Header */}
              <div className={`p-6 border-b ${
                getEffectiveTheme() === 'dark' 
                  ? 'bg-gradient-to-r from-gray-700 to-gray-800 border-gray-600' 
                  : 'bg-gradient-to-r from-yellow-50 to-orange-50 border-gray-200'
              }`}>
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-full flex items-center justify-center">
                      <span className="text-2xl">🔍</span>
                    </div>
                    <div>
                      <h2 className="text-xl font-bold">Analisi Struttura Dati Museum</h2>
                      <p className={`text-sm ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        Diagnostica schema dati squadre
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowDebugModal(false)}
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                      getEffectiveTheme() === 'dark' 
                        ? 'text-gray-400 hover:text-white hover:bg-gray-600' 
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    ✕
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className={`p-4 rounded-lg border ${getEffectiveTheme() === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="text-2xl font-bold text-blue-600">{debugData.totalTeams}</div>
                    <div className="text-sm text-gray-600">Totale Squadre</div>
                  </div>
                  <div className={`p-4 rounded-lg border ${getEffectiveTheme() === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="text-2xl font-bold text-orange-600">{debugData.summary.needsMigration}</div>
                    <div className="text-sm text-gray-600">Da Migrare</div>
                  </div>
                  <div className={`p-4 rounded-lg border ${getEffectiveTheme() === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="text-2xl font-bold text-yellow-600">{debugData.summary.mixedFormat}</div>
                    <div className="text-sm text-gray-600">Formato Misto</div>
                  </div>
                  <div className={`p-4 rounded-lg border ${getEffectiveTheme() === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="text-2xl font-bold text-green-600">{debugData.summary.healthy}</div>
                    <div className="text-sm text-gray-600">Sane</div>
                  </div>
                </div>

                {/* Teams List */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Dettaglio Squadre</h3>
                  <div className="max-h-64 overflow-y-auto">
                    {debugData.detailedAnalysis.map((team, index) => (
                      <div key={index} className={`p-3 rounded-lg border mb-2 ${
                        team.status === 'ok' 
                          ? (getEffectiveTheme() === 'dark' ? 'bg-green-900/20 border-green-800' : 'bg-green-50 border-green-200')
                          : team.status === 'needs_migration'
                          ? (getEffectiveTheme() === 'dark' ? 'bg-orange-900/20 border-orange-800' : 'bg-orange-50 border-orange-200')
                          : (getEffectiveTheme() === 'dark' ? 'bg-yellow-900/20 border-yellow-800' : 'bg-yellow-50 border-yellow-200')
                      }`}>
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="font-medium">{team.name}</div>
                            <div className="text-xs text-gray-600 mt-1">
                              Status: <span className={`px-2 py-1 rounded text-xs ${
                                team.status === 'ok' ? 'bg-green-100 text-green-800' :
                                team.status === 'needs_migration' ? 'bg-orange-100 text-orange-800' :
                                'bg-yellow-100 text-yellow-800'
                              }`}>
                                {team.status === 'ok' ? '✅ OK' :
                                 team.status === 'needs_migration' ? '🔄 Da migrare' :
                                 team.status === 'mixed_format' ? '⚠️ Formato misto' :
                                 '❌ Campi mancanti'}
                              </span>
                            </div>
                          </div>
                          <div className="text-right text-xs space-y-1">
                            {team.colorFields.old.primary_color && (
                              <div>Old: {team.colorFields.old.primary_color}</div>
                            )}
                            {team.colorFields.new.base_color && (
                              <div>New: {team.colorFields.new.base_color}</div>
                            )}
                            {team.missingFields.length > 0 && (
                              <div className="text-red-600">Mancanti: {team.missingFields.join(', ')}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-between items-center pt-6 border-t border-gray-200 mt-6">
                  <div className="text-sm text-gray-600">
                    {debugData.summary.needsMigration > 0 
                      ? `${debugData.summary.needsMigration} squadre necessitano migrazione`
                      : 'Tutte le squadre hanno formato aggiornato!'
                    }
                  </div>
                  <div className="flex gap-2">
                    {debugData.summary.needsMigration > 0 && (
                      <button
                        onClick={() => {
                          setShowDebugModal(false);
                          migrateOldTeams();
                        }}
                        disabled={migrationInProgress}
                        className={`px-4 py-2 rounded-lg text-sm font-medium ${
                          migrationInProgress 
                            ? 'bg-gray-400 text-white cursor-not-allowed'
                            : 'bg-purple-600 text-white hover:bg-purple-700'
                        }`}
                      >
                        {migrationInProgress ? '⏳ Migrazione...' : '🛠️ Migra Ora'}
                      </button>
                    )}
                    <button
                      onClick={() => setShowDebugModal(false)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium border ${
                        getEffectiveTheme() === 'dark' 
                          ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
                          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Chiudi
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* PROFESSIONAL RECOGNITIONS TAB 🏆 */}
        {activeTab === 'achievements' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold">🏆 Riconoscimenti Professionali</h2>
                <p className={`text-sm ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  {allTrophies.filter(t => t.is_completed).length}/{allTrophies.length} riconoscimenti ottenuti
                  {allTrophies.length > 0 && (
                    <span className="ml-2">
                      ({Math.round((allTrophies.filter(t => t.is_completed).length / allTrophies.length) * 100)}% completamento)
                    </span>
                  )}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={checkTrophiesProgress}
                  disabled={loadingStates.trophies}
                  className={`px-3 py-2 rounded-md transition-colors text-sm font-medium ${
                    loadingStates.trophies 
                      ? 'bg-gray-400 text-white cursor-not-allowed' 
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {loadingStates.trophies ? '⏳ Caricamento...' : '🔄 Verifica Progressi'}
                </button>
              </div>
            </div>

            {/* Progress Overview */}
            {allTrophies.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                {['bronze', 'silver', 'gold', 'legendary'].map(difficulty => {
                  const difficultyTrophies = allTrophies.filter(t => t.difficulty === difficulty);
                  const earnedCount = difficultyTrophies.filter(t => t.is_completed).length;
                  return (
                    <div key={difficulty} className={`p-4 rounded-lg ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'} shadow`}>
                      <div className={`text-2xl font-bold ${
                        difficulty === 'promettente' ? 'text-green-600' :
                        difficulty === 'competente' ? 'text-blue-500' :
                        difficulty === 'esperto' ? 'text-orange-500' :
                        'text-purple-600'
                      }`}>
                        {earnedCount}/{difficultyTrophies.length}
                      </div>
                      <div className={`text-sm capitalize ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                        {difficulty}
                      </div>
                    </div>
                  );
                })}
                <div className={`p-4 rounded-lg ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'} shadow`}>
                  <div className="text-2xl font-bold text-green-600">
                    {allTrophies.reduce((sum, t) => sum + (t.is_completed ? t.reward_xp : 0), 0)}
                  </div>
                  <div className={`text-sm ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>XP Totali</div>
                </div>
              </div>
            )}

            {/* Category Filters */}
            <div className="flex flex-wrap gap-2">
              {['tutti', 'performance', 'streak', 'temporal', 'rare', 'fun'].map(category => (
                <button
                  key={category}
                  onClick={() => setTrophyFilter(category)}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    trophyFilter === category
                      ? (getEffectiveTheme() === 'dark' 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-blue-500 text-white')
                      : (getEffectiveTheme() === 'dark' 
                          ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300')
                  }`}
                >
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </button>
              ))}
            </div>

            {/* Trofei Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {allTrophies
                .filter(trophy => {
                  if (trophyFilter === 'tutti') return true;
                  return trophy.category === trophyFilter;
                })
                .sort((a, b) => {
                  // Earned trophies first, then by difficulty and unlock order
                  if (a.is_completed && !b.is_completed) return -1;
                  if (!a.is_completed && b.is_completed) return 1;
                  if (a.difficulty !== b.difficulty) {
                    const diffOrder = {'bronze': 1, 'silver': 2, 'gold': 3, 'legendary': 4};
                    return diffOrder[a.difficulty] - diffOrder[b.difficulty];
                  }
                  return a.unlock_order - b.unlock_order;
                })
                .map((trophy) => (
                  <div key={trophy.id} className={`p-6 rounded-xl shadow-lg border-2 transition-all hover:shadow-xl ${
                    trophy.is_completed 
                      ? `border-yellow-400 ${getEffectiveTheme() === 'dark' ? 'bg-gradient-to-br from-yellow-900/30 to-yellow-800/20' : 'bg-gradient-to-br from-yellow-50 to-yellow-100'} transform hover:scale-105` 
                      : `border-gray-300 ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'} opacity-80`
                  }`}>
                    
                    {/* Trophy Header */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="text-3xl">
                        {trophy.is_completed ? trophy.icon : '🔒'}
                      </div>
                      <div className="flex flex-col items-end">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          trophy.difficulty === 'promettente' ? 'bg-green-100 text-green-800' :
                          trophy.difficulty === 'competente' ? 'bg-blue-100 text-blue-800' :
                          trophy.difficulty === 'esperto' ? 'bg-orange-100 text-orange-800' :
                          'bg-purple-100 text-purple-800'
                        }`}>
                          {trophy.difficulty.toUpperCase()}
                        </span>
                        {trophy.is_completed && (
                          <span className="text-xs text-green-600 font-medium mt-1">
                            +{trophy.reward_xp} XP
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Trophy Content */}
                    <div className="space-y-3">
                      <h3 className={`font-bold text-lg ${trophy.is_completed ? 'text-yellow-700' : getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                        {trophy.name}
                      </h3>
                      <p className={`text-sm ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        {trophy.description}
                      </p>

                      {/* Progress Bar */}
                      <div className="space-y-2">
                        <div className="flex justify-between items-center text-sm">
                          <span className={getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
                            Progresso
                          </span>
                          <span className={`font-semibold ${
                            trophy.is_completed ? 'text-green-600' : getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-600'
                          }`}>
                            {trophy.progress || 0}/{trophy.max_progress || trophy.target_value}
                          </span>
                        </div>
                        <div className={`w-full rounded-full h-2 ${getEffectiveTheme() === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`}>
                          <div 
                            className={`h-2 rounded-full transition-all duration-500 ${
                              trophy.is_completed 
                                ? 'bg-green-500' 
                                : 'bg-blue-500'
                            }`}
                            style={{
                              width: `${Math.min(100, ((trophy.progress || 0) / (trophy.max_progress || trophy.target_value)) * 100)}%`
                            }}
                          ></div>
                        </div>
                      </div>

                      {/* Completion Status */}
                      {trophy.is_completed && trophy.earned_at && (
                        <div className="pt-2 border-t border-yellow-200">
                          <p className="text-xs text-green-600 font-medium">
                            ✅ Sbloccato: {new Date(trophy.earned_at).toLocaleDateString('it-IT')}
                          </p>
                        </div>
                      )}

                      {/* Nearly There Indicator */}
                      {!trophy.is_completed && trophy.progress && 
                       (trophy.progress / (trophy.max_progress || trophy.target_value)) > 0.8 && (
                        <div className="pt-2 border-t border-orange-200">
                          <p className="text-xs text-orange-600 font-medium">
                            🔥 Quasi fatto! {Math.ceil(((trophy.max_progress || trophy.target_value) - trophy.progress))} ancora
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
            </div>

            {/* Empty State */}
            {allTrophies.length === 0 && (
              <div className={`text-center py-12 ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-lg`}>
                <div className="text-6xl mb-4">🏆</div>
                <h3 className="text-xl font-semibold mb-2">Nessun Trofeo Disponibile</h3>
                <p className={`${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Gioca alcune partite per sbloccare il sistema trofei!
                </p>
              </div>
            )}

            {/* Daily Challenges Section */}
            {dailyChallenges && dailyChallenges.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-lg font-bold">🎯 Obiettivi di Allenamento</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {dailyChallenges.map((challenge) => (
                    <div key={challenge.id} className={`p-4 rounded-lg border ${
                      challenge.is_completed
                        ? `border-green-500 ${getEffectiveTheme() === 'dark' ? 'bg-green-900/20' : 'bg-green-50'}`
                        : `border-blue-500 ${getEffectiveTheme() === 'dark' ? 'bg-blue-900/20' : 'bg-blue-50'}`
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold">{challenge.name}</h4>
                        <span className={`text-2xl ${challenge.is_completed ? '✅' : '⏰'}`}></span>
                      </div>
                      <p className={`text-sm mb-3 ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                        {challenge.description}
                      </p>
                      
                      {/* Challenge Progress */}
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Progresso</span>
                          <span className="font-semibold">
                            {challenge.progress || 0}/{challenge.max_progress}
                          </span>
                        </div>
                        <div className={`w-full rounded-full h-2 ${getEffectiveTheme() === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`}>
                          <div 
                            className={`h-2 rounded-full ${
                              challenge.is_completed ? 'bg-green-500' : 'bg-blue-500'
                            }`}
                            style={{
                              width: `${Math.min(100, ((challenge.progress || 0) / challenge.max_progress) * 100)}%`
                            }}
                          ></div>
                        </div>
                      </div>

                      {/* Reward */}
                      <div className="mt-3 pt-2 border-t border-gray-200">
                        <p className="text-xs text-blue-600 font-medium">
                          🎁 Ricompensa: +{challenge.reward_xp} XP
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ANALYTICS PRO & AI INSIGHTS TAB 🧠📊 */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold">🧠 Analytics Pro & AI Coach</h2>
                <p className={`text-sm ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Insights intelligenti e analisi avanzate per migliorare le tue performance
                </p>
              </div>
              <button
                onClick={loadAiInsights}
                disabled={loadingStates.aiInsights}
                className={`px-3 py-2 rounded-md transition-colors text-sm font-medium ${
                  loadingStates.aiInsights 
                    ? 'bg-gray-400 text-white cursor-not-allowed' 
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {loadingStates.aiInsights ? '⏳ Caricamento...' : '🔄 Aggiorna AI Insights'}
              </button>
            </div>

            {/* Performance Score */}
            {aiInsights.performance_score !== undefined && (
              <div className={`p-6 rounded-lg ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'} shadow border-l-4 border-blue-500`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold mb-2">📊 Performance Score</h3>
                    <p className={`text-sm ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                      Il tuo punteggio generale basato su wins e goal average
                    </p>
                  </div>
                  <div className="text-right">
                    <div className={`text-4xl font-bold ${
                      aiInsights.performance_score >= 80 ? 'text-green-500' :
                      aiInsights.performance_score >= 60 ? 'text-yellow-500' :
                      aiInsights.performance_score >= 40 ? 'text-orange-500' :
                      'text-red-500'
                    }`}>
                      {aiInsights.performance_score}/100
                    </div>
                    <div className={`text-sm font-medium ${
                      aiInsights.performance_score >= 80 ? 'text-green-500' :
                      aiInsights.performance_score >= 60 ? 'text-yellow-500' :
                      aiInsights.performance_score >= 40 ? 'text-orange-500' :
                      'text-red-500'
                    }`}>
                      {aiInsights.performance_score >= 80 ? 'ECCELLENTE' :
                       aiInsights.performance_score >= 60 ? 'BUONO' :
                       aiInsights.performance_score >= 40 ? 'NELLA MEDIA' :
                       'DA MIGLIORARE'}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* AI Insights Feed */}
            {aiInsights.insights && aiInsights.insights.length > 0 && (
              <div className={`p-6 rounded-lg ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'} shadow`}>
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <span className="mr-2">🤖</span>
                  AI Insights - Analisi Intelligente
                </h3>
                <div className="space-y-3">
                  {aiInsights.insights.map((insight, index) => (
                    <div key={index} className={`p-4 rounded-lg border-l-4 border-blue-400 ${
                      getEffectiveTheme() === 'dark' ? 'bg-blue-900/20' : 'bg-blue-50'
                    }`}>
                      <p className={`font-medium ${getEffectiveTheme() === 'dark' ? 'text-blue-300' : 'text-blue-700'}`}>
                        {insight}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* AI Predictions */}
            {aiInsights.predictions && aiInsights.predictions.length > 0 && (
              <div className={`p-6 rounded-lg ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'} shadow`}>
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <span className="mr-2">🔮</span>
                  Predizioni AI
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {aiInsights.predictions.map((prediction, index) => (
                    <div key={index} className={`p-4 rounded-lg border ${
                      getEffectiveTheme() === 'dark' ? 'border-purple-600 bg-purple-900/20' : 'border-purple-300 bg-purple-50'
                    }`}>
                      <p className={`font-medium ${getEffectiveTheme() === 'dark' ? 'text-purple-300' : 'text-purple-700'}`}>
                        {prediction}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* AI Recommendations */}
            {aiInsights.recommendations && aiInsights.recommendations.length > 0 && (
              <div className={`p-6 rounded-lg ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'} shadow`}>
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <span className="mr-2">💡</span>
                  Raccomandazioni Personalizzate
                </h3>
                <div className="space-y-3">
                  {aiInsights.recommendations.map((recommendation, index) => (
                    <div key={index} className={`p-4 rounded-lg border-l-4 border-green-400 ${
                      getEffectiveTheme() === 'dark' ? 'bg-green-900/20' : 'bg-green-50'
                    }`}>
                      <p className={`font-medium ${getEffectiveTheme() === 'dark' ? 'text-green-300' : 'text-green-700'}`}>
                        {recommendation}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Advanced Analytics */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              
              {/* Temporal Analysis */}
              {advancedStats.temporal_analysis && (
                <div className={`p-6 rounded-lg ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'} shadow`}>
                  <h3 className="text-lg font-semibold mb-4">⏰ Analisi Temporale</h3>
                  
                  {/* Best Days */}
                  {advancedStats.temporal_analysis.daily && (
                    <div className="mb-6">
                      <h4 className="font-medium mb-3">📅 Performance per Giorno</h4>
                      <div className="space-y-2">
                        {Object.entries(advancedStats.temporal_analysis.daily)
                          .sort(([,a], [,b]) => b.win_rate - a.win_rate)
                          .slice(0, 4)
                          .map(([day, stats]) => (
                            <div key={day} className="flex justify-between items-center p-2 rounded bg-gray-100 dark:bg-gray-700">
                              <span className="font-medium">{day}</span>
                              <div className="text-right">
                                <span className="text-green-600 font-bold">{stats.win_rate}%</span>
                                <span className={`text-sm ml-2 ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                                  ({stats.wins}/{stats.total})
                                </span>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Monthly Trends */}
                  {advancedStats.temporal_analysis.monthly && (
                    <div>
                      <h4 className="font-medium mb-3">📈 Trend Mensili</h4>
                      <div className="space-y-2">
                        {Object.entries(advancedStats.temporal_analysis.monthly)
                          .sort(([a], [b]) => b.localeCompare(a))
                          .slice(0, 6)
                          .map(([month, stats]) => (
                            <div key={month} className="flex justify-between items-center p-2 rounded bg-gray-100 dark:bg-gray-700">
                              <span className="font-medium">{month}</span>
                              <div className="text-right">
                                <span className="text-blue-600 font-bold">{stats.win_rate}%</span>
                                <span className={`text-sm ml-2 ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                                  {stats.avg_goals} goal/partita
                                </span>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Performance Matrix */}
              {advancedStats.performance_matrix && (
                <div className={`p-6 rounded-lg ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'} shadow`}>
                  <h3 className="text-lg font-semibold mb-4">⚽ Matrix Performance Squadre</h3>
                  <div className="space-y-3">
                    {Object.entries(advancedStats.performance_matrix)
                      .sort(([,a], [,b]) => b.win_rate - a.win_rate)
                      .slice(0, 8)
                      .map(([team, stats]) => (
                        <div key={team} className={`p-3 rounded-lg ${
                          stats.win_rate >= 70 ? 'bg-green-100 dark:bg-green-900/30' :
                          stats.win_rate >= 50 ? 'bg-yellow-100 dark:bg-yellow-900/30' :
                          'bg-red-100 dark:bg-red-900/30'
                        }`}>
                          <div className="flex justify-between items-center mb-1">
                            <span className="font-semibold">{team}</span>
                            <span className={`font-bold ${
                              stats.win_rate >= 70 ? 'text-green-600' :
                              stats.win_rate >= 50 ? 'text-yellow-600' :
                              'text-red-600'
                            }`}>
                              {stats.win_rate}%
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>{stats.wins}V/{stats.total}P</span>
                            <span>
                              {stats.avg_goals_for} - {stats.avg_goals_against} avg
                            </span>
                          </div>
                          {stats.clean_sheets > 0 && (
                            <div className="text-xs text-blue-600 mt-1">
                              🛡️ {stats.clean_sheets} clean sheets
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>

            {/* Streak Analysis */}
            {advancedStats.streak_analysis && (
              <div className={`p-6 rounded-lg ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'} shadow`}>
                <h3 className="text-lg font-semibold mb-4">🔥 Analisi Serie e Streak</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                  
                  {/* Current Streak */}
                  <div className="text-center">
                    <div className={`text-3xl font-bold ${
                      advancedStats.streak_analysis.current_streak?.type === 'win' ? 'text-green-500' :
                      advancedStats.streak_analysis.current_streak?.type === 'loss' ? 'text-red-500' :
                      'text-yellow-500'
                    }`}>
                      {advancedStats.streak_analysis.current_streak?.count || 0}
                    </div>
                    <div className={`text-sm font-medium ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                      {advancedStats.streak_analysis.current_streak?.type === 'win' ? '🔥 Vittorie Consecutive' :
                       advancedStats.streak_analysis.current_streak?.type === 'loss' ? '❄️ Sconfitte Consecutive' :
                       '⚖️ Pareggi Consecutivi'}
                    </div>
                  </div>

                  {/* Longest Win Streak */}
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-500">
                      {advancedStats.streak_analysis.longest_win_streak || 0}
                    </div>
                    <div className={`text-sm font-medium ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                      🏆 Record Vittorie
                    </div>
                  </div>

                  {/* Longest Loss Streak */}
                  <div className="text-center">
                    <div className="text-3xl font-bold text-red-500">
                      {advancedStats.streak_analysis.longest_loss_streak || 0}
                    </div>
                    <div className={`text-sm font-medium ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                      😓 Peggior Serie
                    </div>
                  </div>
                </div>

                {/* Recent Streaks History */}
                {advancedStats.streak_analysis.recent_streaks && (
                  <div className="mt-6">
                    <h4 className="font-medium mb-3">📊 Serie Recenti</h4>
                    <div className="flex flex-wrap gap-2">
                      {advancedStats.streak_analysis.recent_streaks.slice(-10).map((streak, index) => (
                        <div key={index} className={`px-3 py-1 rounded-full text-sm font-medium ${
                          streak.type === 'win' ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300' :
                          streak.type === 'loss' ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300' :
                          'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300'
                        }`}>
                          {streak.count}{streak.type === 'win' ? 'V' : streak.type === 'loss' ? 'L' : 'D'}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Goal Patterns */}
            {advancedStats.goal_patterns && (
              <div className={`p-6 rounded-lg ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'} shadow`}>
                <h3 className="text-lg font-semibold mb-4">⚽ Pattern dei Goal</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  
                  {/* My Goals Distribution */}
                  {advancedStats.goal_patterns.my_goals_distribution && (
                    <div>
                      <h4 className="font-medium mb-3">🥅 Distribuzione Goal Segnati</h4>
                      <div className="space-y-2">
                        {Object.entries(advancedStats.goal_patterns.my_goals_distribution)
                          .sort(([,a], [,b]) => b - a)
                          .slice(0, 5)
                          .map(([goals, count]) => (
                            <div key={goals} className="flex justify-between items-center p-2 rounded bg-gray-100 dark:bg-gray-700">
                              <span>{goals} goal{parseInt(goals) !== 1 ? 's' : ''}</span>
                              <span className="font-bold text-green-600">{count} volte</span>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Opponent Goals Distribution */}
                  {advancedStats.goal_patterns.opponent_goals_distribution && (
                    <div>
                      <h4 className="font-medium mb-3">🛡️ Distribuzione Goal Subiti</h4>
                      <div className="space-y-2">
                        {Object.entries(advancedStats.goal_patterns.opponent_goals_distribution)
                          .sort(([,a], [,b]) => b - a)
                          .slice(0, 5)
                          .map(([goals, count]) => (
                            <div key={goals} className="flex justify-between items-center p-2 rounded bg-gray-100 dark:bg-gray-700">
                              <span>{goals} goal{parseInt(goals) !== 1 ? 's' : ''} subiti</span>
                              <span className="font-bold text-blue-600">{count} volte</span>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Most Common Scoreline */}
                {advancedStats.goal_patterns.most_common_scoreline && (
                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600 text-center">
                    <p className={`text-sm ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                      Risultato più comune:
                    </p>
                    <p className="text-2xl font-bold text-blue-600">
                      {advancedStats.goal_patterns.most_common_scoreline}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Empty State */}
            {(!aiInsights.insights || aiInsights.insights.length === 0) && (
              <div className={`text-center py-12 ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'} rounded-lg`}>
                <div className="text-6xl mb-4">🧠</div>
                <h3 className="text-xl font-semibold mb-2">AI Coach sta Imparando</h3>
                <p className={`${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Gioca almeno 5 partite per ricevere insights personalizzati dall'AI!
                </p>
              </div>
            )}

            {/* Footer Info */}
            {aiInsights.generated_at && (
              <div className={`text-center text-xs ${getEffectiveTheme() === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                Ultimo aggiornamento AI: {new Date(aiInsights.generated_at).toLocaleString('it-IT')}
              </div>
            )}
          </div>
        )}

        {/* I MIEI CLUB TAB 🏢 */}
        {activeTab === 'my-clubs' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl sm:text-2xl font-bold">🏢 I Miei Club</h2>
              <div className={`px-3 py-1 rounded-full text-sm ${
                getEffectiveTheme() === 'dark' ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-800'
              }`}>
                {userClubs.length} club
              </div>
            </div>

            {/* Join Club Section */}
            <div className={`p-6 rounded-lg shadow ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <span className="mr-2">🎟️</span> Hai un codice invito?
              </h3>
              <p className={`text-sm mb-4 ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Se hai ricevuto un codice da un club, puoi attivarlo per unirti.
              </p>
              
              <Link 
                to="/attiva-codice"
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
              >
                🎟️ Attiva Codice Club
              </Link>
            </div>

            {/* My Clubs List */}
            <div className={`p-6 rounded-lg shadow ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <span className="mr-2">📋</span> I Tuoi Club
              </h3>
              
              {loadingClubs ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-gray-500">Caricamento club...</p>
                </div>
              ) : userClubs.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">🏢</div>
                  <p className={`text-lg font-medium ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    Non sei ancora membro di nessun club
                  </p>
                  <p className={`text-sm mt-2 ${getEffectiveTheme() === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                    Chiedi un codice al presidente del tuo club per unirti!
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {userClubs.map((membership) => (
                    <div 
                      key={membership.id}
                      className={`p-4 rounded-lg border-2 ${
                        getEffectiveTheme() === 'dark' 
                          ? 'bg-gray-700 border-gray-600' 
                          : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="text-3xl">🏢</div>
                          <div>
                            <h4 className="font-bold text-lg">{membership.club_name}</h4>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                membership.role === 'admin' 
                                  ? 'bg-orange-100 text-orange-700' 
                                  : 'bg-blue-100 text-blue-700'
                              }`}>
                                {membership.role === 'admin' ? '🔑 Admin' : '👁️ Socio'}
                              </span>
                              {membership.club_city && (
                                <span className={`text-sm ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                  📍 {membership.club_city}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/mio-club/${membership.club_id}`}
                            className={`px-3 py-1 rounded text-sm font-medium ${
                              getEffectiveTheme() === 'dark' 
                                ? 'bg-blue-900/50 text-blue-300 hover:bg-blue-900/70' 
                                : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                            }`}
                            data-testid={`view-club-${membership.club_id}`}
                          >
                            👁️ Visualizza
                          </Link>
                          <button
                            onClick={() => leaveClub(membership.club_id, membership.club_name)}
                            className={`px-3 py-1 rounded text-sm ${
                              getEffectiveTheme() === 'dark' 
                                ? 'bg-red-900/30 text-red-400 hover:bg-red-900/50' 
                                : 'bg-red-50 text-red-600 hover:bg-red-100'
                            }`}
                          >
                            Esci
                          </button>
                        </div>
                      </div>
                      
                      <div className={`mt-3 pt-3 border-t text-sm ${
                        getEffectiveTheme() === 'dark' ? 'border-gray-600 text-gray-400' : 'border-gray-200 text-gray-500'
                      }`}>
                        <span>Iscritto il: {new Date(membership.joined_at).toLocaleDateString('it-IT')}</span>
                        {membership.role === 'admin' && (
                          <span className="ml-4">✏️ Puoi modificare la pagina del club</span>
                        )}
                        {membership.role === 'socio' && (
                          <span className="ml-4">👁️ Puoi visualizzare la pagina del club</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Info Box */}
            <div className={`p-4 rounded-lg ${getEffectiveTheme() === 'dark' ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
              <h4 className={`font-semibold mb-2 ${getEffectiveTheme() === 'dark' ? 'text-blue-300' : 'text-blue-800'}`}>
                💡 Come funziona?
              </h4>
              <ul className={`text-sm space-y-1 ${getEffectiveTheme() === 'dark' ? 'text-blue-200' : 'text-blue-700'}`}>
                <li>• <strong>Codice Socio:</strong> ti permette di visualizzare la pagina del club</li>
                <li>• <strong>Codice Admin:</strong> ti permette anche di modificare la pagina del club</li>
                <li>• Puoi essere membro di più club contemporaneamente</li>
                <li>• Le tue statistiche personali rimangono sempre separate</li>
              </ul>
            </div>
          </div>
        )}

        {/* SETTINGS PRO TAB ⚙️ */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl sm:text-2xl font-bold">⚙️ Impostazioni Pro</h2>
              <div className={`px-3 py-1 rounded-full text-sm ${
                getEffectiveTheme() === 'dark' ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-800'
              }`}>
                Settings 2.0
              </div>
            </div>
            
            {/* Tema e Personalizzazione */}
            <div className={`p-6 rounded-lg shadow ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <span className="mr-2">🎨</span> Tema & Personalizzazione
              </h3>
              <div className="space-y-6">
                {/* Profile Section */}
                <div>
                  <label className={`block text-sm font-medium mb-3 ${
                    getEffectiveTheme() === 'dark' ? 'text-gray-200' : 'text-gray-700'
                  }`}>
                    Profilo
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Nome Giocatore */}
                    <div>
                      <input
                        type="text"
                        placeholder="Il tuo nome..."
                        value={localPlayerName}
                        onChange={(e) => handlePlayerNameChange(e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md ${
                          getEffectiveTheme() === 'dark' 
                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                            : 'bg-white border-gray-300'
                        }`}
                      />
                    </div>
                    
                    {/* Upload Immagine Profilo */}
                    <div>
                      <div className="flex items-center space-x-3">
                        {settings?.profile_image && (
                          <div className="w-10 h-10 rounded-full overflow-hidden border">
                            <img 
                              src={settings.profile_image} 
                              alt="Profile" 
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        <div className="flex-1">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={async (e) => {
                              if (e.target.files[0]) {
                                const photoUrl = await uploadProfilePhoto(e.target.files[0]);
                                if (photoUrl) {
                                  updateSettings({...settings, profile_image: photoUrl});
                                }
                              }
                            }}
                            className="text-sm file:mr-4 file:py-1 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-medium file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Tema chiaro/scuro */}
                <div>
                  <label className={`block text-sm font-medium mb-3 ${
                    getEffectiveTheme() === 'dark' ? 'text-gray-200' : 'text-gray-700'
                  }`}>
                    Tema Interfaccia
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => {
                        setTheme('light');
                        updateSettings({...settings, theme: 'light'});
                      }}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        settings.theme === 'light' 
                          ? 'border-green-500 bg-green-50 text-green-700 shadow-lg' 
                          : `border-gray-300 hover:border-gray-400 ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : ''}`
                      }`}
                    >
                      <div className="text-center">
                        <div className="text-2xl mb-2">☀️</div>
                        <div className="font-medium">Chiaro</div>
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        setTheme('dark');
                        updateSettings({...settings, theme: 'dark'});
                      }}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        settings.theme === 'dark' 
                          ? 'border-green-500 bg-green-50 text-green-700 shadow-lg' 
                          : `border-gray-300 hover:border-gray-400 ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : ''}`
                      }`}
                    >
                      <div className="text-center">
                        <div className="text-2xl mb-2">🌙</div>
                        <div className="font-medium">Scuro</div>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Colore Squadra del Cuore */}
                <div>
                  <label className={`block text-sm font-medium mb-3 ${
                    getEffectiveTheme() === 'dark' ? 'text-gray-200' : 'text-gray-700'
                  }`}>
                    Colore Squadra del Cuore
                  </label>
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                    {[
                      { name: 'Verde', value: 'green', emoji: '🟢' },
                      { name: 'Blu', value: 'blue', emoji: '🔵' },
                      { name: 'Rosso', value: 'red', emoji: '🔴' },
                      { name: 'Viola', value: 'purple', emoji: '🟣' },
                      { name: 'Arancione', value: 'orange', emoji: '🟠' },
                      { name: 'Rosa', value: 'pink', emoji: '🩷' }
                    ].map((color) => (
                      <button
                        key={color.value}
                        onClick={() => updateSettings({
                          ...settings, 
                          accent_color: color.value
                        })}
                        className={`p-3 rounded-lg border-2 transition-all ${
                          settings.accent_color === color.value
                            ? 'border-gray-800 dark:border-white shadow-lg transform scale-105'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                        title={color.name}
                      >
                        <div className="text-center">
                          <div className="text-2xl mb-1">{color.emoji}</div>
                          <div className="text-xs font-medium">{color.name}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Layout Dashboard */}
                <div>
                  <label className={`block text-sm font-medium mb-3 ${
                    getEffectiveTheme() === 'dark' ? 'text-gray-200' : 'text-gray-700'
                  }`}>
                    Layout Dashboard
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => updateSettings({...settings, dashboard_style: 'compact'})}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        settings.dashboard_style === 'compact'
                          ? 'border-green-500 bg-green-50 text-green-700 shadow-lg'
                          : `border-gray-300 hover:border-gray-400 ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : ''}`
                      }`}
                    >
                      <div className="text-center">
                        <div className="text-2xl mb-2">📱</div>
                        <div className="font-medium">Compatto</div>
                        <div className="text-xs mt-1 opacity-75">Più info, meno spazio</div>
                      </div>
                    </button>
                    <button
                      onClick={() => updateSettings({...settings, dashboard_style: 'expanded'})}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        settings.dashboard_style === 'expanded'
                          ? 'border-green-500 bg-green-50 text-green-700 shadow-lg'
                          : `border-gray-300 hover:border-gray-400 ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : ''}`
                      }`}
                    >
                      <div className="text-center">
                        <div className="text-2xl mb-2">🖥️</div>
                        <div className="font-medium">Espanso</div>
                        <div className="text-xs mt-1 opacity-75">Card più grandi</div>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* 🌐 Profilo Pubblico */}
            <div className={`p-6 rounded-lg shadow ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <span className="mr-2">🌐</span> Profilo Pubblico
              </h3>
              <div className="space-y-4">
                <p className={`text-sm ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Attiva il tuo profilo pubblico per condividere le tue statistiche con amici e avversari. 
                  Il profilo sarà visibile a chiunque abbia il link.
                </p>
                
                <div className="flex items-center justify-between p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{profiloPublicEnabled ? '✅' : '🔒'}</span>
                    <div>
                      <div className="font-medium">Profilo Pubblico</div>
                      <div className={`text-sm ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        {profiloPublicEnabled ? 'Visibile a tutti' : 'Solo tu puoi vedere'}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={toggleProfiloPublico}
                    disabled={togglingProfilo}
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      profiloPublicEnabled
                        ? 'bg-red-100 text-red-700 hover:bg-red-200'
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
                  >
                    {togglingProfilo ? '...' : profiloPublicEnabled ? 'Disattiva' : 'Attiva'}
                  </button>
                </div>
                
                {profiloPublicEnabled && profiloUsername && (
                  <div className="p-3 sm:p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                    <div className="space-y-3">
                      <div>
                        <div className="text-sm text-green-700 dark:text-green-300 font-medium">Il tuo link pubblico:</div>
                        <div className="text-green-800 dark:text-green-200 font-mono text-xs sm:text-sm mt-1 break-all">
                          {window.location.origin}/profilo/{profiloUsername}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(`${window.location.origin}/profilo/${profiloUsername}`);
                            showToastNotification('Link copiato!', 'success');
                          }}
                          className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"
                        >
                          📋 Copia
                        </button>
                        <button
                          onClick={() => window.open(`/profilo/${profiloUsername}`, '_blank')}
                          className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                        >
                          👁️ Anteprima
                        </button>
                      </div>
                    </div>
                    
                    {/* Pulsanti Social */}
                    <div className="mt-4 pt-4 border-t border-green-200 dark:border-green-800">
                      <div className="text-sm text-green-700 dark:text-green-300 mb-3">Condividi su:</div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => {
                            const shareUrl = `${window.location.origin}/api/share/profilo/${profiloUsername}`;
                            window.open(`https://wa.me/?text=${encodeURIComponent(`Guarda le mie statistiche! ${shareUrl}`)}`, '_blank');
                          }}
                          className="px-3 py-2 bg-green-500 text-white rounded-lg text-sm hover:bg-green-600 flex items-center gap-1"
                        >
                          📱 WhatsApp
                        </button>
                        <button
                          onClick={() => {
                            const shareUrl = `${window.location.origin}/api/share/profilo/${profiloUsername}`;
                            window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
                          }}
                          className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 flex items-center gap-1"
                        >
                          📘 Facebook
                        </button>
                        <button
                          onClick={() => {
                            const url = `${window.location.origin}/profilo/${profiloUsername}`;
                            navigator.clipboard.writeText(url);
                            showToastNotification('Link copiato! Incollalo su Instagram 📸', 'success');
                            // Apri Instagram se su mobile
                            const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
                            if (isMobile) {
                              setTimeout(() => window.open('instagram://app', '_blank'), 500);
                            }
                          }}
                          className="px-3 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg text-sm hover:from-purple-600 hover:to-pink-600 flex items-center gap-1"
                        >
                          📸 Instagram
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* 🔗 Importa Risultati da Link */}
            <ImportStatsFromLink 
              token={localStorage.getItem('subbuteo_token') || sessionStorage.getItem('subbuteo_token')}
              theme={getEffectiveTheme()}
              onSuccess={() => {
                loadMatches();
                showToastNotification('Partite importate con successo!', 'success');
              }}
            />

            {/* Obiettivi Personali Pro */}
            <div className={`p-6 rounded-lg shadow ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <span className="mr-2">🎯</span> I Tuoi Obiettivi Personali
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    getEffectiveTheme() === 'dark' ? 'text-gray-200' : 'text-gray-700'
                  }`}>
                    🎮 Partite/Mese
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={settings.goals?.monthly_matches || 10}
                    onChange={(e) => {
                      // Solo aggiorna lo stato locale, non salva
                      setSettings({
                        ...settings, 
                        goals: {...settings.goals, monthly_matches: parseInt(e.target.value)}
                      });
                    }}
                    onBlur={(e) => {
                      // Salva solo quando l'utente finisce di editare
                      updateSettings({
                        ...settings, 
                        goals: {...settings.goals, monthly_matches: parseInt(e.target.value)}
                      });
                    }}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                      getEffectiveTheme() === 'dark' 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300'
                    }`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    getEffectiveTheme() === 'dark' ? 'text-gray-200' : 'text-gray-700'
                  }`}>
                    🏆 % Vittorie Target
                  </label>
                  <input
                    type="number"
                    min="10"
                    max="100"
                    value={settings.goals?.win_percentage || 60}
                    onChange={(e) => {
                      // Solo aggiorna lo stato locale, non salva
                      setSettings({
                        ...settings, 
                        goals: {...settings.goals, win_percentage: parseInt(e.target.value)}
                      });
                    }}
                    onBlur={(e) => {
                      // Salva solo quando l'utente finisce di editare
                      updateSettings({
                        ...settings, 
                        goals: {...settings.goals, win_percentage: parseInt(e.target.value)}
                      });
                    }}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                      getEffectiveTheme() === 'dark' 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300'
                    }`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    getEffectiveTheme() === 'dark' ? 'text-gray-200' : 'text-gray-700'
                  }`}>
                    ⚽ Goal/Mese
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="200"
                    value={settings.goals?.monthly_goals || 20}
                    onChange={(e) => {
                      // Solo aggiorna lo stato locale, non salva
                      setSettings({
                        ...settings, 
                        goals: {...settings.goals, monthly_goals: parseInt(e.target.value)}
                      });
                    }}
                    onBlur={(e) => {
                      // Salva solo quando l'utente finisce di editare
                      updateSettings({
                        ...settings, 
                        goals: {...settings.goals, monthly_goals: parseInt(e.target.value)}
                      });
                    }}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                      getEffectiveTheme() === 'dark' 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300'
                    }`}
                  />
                </div>
              </div>
            </div>

            {/* Notifiche e Celebrazioni */}
            <div className={`p-6 rounded-lg shadow ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <span className="mr-2">🔔</span> Notifiche & Celebrazioni
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.notifications?.achievements || true}
                      onChange={(e) => updateSettings({
                        ...settings,
                        notifications: {
                          ...settings.notifications,
                          achievements: e.target.checked
                        }
                      })}
                      className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    />
                    <div>
                      <div className="font-medium">🏆 Riconoscimenti Ottenuti</div>
                      <div className="text-sm text-gray-500">Celebra i tuoi risultati professionali</div>
                    </div>
                  </label>
                </div>
                <div>
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.notifications?.celebrations || true}
                      onChange={(e) => updateSettings({
                        ...settings,
                        notifications: {
                          ...settings.notifications,
                          celebrations: e.target.checked
                        }
                      })}
                      className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    />
                    <div>
                      <div className="font-medium">🎉 Animazioni Vittoria</div>
                      <div className="text-sm text-gray-500">Celebrazioni animate</div>
                    </div>
                  </label>
                </div>
                <div>
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.notifications?.streaks || true}
                      onChange={(e) => updateSettings({
                        ...settings,
                        notifications: {
                          ...settings.notifications,
                          streaks: e.target.checked
                        }
                      })}
                      className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    />
                    <div>
                      <div className="font-medium">🔥 Serie Vincenti</div>
                      <div className="text-sm text-gray-500">Avvisi per streak importanti</div>
                    </div>
                  </label>
                </div>
                <div>
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.notifications?.goals || true}
                      onChange={(e) => updateSettings({
                        ...settings,
                        notifications: {
                          ...settings.notifications,
                          goals: e.target.checked
                        }
                      })}
                      className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    />
                    <div>
                      <div className="font-medium">🎯 Obiettivi Raggiunti</div>
                      <div className="text-sm text-gray-500">Progress verso i tuoi goal</div>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            {/* Export e Backup Pro */}
            <div className={`p-6 rounded-lg shadow ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <span className="mr-2">💾</span> Export & Backup Avanzato
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <button
                  onClick={createBackup}
                  className="flex items-center justify-center space-x-2 bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 transition-colors"
                >
                  <span>☁️</span>
                  <span>Auto-Backup</span>
                </button>
                <button
                  onClick={() => document.getElementById('restore-input').click()}
                  className="flex items-center justify-center space-x-2 bg-orange-600 text-white px-4 py-3 rounded-lg hover:bg-orange-700 transition-colors"
                >
                  <span>⚡</span>
                  <span>Ripristina</span>
                </button>
                <button
                  onClick={exportCompleteData}
                  className="flex items-center justify-center space-x-2 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <span>📁</span>
                  <span>Export JSON</span>
                </button>
                <button
                  onClick={exportAdvancedReport}
                  className="flex items-center justify-center space-x-2 bg-red-600 text-white px-4 py-3 rounded-lg hover:bg-red-700 transition-colors"
                >
                  <span>📄</span>
                  <span>Report PDF</span>
                </button>
              </div>

              {/* Hidden File Input for Restore */}
              <input
                id="restore-input"
                type="file"
                accept=".json"
                style={{ display: 'none' }}
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    restoreFromBackup(e.target.files[0]);
                  }
                }}
              />

              {/* Backup Info */}
              {backupData && (
                <div className={`mt-4 p-4 rounded-lg border ${
                  getEffectiveTheme() === 'dark' ? 'bg-green-900/20 border-green-600' : 'bg-green-50 border-green-300'
                }`}>
                  <h4 className="font-semibold text-green-600 dark:text-green-400 mb-2">✅ Ultimo Backup Creato</h4>
                  <div className="text-sm space-y-1">
                    <div>📅 Data: {new Date(backupData.created_at).toLocaleString('it-IT')}</div>
                    <div>📊 {backupData.stats.total_matches} partite • {backupData.stats.total_museum_teams} squadre Museum</div>
                    <div>💾 Versione: {backupData.version}</div>
                  </div>
                </div>
              )}

              {/* Instructions */}
              <div className={`mt-4 p-4 rounded-lg ${
                getEffectiveTheme() === 'dark' ? 'bg-gray-700' : 'bg-gray-100'
              }`}>
                <h4 className="font-medium mb-2">ℹ️ Istruzioni Backup</h4>
                <div className="text-sm space-y-1">
                  <div><strong>Auto-Backup:</strong> Crea e scarica backup automatico completo</div>
                  <div><strong>Ripristina:</strong> Carica un file backup .json per ripristinare i dati</div>
                  <div><strong>Export JSON:</strong> Scarica dati in formato JSON compatibile</div>
                  <div><strong>Report PDF:</strong> Genera report statistiche dettagliato</div>
                </div>
              </div>
            </div>


            {/* 📧 CALENDAR NOTIFICATIONS - NEW */}
            <div className={`p-6 rounded-lg shadow border-2 ${
              getEffectiveTheme() === 'dark' 
                ? 'bg-gray-800 border-orange-400/30' 
                : 'bg-orange-50/50 border-orange-200'
            }`}>
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <span className="mr-2">📧</span> Notifiche Calendario
              </h3>
              <p className={`text-sm mb-6 ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Ricevi promemoria via email per i tuoi eventi del calendario
              </p>
              
              <div className="space-y-4">
                {/* Weekly Email */}
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="weekly_email"
                    checked={calendarNotifications.weekly_email}
                    onChange={(e) => {
                      const newPrefs = {...calendarNotifications, weekly_email: e.target.checked};
                      setCalendarNotifications(newPrefs);
                    }}
                    className="mt-1 w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                  />
                  <label htmlFor="weekly_email" className="cursor-pointer flex-1">
                    <div className="font-medium flex items-center">
                      📅 Email settimanale eventi
                      <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">Ogni Lunedì</span>
                    </div>
                    <div className={`text-sm ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      Riepilogo degli eventi della settimana ogni lunedì mattina
                    </div>
                  </label>
                </div>

                {/* Monthly Email */}
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="monthly_email"
                    checked={calendarNotifications.monthly_email}
                    onChange={(e) => {
                      const newPrefs = {...calendarNotifications, monthly_email: e.target.checked};
                      setCalendarNotifications(newPrefs);
                    }}
                    className="mt-1 w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                  />
                  <label htmlFor="monthly_email" className="cursor-pointer flex-1">
                    <div className="font-medium flex items-center">
                      📆 Email mensile calendario
                      <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">1° del mese</span>
                    </div>
                    <div className={`text-sm ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      Recap completo degli eventi del mese il primo giorno
                    </div>
                  </label>
                </div>

                {/* 24h Reminder */}
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="reminder_24h"
                    checked={calendarNotifications.reminder_24h}
                    onChange={(e) => {
                      const newPrefs = {...calendarNotifications, reminder_24h: e.target.checked};
                      setCalendarNotifications(newPrefs);
                    }}
                    className="mt-1 w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                  />
                  <label htmlFor="reminder_24h" className="cursor-pointer flex-1">
                    <div className="font-medium flex items-center">
                      ⏰ Reminder 24 ore prima
                      <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">Il giorno prima</span>
                    </div>
                    <div className={`text-sm ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      Promemoria il giorno prima dell'evento
                    </div>
                  </label>
                </div>

                {/* 1 Week Reminder */}
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="reminder_1week"
                    checked={calendarNotifications.reminder_1week}
                    onChange={(e) => {
                      const newPrefs = {...calendarNotifications, reminder_1week: e.target.checked};
                      setCalendarNotifications(newPrefs);
                    }}
                    className="mt-1 w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                  />
                  <label htmlFor="reminder_1week" className="cursor-pointer flex-1">
                    <div className="font-medium flex items-center">
                      📌 Reminder 1 settimana prima
                      <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full">7 giorni prima</span>
                    </div>
                    <div className={`text-sm ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      Promemoria una settimana prima dell'evento
                    </div>
                  </label>
                </div>
              </div>

              {/* Save Button */}
              <div className="mt-6 pt-6 border-t border-orange-200">
                <button
                  onClick={() => saveCalendarNotifications(calendarNotifications)}
                  className="w-full px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-lg hover:from-orange-600 hover:to-pink-600 transition-all font-medium shadow-lg hover:shadow-xl"
                >
                  💾 Salva Preferenze Notifiche
                </button>
              </div>

              {/* Info Box */}
              <div className={`mt-4 p-4 rounded-lg ${
                getEffectiveTheme() === 'dark' ? 'bg-gray-700' : 'bg-white'
              }`}>
                <h4 className="font-medium mb-2 flex items-center">
                  <span className="mr-2">💡</span>
                  Come funziona
                </h4>
                <div className={`text-sm space-y-1 ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  <div>• Le email vengono inviate automaticamente ogni giorno alle 8:00 AM</div>
                  <div>• Email settimanale: ogni lunedì con eventi dei prossimi 7 giorni</div>
                  <div>• Email mensile: il 1° del mese con tutti gli eventi del mese</div>
                  <div>• Promemoria vengono inviati solo se hai eventi in programma</div>
                  <div>• Email: <strong>{user?.email || 'il tuo indirizzo'}</strong></div>
                </div>
              </div>
            </div>

            {/* 💾 BACKUP & EXPORT DATA - NEW SECTION */}
            <div className={`p-6 rounded-lg shadow border-2 ${
              getEffectiveTheme() === 'dark' 
                ? 'bg-gray-800 border-green-400/30' 
                : 'bg-green-50/50 border-green-200'
            }`}>
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <span className="mr-2">💾</span> Backup & Export Dati
              </h3>
              <p className={`text-sm mb-6 ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Scarica tutti i tuoi dati e ricevi promemoria settimanali per i backup
              </p>
              
              {/* Export Button */}
              <div className="mb-6">
                <button
                  onClick={exportAllData}
                  disabled={isExporting}
                  className={`w-full px-6 py-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:from-green-600 hover:to-emerald-600 transition-all font-medium shadow-lg hover:shadow-xl flex items-center justify-center gap-3 ${isExporting ? 'opacity-75 cursor-wait' : ''}`}
                >
                  {isExporting ? (
                    <>
                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Creazione backup in corso...
                    </>
                  ) : (
                    <>
                      <span className="text-xl">📥</span>
                      Esporta Tutti i Dati
                    </>
                  )}
                </button>
              </div>

              {/* What's included */}
              <div className={`p-4 rounded-lg mb-6 ${
                getEffectiveTheme() === 'dark' ? 'bg-gray-700' : 'bg-white'
              }`}>
                <h4 className="font-medium mb-3 flex items-center">
                  <span className="mr-2">📦</span>
                  Cosa viene salvato
                </h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className={`flex items-center ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    <span className="mr-2 text-green-500">✓</span> Partite
                  </div>
                  <div className={`flex items-center ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    <span className="mr-2 text-green-500">✓</span> Squadre Museum
                  </div>
                  <div className={`flex items-center ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    <span className="mr-2 text-green-500">✓</span> Tornei
                  </div>
                  <div className={`flex items-center ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    <span className="mr-2 text-green-500">✓</span> Eventi Calendario
                  </div>
                  <div className={`flex items-center ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    <span className="mr-2 text-green-500">✓</span> Trofei
                  </div>
                  <div className={`flex items-center ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    <span className="mr-2 text-green-500">✓</span> Sfide
                  </div>
                  <div className={`flex items-center ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    <span className="mr-2 text-green-500">✓</span> Teams & Players
                  </div>
                  <div className={`flex items-center ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    <span className="mr-2 text-green-500">✓</span> Impostazioni
                  </div>
                </div>
              </div>

              {/* Weekly Reminder Toggle */}
              <div className="border-t border-green-200 pt-6">
                <h4 className="font-medium mb-4 flex items-center">
                  <span className="mr-2">📧</span>
                  Promemoria Backup Settimanale
                </h4>
                
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="backup_weekly_reminder"
                    checked={backupReminder.weekly_reminder}
                    onChange={(e) => {
                      const newPrefs = { weekly_reminder: e.target.checked };
                      setBackupReminder(newPrefs);
                      saveBackupReminder(newPrefs);
                    }}
                    className="mt-1 w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                  />
                  <label htmlFor="backup_weekly_reminder" className="cursor-pointer flex-1">
                    <div className="font-medium flex items-center">
                      Ricevi email promemoria
                      <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Ogni Lunedì</span>
                    </div>
                    <div className={`text-sm ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      Ogni lunedì riceverai un promemoria per effettuare il backup dei tuoi dati
                    </div>
                  </label>
                </div>

                {/* Info about reminder */}
                <div className={`mt-4 p-3 rounded-lg text-sm ${
                  getEffectiveTheme() === 'dark' ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-800'
                }`}>
                  <strong>💡 Suggerimento:</strong> Effettua un backup almeno una volta a settimana per avere sempre i tuoi dati al sicuro!
                </div>
              </div>
            </div>


            {/* Info App Pro */}
            <div className={`p-6 rounded-lg shadow border-2 border-dashed ${
              getEffectiveTheme() === 'dark' ? 'bg-gray-800/50 border-gray-600' : 'bg-gray-50 border-gray-300'
            }`}>
              <div className="text-center">
                <div className="text-3xl mb-3">⚽</div>
                <h3 className="font-bold text-lg mb-2">Subbuteo Statistics Pro 2.0</h3>
                <p className={`text-sm mb-4 ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  L'app definitiva per statistiche Subbuteo con AI e personalizzazione avanzata
                </p>
                <div className="flex justify-center space-x-2 text-xs flex-wrap gap-2">
                  <span className={`px-2 py-1 rounded ${getEffectiveTheme() === 'dark' ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-800'}`}>
                    Settings Pro
                  </span>
                  <span className={`px-2 py-1 rounded ${getEffectiveTheme() === 'dark' ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-800'}`}>
                    Smart Analytics
                  </span>
                  <span className={`px-2 py-1 rounded ${getEffectiveTheme() === 'dark' ? 'bg-purple-900/30 text-purple-300' : 'bg-purple-100 text-purple-800'}`}>
                    Achievement System
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* General Stats Tab */}
        {activeTab === 'stats' && generalStats && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Statistiche Generali</h2>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={exportAdvancedReport}
                  className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors text-sm font-medium"
                >
                  📄 Esporta PDF
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
              <div className={`p-4 sm:p-6 rounded-lg shadow ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
                <div className="text-2xl sm:text-3xl font-bold text-blue-600">{generalStats.total_matches}</div>
                <div className={`text-sm sm:text-base ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>Partite Totali</div>
              </div>
              
              <div className={`p-4 sm:p-6 rounded-lg shadow ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
                <div className="text-2xl sm:text-3xl font-bold text-green-600">{generalStats.wins}</div>
                <div className={`text-sm sm:text-base ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>Vittorie</div>
                <div className={`text-xs sm:text-sm ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{generalStats.win_percentage}%</div>
              </div>
              
              <div className={`p-4 sm:p-6 rounded-lg shadow ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
                <div className="text-2xl sm:text-3xl font-bold text-yellow-600">{generalStats.draws}</div>
                <div className={`text-sm sm:text-base ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>Pareggi</div>
                <div className={`text-xs sm:text-sm ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{generalStats.draw_percentage}%</div>
              </div>
              
              <div className={`p-4 sm:p-6 rounded-lg shadow ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
                <div className="text-2xl sm:text-3xl font-bold text-red-600">{generalStats.losses}</div>
                <div className={`text-sm sm:text-base ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>Sconfitte</div>
                <div className={`text-xs sm:text-sm ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{generalStats.loss_percentage}%</div>
              </div>
              
              <div className={`p-4 sm:p-6 rounded-lg shadow ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
                <div className="text-2xl sm:text-3xl font-bold text-purple-600">{generalStats.goals_scored}</div>
                <div className={`text-sm sm:text-base ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>Goal Fatti</div>
                <div className={`text-xs sm:text-sm ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                  {generalStats.total_matches > 0 ? (generalStats.goals_scored / generalStats.total_matches).toFixed(1) : 0} per partita
                </div>
              </div>
              
              <div className={`p-4 sm:p-6 rounded-lg shadow ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
                <div className="text-2xl sm:text-3xl font-bold text-orange-600">{generalStats.goals_conceded}</div>
                <div className={`text-sm sm:text-base ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>Goal Subiti</div>
                <div className={`text-xs sm:text-sm ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                  {generalStats.total_matches > 0 ? (generalStats.goals_conceded / generalStats.total_matches).toFixed(1) : 0} per partita
                </div>
              </div>
              
              <div className={`p-4 sm:p-6 rounded-lg shadow ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
                <div className="text-2xl sm:text-3xl font-bold text-indigo-600">
                  {generalStats.goals_scored - generalStats.goals_conceded > 0 ? '+' : ''}{generalStats.goals_scored - generalStats.goals_conceded}
                </div>
                <div className={`text-sm sm:text-base ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>Differenza Reti</div>
              </div>
              
              <div className={`p-4 sm:p-6 rounded-lg shadow ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
                <div className="text-2xl sm:text-3xl font-bold text-teal-600">
                  {generalStats.total_matches > 0 ? ((generalStats.wins * 3 + generalStats.draws) / (generalStats.total_matches * 3) * 100).toFixed(1) : 0}%
                </div>
                <div className={`text-sm sm:text-base ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>Punti %</div>
                <div className={`text-xs sm:text-sm ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>(3 pt vittoria, 1 pareggio)</div>
              </div>
            </div>
          </div>
        )}

        {/* Charts Tab */}
        {activeTab === 'charts' && (
          <div className="space-y-6">
            <h2 className="text-xl sm:text-2xl font-bold">📈 Grafici e Analisi</h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Results Distribution */}
              {generalStats && generalStats.total_matches > 0 && (
                <div className={`p-4 sm:p-6 rounded-lg shadow ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
                  <h3 className="text-lg font-semibold mb-4">Distribuzione Risultati</h3>
                  <div className="h-64">
                    {safeChartRender(Doughnut, getResultsDistributionData(), {
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          position: 'bottom',
                        },
                      },
                    }, 'results-distribution')}
                  </div>
                </div>
              )}

              {/* Monthly Trend */}
              {monthlyStats.length > 0 && (
                <div className={`p-4 sm:p-6 rounded-lg shadow ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
                  <h3 className="text-lg font-semibold mb-4">Trend Mensile</h3>
                  <div className="h-64">
                    {safeChartRender(Line, getMonthlyTrendData(), {
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                        },
                      },
                    }, 'monthly-trend')}
                  </div>
                </div>
              )}

              {/* Goals Trend */}
              {monthlyStats.length > 0 && (
                <div className={`p-4 sm:p-6 rounded-lg shadow ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
                  <h3 className="text-lg font-semibold mb-4">Trend Goal</h3>
                  <div className="h-64">
                    <Line 
                      data={{
                        labels: monthlyStats.sort((a, b) => a.month_key.localeCompare(b.month_key)).map(stat => stat.month),
                        datasets: [
                          {
                            label: 'Goal Fatti',
                            data: monthlyStats.sort((a, b) => a.month_key.localeCompare(b.month_key)).map(stat => stat.goals_for),
                            borderColor: 'rgb(59, 130, 246)',
                            backgroundColor: 'rgba(59, 130, 246, 0.2)',
                            tension: 0.4,
                          },
                          {
                            label: 'Goal Subiti',
                            data: monthlyStats.sort((a, b) => a.month_key.localeCompare(b.month_key)).map(stat => stat.goals_against),
                            borderColor: 'rgb(239, 68, 68)',
                            backgroundColor: 'rgba(239, 68, 68, 0.2)',
                            tension: 0.4,
                          }
                        ]
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: {
                            beginAtZero: true,
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Opponent Stats Chart */}
              {opponentStats.length > 0 && (
                <div className={`p-4 sm:p-6 rounded-lg shadow ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
                  <h3 className="text-lg font-semibold mb-4">Performance vs Avversari</h3>
                  <div className="h-64">
                    <Bar 
                      data={{
                        labels: opponentStats.slice(0, 8).map(stat => stat.opponent),
                        datasets: [
                          {
                            label: 'Vittorie',
                            data: opponentStats.slice(0, 8).map(stat => stat.wins),
                            backgroundColor: 'rgba(34, 197, 94, 0.8)',
                          },
                          {
                            label: 'Pareggi',
                            data: opponentStats.slice(0, 8).map(stat => stat.draws),
                            backgroundColor: 'rgba(234, 179, 8, 0.8)',
                          },
                          {
                            label: 'Sconfitte',
                            data: opponentStats.slice(0, 8).map(stat => stat.losses),
                            backgroundColor: 'rgba(239, 68, 68, 0.8)',
                          }
                        ]
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          x: {
                            stacked: true,
                          },
                          y: {
                            stacked: true,
                            beginAtZero: true,
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {(monthlyStats.length === 0 && opponentStats.length === 0) && (
              <div className={`p-8 rounded-lg shadow text-center ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
                <div className="text-gray-400 text-6xl mb-4">📊</div>
                <h3 className="text-lg font-semibold mb-2">Nessun dato per i grafici</h3>
                <p className={getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Usa il tab "Aggiungi" per registrare partite e vedere i grafici delle tue statistiche!</p>
              </div>
            )}
          </div>
        )}

        {/* Opponent Stats Tab */}
        {activeTab === 'opponents' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <h2 className="text-xl sm:text-2xl font-bold">👥 Statistiche vs Avversari</h2>
              <button
                onClick={() => {
                  const opponentData = opponentStats.map(stat => ({
                    Avversario: stat.opponent,
                    Partite: stat.matches,
                    Vittorie: stat.wins,
                    Pareggi: stat.draws,
                    Sconfitte: stat.losses,
                    'Goal Fatti': stat.goals_for,
                    'Goal Subiti': stat.goals_against,
                    'Percentuale Vittorie': stat.win_percentage
                  }));
                  exportToCSV(opponentData, 'statistiche-avversari.csv');
                }}
                className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors text-sm font-medium"
              >
                📊 Esporta CSV
              </button>
            </div>
            
            <div className={`rounded-lg shadow overflow-hidden ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className={getEffectiveTheme() === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}>
                    <tr>
                      <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                        getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-500'
                      }`}>
                        Avversario
                      </th>
                      <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                        getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-500'
                      }`}>
                        Partite
                      </th>
                      <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                        getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-500'
                      }`}>
                        V-P-S
                      </th>
                      <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                        getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-500'
                      }`}>
                        Goal F/S
                      </th>
                      <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                        getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-500'
                      }`}>
                        % Vittorie
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${getEffectiveTheme() === 'dark' ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'}`}>
                    {opponentStats.map((stat, index) => (
                      <tr key={index} className={`${getEffectiveTheme() === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}>
                        <td className={`px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium`}>
                          <button
                            onClick={() => openOpponentModal(stat.opponent)}
                            className={`hover:underline cursor-pointer transition-colors ${
                              getEffectiveTheme() === 'dark' 
                                ? 'text-blue-400 hover:text-blue-300' 
                                : 'text-blue-600 hover:text-blue-800'
                            }`}
                          >
                            {stat.opponent}
                          </button>
                        </td>
                        <td className={`px-3 sm:px-6 py-4 whitespace-nowrap text-sm ${
                          getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-500'
                        }`}>
                          {stat.matches}
                        </td>
                        <td className={`px-3 sm:px-6 py-4 whitespace-nowrap text-sm ${
                          getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-500'
                        }`}>
                          <span className="text-green-600">{stat.wins}</span>-
                          <span className="text-yellow-600">{stat.draws}</span>-
                          <span className="text-red-600">{stat.losses}</span>
                        </td>
                        <td className={`px-3 sm:px-6 py-4 whitespace-nowrap text-sm ${
                          getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-500'
                        }`}>
                          {stat.goals_for}/{stat.goals_against}
                        </td>
                        <td className={`px-3 sm:px-6 py-4 whitespace-nowrap text-sm ${
                          getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-500'
                        }`}>
                          <span className={`px-2 py-1 rounded text-xs ${
                            stat.win_percentage >= 60 ? 'bg-green-100 text-green-800' :
                            stat.win_percentage >= 40 ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {stat.win_percentage}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* 👥 OPPONENT MATCHES MODAL */}
        {showOpponentModal && selectedOpponent && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`w-full max-w-2xl max-h-[90vh] rounded-lg shadow-xl ${
              getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'
            }`}>
              {/* Header */}
              <div className={`px-6 py-4 border-b flex justify-between items-center ${
                getEffectiveTheme() === 'dark' ? 'border-gray-700' : 'border-gray-200'
              }`}>
                <div>
                  <h3 className={`text-xl font-bold ${
                    getEffectiveTheme() === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    ⚽ Scontri vs {selectedOpponent.name}
                  </h3>
                  <p className={`text-sm mt-1 ${
                    getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    📊 Bilancio: <span className="text-green-600">{selectedOpponent.wins}V</span> - <span className="text-yellow-600">{selectedOpponent.draws}P</span> - <span className="text-red-600">{selectedOpponent.losses}S</span> ({selectedOpponent.totalMatches} partite)
                  </p>
                </div>
                <button
                  onClick={() => setShowOpponentModal(false)}
                  className={`text-gray-500 hover:text-gray-700 text-xl font-bold ${
                    getEffectiveTheme() === 'dark' ? 'hover:text-gray-300' : ''
                  }`}
                >
                  ✕
                </button>
              </div>

              {/* Content - Scrollable */}
              <div className="max-h-[60vh] overflow-y-auto px-6 py-4">
                {opponentMatches.length > 0 ? (
                  <div className="space-y-3">
                    {opponentMatches.map((match, index) => {
                      // Considera i piazzati per determinare il risultato
                      let isWin, isDraw, isLoss;
                      if (match.has_piazzati && match.piazzati_miei !== null && match.piazzati_avversario !== null) {
                        isWin = match.piazzati_miei > match.piazzati_avversario;
                        isLoss = match.piazzati_miei < match.piazzati_avversario;
                        isDraw = match.piazzati_miei === match.piazzati_avversario;
                      } else {
                        isWin = match.my_goals > match.opponent_goals;
                        isDraw = match.my_goals === match.opponent_goals;
                        isLoss = match.my_goals < match.opponent_goals;
                      }
                      
                      return (
                        <div key={match.id} className={`p-4 rounded-lg border ${
                          getEffectiveTheme() === 'dark' 
                            ? 'bg-gray-700/50 border-gray-600' 
                            : 'bg-gray-50 border-gray-200'
                        }`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                getEffectiveTheme() === 'dark' ? 'bg-gray-600 text-white' : 'bg-gray-200 text-gray-700'
                              }`}>
                                {index + 1}
                              </div>
                              <div>
                                <div className={`font-medium text-sm ${
                                  getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-600'
                                }`}>
                                  📅 {new Date(match.match_date).toLocaleDateString('it-IT')}
                                  {match.tournament && match.tournament !== 'Amichevole' && (
                                    <span className="ml-2">🏆 {match.tournament}</span>
                                  )}
                                  {(!match.tournament || match.tournament === 'Amichevole') && (
                                    <span className="ml-2">⚽ Amichevole</span>
                                  )}
                                </div>
                                <div className={`text-lg font-bold mt-1 ${
                                  getEffectiveTheme() === 'dark' ? 'text-white' : 'text-gray-900'
                                }`}>
                                  {match.my_team} {match.my_goals}-{match.opponent_goals} {match.opponent_team}
                                  {match.has_piazzati && (
                                    <span className="text-sm font-normal ml-2 text-gray-500">
                                      (🎯 {match.piazzati_miei}-{match.piazzati_avversario})
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className={`px-3 py-1 rounded-full text-sm font-bold ${
                              isWin ? 'bg-green-100 text-green-800' :
                              isDraw ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {isWin ? '🏅 Vittoria' : isDraw ? '🤝 Pareggio' : '❌ Sconfitta'}
                              {match.has_piazzati && ' 🎯'}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="text-4xl mb-4">⚽</div>
                    <p className={`${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      Nessuna partita trovata contro questo avversario
                    </p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className={`px-6 py-4 border-t flex justify-end ${
                getEffectiveTheme() === 'dark' ? 'border-gray-700' : 'border-gray-200'
              }`}>
                <button
                  onClick={() => setShowOpponentModal(false)}
                  className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Chiudi
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 🏆 TOURNAMENTS TAB - Museum Style Layout */}

        {/* 📅 CALENDAR TAB - NEW REDESIGNED */}
        {activeTab === 'calendar' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="relative">
              <div className={`
                backdrop-blur-lg border border-opacity-30 rounded-2xl p-6
                ${getEffectiveTheme() === 'dark' 
                  ? 'bg-gray-900/60 border-gray-700/30' 
                  : 'bg-white/80 border-gray-200/30'
                } shadow-lg
              `}>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-pink-600 rounded-xl flex items-center justify-center shadow-lg">
                      <span className="text-2xl">📅</span>
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold bg-gradient-to-r from-orange-600 to-pink-600 bg-clip-text text-transparent">
                        Calendario Eventi
                      </h2>
                      <p className={`text-sm ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        {calendarEvents.length} eventi salvati
                      </p>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => setShowAddEventModal(true)}
                    className="px-4 py-2 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-lg hover:from-orange-600 hover:to-pink-600 transition-all duration-200 shadow-lg hover:shadow-xl font-medium flex items-center space-x-2"
                  >
                    <span>➕</span>
                    <span>Nuovo Evento</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Filter Tabs */}
            <div className="flex gap-2 overflow-x-auto pb-2">
              {['all', 'upcoming', 'past'].map(filter => (
                <button
                  key={filter}
                  onClick={() => setCalendarFilter(filter)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                    calendarFilter === filter
                      ? 'bg-gradient-to-r from-orange-100 to-pink-100 text-orange-700 shadow-md'
                      : getEffectiveTheme() === 'dark'
                        ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                        : 'bg-white text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {filter === 'all' && '📋 Tutti'}
                  {filter === 'upcoming' && '🔜 Prossimi'}
                  {filter === 'past' && '✅ Passati'}
                </button>
              ))}
            </div>

            {/* Events Cards Grid - Grouped by Month */}
            {calendarEvents.length === 0 ? (
              <div className={`text-center py-16 rounded-lg border-2 border-dashed ${
                getEffectiveTheme() === 'dark' 
                  ? 'border-gray-700 text-gray-400' 
                  : 'border-gray-300 text-gray-500'
              }`}>
                <p className="text-6xl mb-4">📅</p>
                <p className="text-lg font-medium mb-2">Nessun evento in calendario</p>
                <p className="text-sm mb-4">Aggiungi il tuo primo evento per iniziare</p>
                <button
                  onClick={() => setShowAddEventModal(true)}
                  className="px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-lg hover:from-orange-600 hover:to-pink-600 transition-all font-medium"
                >
                  ➕ Crea Primo Evento
                </button>
              </div>
            ) : (
              <div className="space-y-8">
                {(() => {
                  // Filter events based on calendarFilter
                  const filteredEvents = calendarEvents.filter(event => {
                    const eventDate = new Date(event.date);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    
                    if (calendarFilter === 'upcoming') return eventDate >= today;
                    if (calendarFilter === 'past') return eventDate < today;
                    return true;
                  });

                  // Sort events by date (ascending - closest first)
                  const sortedEvents = [...filteredEvents].sort((a, b) => new Date(a.date) - new Date(b.date));

                  // Group events by month
                  const eventsByMonth = sortedEvents.reduce((groups, event) => {
                    const eventDate = new Date(event.date);
                    const monthKey = `${eventDate.getFullYear()}-${String(eventDate.getMonth() + 1).padStart(2, '0')}`;
                    const monthLabel = eventDate.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
                    
                    if (!groups[monthKey]) {
                      groups[monthKey] = { label: monthLabel, events: [] };
                    }
                    groups[monthKey].events.push(event);
                    return groups;
                  }, {});

                  // Sort months chronologically
                  const sortedMonths = Object.keys(eventsByMonth).sort();

                  // Pastel colors for cards
                  const colors = [
                    { bg: 'bg-blue-50/50', border: 'border-blue-200', text: 'text-blue-600', badge: 'bg-blue-100 text-blue-700' },
                    { bg: 'bg-green-50/50', border: 'border-green-200', text: 'text-green-600', badge: 'bg-green-100 text-green-700' },
                    { bg: 'bg-purple-50/50', border: 'border-purple-200', text: 'text-purple-600', badge: 'bg-purple-100 text-purple-700' },
                    { bg: 'bg-pink-50/50', border: 'border-pink-200', text: 'text-pink-600', badge: 'bg-pink-100 text-pink-700' },
                    { bg: 'bg-orange-50/50', border: 'border-orange-200', text: 'text-orange-600', badge: 'bg-orange-100 text-orange-700' },
                  ];

                  let globalIndex = 0;

                  return sortedMonths.map(monthKey => {
                    const monthData = eventsByMonth[monthKey];
                    
                    return (
                      <div key={monthKey} className="space-y-4">
                        {/* Month Header */}
                        <div className={`flex items-center gap-3 pb-2 border-b-2 ${
                          getEffectiveTheme() === 'dark' ? 'border-gray-700' : 'border-gray-200'
                        }`}>
                          <span className="text-2xl">📅</span>
                          <h3 className={`text-xl font-bold capitalize ${
                            getEffectiveTheme() === 'dark' ? 'text-white' : 'text-gray-800'
                          }`}>
                            {monthData.label}
                          </h3>
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            getEffectiveTheme() === 'dark' 
                              ? 'bg-gray-700 text-gray-300' 
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {monthData.events.length} {monthData.events.length === 1 ? 'evento' : 'eventi'}
                          </span>
                        </div>

                        {/* Events Grid for this Month */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {monthData.events.map((event) => {
                            const eventDate = new Date(event.date);
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            const isPast = eventDate < today;
                            const isToday = eventDate.toDateString() === today.toDateString();
                            
                            const colorScheme = isPast 
                              ? { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-600', badge: 'bg-gray-100 text-gray-600' }
                              : colors[globalIndex % colors.length];
                            
                            const currentIndex = globalIndex;
                            globalIndex++;
                            
                            return (
                              <div
                                key={event.id}
                                className={`p-5 rounded-lg shadow-md border-2 ${colorScheme.bg} ${colorScheme.border} hover:shadow-lg transition-all duration-300 hover:scale-105 animate-fade-in-up`}
                                style={{animationDelay: `${currentIndex * 0.05}s`}}
                              >
                                {/* Badge Status */}
                                <div className="flex justify-between items-start mb-3">
                                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${colorScheme.badge}`}>
                                    {isToday ? '🔴 OGGI' : isPast ? '✅ PASSATO' : '🔜 PROSSIMO'}
                                  </span>
                                  <button
                                    onClick={() => deleteCalendarEvent(event.id)}
                                    className="text-gray-400 hover:text-red-600 transition-colors"
                                  >
                                    🗑️
                                  </button>
                                </div>

                                {/* Date & Time */}
                                <div className="mb-3">
                                  <p className={`text-sm font-medium ${colorScheme.text}`}>
                                    🗓️ {new Date(event.date).toLocaleDateString('it-IT', { 
                                      weekday: 'short', 
                                      day: 'numeric', 
                                      month: 'long'
                                    })}
                                  </p>
                                  {event.time && (
                                    <p className={`text-sm ${colorScheme.text}`}>
                                      🕐 {event.time}
                                    </p>
                                  )}
                                </div>

                                {/* Event Name */}
                                <h3 className={`text-lg font-bold ${colorScheme.text} mb-2`}>
                                  {event.name}
                                </h3>

                                {/* Category */}
                                <div className="flex items-center gap-2 mb-2">
                                  {event.category === 'training' && <span className="text-sm">🏃 Allenamento</span>}
                                  {event.category === 'club_tournament' && <span className="text-sm">🏆 Torneo Club</span>}
                                  {event.category === 'national_tournament' && <span className="text-sm">🥇 Torneo Nazionale</span>}
                                </div>

                                {/* Description */}
                                {event.description && (
                                  <p className={`text-sm ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'} line-clamp-2`}>
                                    {event.description}
                                  </p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            )}
          </div>
        )}

        {activeTab === 'tournaments' && (
          <div className="space-y-6">
            {/* Modern Header with Glassmorphism Effect - Same as Museum */}
            <div className="relative">
              <div className={`
                backdrop-blur-lg border border-opacity-30 rounded-2xl p-6
                ${getEffectiveTheme() === 'dark' 
                  ? 'bg-gray-900/60 border-gray-700/30' 
                  : 'bg-white/80 border-gray-200/30'
                } shadow-lg
              `}>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                      <span className="text-2xl">🏆</span>
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
                        Tornei
                      </h2>
                      <p className={`text-sm ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        {tournamentsCards.length} tornei nella collezione
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {tournamentsCards.length > 0 && (
                      <div className={`text-sm px-4 py-2 rounded-lg ${
                        getEffectiveTheme() === 'dark' ? 'bg-gray-700/50 text-gray-300' : 'bg-gray-100/50 text-gray-600'
                      }`}>
                        Clicca per vedere partite • Hover per modificare locandina
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Loading State */}
            {loadingTournamentsCards && (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
                <span className="ml-3">Caricamento tornei...</span>
              </div>
            )}

            {/* Modern Horizontal List Layout - Same as Museum */}
            {!loadingTournamentsCards && tournamentsCards.length > 0 && (() => {
              // Separate active and archived tournaments
              const activeTournaments = tournamentsCards.filter(t => t.status === 'active');
              const archivedTournaments = tournamentsCards.filter(t => t.status === 'completed');
              
              // Limit BOTH active and archived tournaments
              const visibleActive = activeTournaments.slice(0, visibleActiveCount);
              const hasMoreActive = activeTournaments.length > visibleActiveCount;
              
              const visibleArchived = archivedTournaments.slice(0, visibleArchivedCount);
              const hasMoreArchived = archivedTournaments.length > visibleArchivedCount;
              
              // Combine: limited active + limited archived
              const visibleTournaments = [...visibleActive, ...visibleArchived];
              
              return (
                <>
                <div className="space-y-3">
                  {visibleTournaments.map((tournament, index) => {
                    // Check if we need to insert separator (between active and completed)
                    const prevTournament = index > 0 ? visibleTournaments[index - 1] : null;
                    const needsSeparator = prevTournament && 
                                          prevTournament.status === 'active' && 
                                          tournament.status === 'completed';
                    
                    // Check if we need "Load More Active" button (after last active, before separator)
                    const isLastActive = tournament.status === 'active' && 
                                        index < visibleTournaments.length - 1 &&
                                        visibleTournaments[index + 1].status === 'completed';
                    
                    return (
                      <React.Fragment key={tournament.name || index}>
                        {/* Load More Active Tournaments Button */}
                        {hasMoreActive && isLastActive && (
                          <div className="flex justify-center py-4">
                            <button
                              onClick={() => setVisibleActiveCount(prev => prev + 10)}
                              className={`group flex items-center space-x-3 px-6 py-3 rounded-xl transition-all duration-200 ${
                                getEffectiveTheme() === 'dark'
                                  ? 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600 text-white'
                                  : 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white'
                              } shadow-md hover:shadow-lg`}
                            >
                              <span className="text-xl">🏆</span>
                              <div className="text-left">
                                <div className="font-semibold text-sm">Carica Altri Tornei Attivi</div>
                                <div className="text-xs opacity-90">
                                  {activeTournaments.length - visibleActiveCount} rimanenti
                                </div>
                              </div>
                              <span className="text-lg group-hover:translate-x-1 transition-transform">→</span>
                            </button>
                          </div>
                        )}
                        
                        {/* Separator between Active and Completed tournaments */}
                        {needsSeparator && (
                          <div className={`flex items-center gap-4 my-6 py-4 ${
                            getEffectiveTheme() === 'dark' ? 'text-gray-500' : 'text-gray-400'
                          }`}>
                            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
                            <div className="flex items-center space-x-2 px-4 py-2 rounded-full bg-gradient-to-r from-gray-100 to-gray-200">
                              <span className="text-xl">📦</span>
                              <span className="font-semibold text-sm text-gray-700">Tornei Archiviati ({archivedTournaments.length})</span>
                            </div>
                            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
                          </div>
                        )}
                      
                      <div
                        className={`group relative rounded-xl overflow-hidden transition-all duration-200 hover:shadow-lg ${
                          getEffectiveTheme() === 'dark'
                            ? 'bg-gradient-to-r from-gray-800/90 to-gray-900/90 backdrop-blur-sm border border-gray-700/30 hover:bg-gray-800'
                            : 'bg-gradient-to-r from-white/90 to-gray-50/90 backdrop-blur-sm border border-gray-200/30 hover:bg-gray-50'
                        } shadow-sm`}
                      >
                    {/* Desktop Layout (md and up): Single Row Horizontal */}
                    <div className="hidden md:flex items-center p-4">
                      
                      {/* Tournament Image - 100x100 Size (Same as Museum) */}
                      <div className="relative flex-shrink-0 mr-6">
                        <div className="w-25 h-25 rounded-xl overflow-hidden border-2 border-gray-200/50 shadow-sm" 
                             style={{ width: '100px', height: '100px' }}>
                          {tournament.poster_url ? (
                            <LazyTournamentImage
                              src={tournament.poster_thumbnail || tournament.poster_url}
                              alt={tournament.name}
                              className="w-full h-full"
                              tournamentName={tournament.name}
                            />
                          ) : (
                            <div className={`w-full h-full flex items-center justify-center bg-gradient-to-br from-green-500 to-blue-600`}>
                              <span className="text-3xl text-white">🏆</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Status Badge - Desktop */}
                        <div className="absolute -top-1 -right-1">
                          <div className={`px-2 py-1 rounded-lg text-xs font-bold shadow-sm ${
                            tournament.status === 'completed' 
                              ? 'bg-gray-500 text-white' 
                              : 'bg-green-500 text-white'
                          }`}>
                            {tournament.status === 'completed' ? 'TERMINATO' : 'ATTIVO'}
                          </div>
                        </div>
                      </div>

                      {/* Nome Torneo - Desktop */}
                      <div className="flex-1 min-w-0 mr-4">
                        <h3 className={`font-bold text-xl leading-tight truncate mb-1 ${
                          getEffectiveTheme() === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>
                          {tournament.name}
                        </h3>
                        
                        <div className={`flex items-center space-x-1 text-sm ${
                          getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                          <span className="text-sm">📅</span>
                          <span className="font-medium">Torneo Attivo</span>
                        </div>
                      </div>

                      {/* Statistiche V-P-S - Desktop (Same as Museum) */}
                      <div className="flex-shrink-0 mr-6">
                        <div className={`text-center ${
                          getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          <div className="text-xs font-medium mb-1 opacity-75">Statistiche</div>
                          <div className="flex space-x-3 text-sm font-bold">
                            <div className="text-center">
                              <div className="text-green-600">{tournament.wins || '-'}</div>
                              <div className="text-xs opacity-75">V</div>
                            </div>
                            <div className="text-center">
                              <div className="text-yellow-600">{tournament.draws || '-'}</div>
                              <div className="text-xs opacity-75">P</div>
                            </div>
                            <div className="text-center">
                              <div className="text-red-600">{tournament.losses || '-'}</div>
                              <div className="text-xs opacity-75">S</div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Partite Totali - Desktop (Same as Museum) */}
                      <div className="flex-shrink-0 mr-6">
                        <div className={`text-center px-4 py-2 rounded-lg ${
                          getEffectiveTheme() === 'dark' ? 'bg-blue-600/20 text-blue-400' : 'bg-blue-100 text-blue-700'
                        }`}>
                          <div className="text-lg font-bold leading-none">
                            {tournament.total_matches || '0'}
                          </div>
                          <div className="text-xs mt-1 opacity-75">partite</div>
                        </div>
                      </div>

                      {/* Percentuale Vittorie - Desktop (Same as Museum) */}
                      <div className="flex-shrink-0 mr-4">
                        {(tournament.win_percentage !== undefined && tournament.win_percentage !== null) ? (
                          <div className={`px-4 py-2 rounded-lg font-bold text-center min-w-[90px] ${
                            tournament.win_percentage >= 70 ? 'bg-green-500 text-white' :
                            tournament.win_percentage >= 50 ? 'bg-yellow-500 text-white' :
                            'bg-red-500 text-white'
                          }`}>
                            <div className="text-xl leading-none">{Math.round(tournament.win_percentage)}%</div>
                            <div className="text-xs mt-1 opacity-90">vittorie</div>
                          </div>
                        ) : (
                          <div className={`px-4 py-2 rounded-lg font-bold text-center min-w-[90px] ${
                            getEffectiveTheme() === 'dark' ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'
                          }`}>
                            <div className="text-xl leading-none">-%</div>
                            <div className="text-xs mt-1 opacity-75">vittorie</div>
                          </div>
                        )}
                      </div>

                      {/* Pulsante Vedi Partite - Desktop */}
                      <div className="flex-shrink-0 mr-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            loadTournamentModalMatches(tournament.name);
                          }}
                          className="group/btn bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-3 py-2 rounded-lg transition-all duration-200 hover:shadow-lg hover:scale-105 font-medium text-sm flex items-center space-x-1"
                        >
                          <span className="group-hover/btn:rotate-12 transition-transform duration-200">👁️</span>
                          <span>Vedi Partite</span>
                        </button>
                      </div>

                      {/* Pulsante Modifica Locandina - Desktop */}
                      <div className="flex-shrink-0">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            console.log('🎯 DEBUG: Edit button clicked for:', tournament.name);
                            editTournamentImage(tournament);
                          }}
                          className="group/btn bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-3 py-2 rounded-lg transition-all duration-200 hover:shadow-lg hover:scale-105 font-medium text-sm flex items-center space-x-1"
                        >
                          <span className="group-hover/btn:rotate-12 transition-transform duration-200">📝</span>
                          <span>Modifica</span>
                        </button>
                      </div>
                    </div>

                    {/* Mobile Layout (below md): Two Row Layout - Same as Museum */}
                    <div className="md:hidden p-4">
                      {/* Row 1: Image + Name + Category Badge */}
                      <div className="flex items-center mb-3">
                        {/* Image - Smaller on mobile */}
                        <div className="relative flex-shrink-0 mr-3">
                          <div className="w-16 h-16 rounded-lg overflow-hidden border border-gray-200/50 shadow-sm">
                            {tournament.poster_url ? (
                              <LazyTournamentImage
                                src={tournament.poster_thumbnail || tournament.poster_url}
                                alt={tournament.name}
                                className="w-full h-full"
                                tournamentName={tournament.name}
                              />
                            ) : (
                              <div className={`w-full h-full flex items-center justify-center bg-gradient-to-br from-green-500 to-blue-600`}>
                                <span className="text-xl text-white">🏆</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Name */}
                        <div className="flex-1 min-w-0">
                          <h3 className={`font-bold text-lg leading-tight truncate ${
                            getEffectiveTheme() === 'dark' ? 'text-white' : 'text-gray-900'
                          }`}>
                            {tournament.name}
                          </h3>
                          <div className={`flex items-center space-x-2 text-sm ${
                            getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'
                          }`}>
                            <span>📅</span>
                            <span className={`px-2 py-1 rounded text-xs font-bold ${
                              tournament.status === 'completed' 
                                ? 'bg-gray-500 text-white' 
                                : 'bg-green-500 text-white'
                            }`}>
                              {tournament.status === 'completed' ? 'TERMINATO' : 'ATTIVO'}
                            </span>
                          </div>
                        </div>

                        {/* Status Badge - Mobile */}
                        <div className="flex-shrink-0">
                          <div className={`px-2 py-1 rounded-lg text-xs font-bold ${
                            tournament.status === 'completed' 
                              ? 'bg-gray-500 text-white' 
                              : 'bg-green-500 text-white'
                          }`}>
                            {tournament.status === 'completed' ? 'TERMINATO' : 'ATTIVO'}
                          </div>
                        </div>
                      </div>

                      {/* Row 2: Statistics + Actions (Mobile) */}
                      <div className="flex items-center justify-between">
                        {/* Stats - Mobile */}
                        <div className="flex space-x-4">
                          <div className="text-center">
                            <div className={`text-lg font-bold ${getEffectiveTheme() === 'dark' ? 'text-blue-400' : 'text-blue-600'}`}>
                              {tournament.total_matches}
                            </div>
                            <div className={`text-xs ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                              partite
                            </div>
                          </div>
                          <div className="text-center">
                            <div className={`text-lg font-bold ${
                              tournament.win_percentage >= 70 ? 'text-green-500' :
                              tournament.win_percentage >= 50 ? 'text-yellow-500' :
                              'text-red-500'
                            }`}>
                              {Math.round(tournament.win_percentage || 0)}%
                            </div>
                            <div className={`text-xs ${getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                              vittorie
                            </div>
                          </div>
                        </div>

                        {/* Action Buttons - Mobile */}
                        <div className="flex space-x-2">
                          <button
                            onClick={() => loadTournamentModalMatches(tournament.name)}
                            className="bg-gradient-to-r from-green-500 to-green-600 text-white px-3 py-2 rounded-lg text-xs font-medium"
                          >
                            👁️
                          </button>
                          <button
                            onClick={() => editTournamentImage(tournament)}
                            className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-3 py-2 rounded-lg text-xs font-medium"
                          >
                            📝
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}
              </div>
              
              {/* Load More Archived Tournaments Button - at the end */}
              {hasMoreArchived && (
                <div className="flex justify-center pt-6 pb-2">
                  <button
                    onClick={() => setVisibleArchivedCount(prev => prev + 10)}
                    className={`group flex items-center space-x-3 px-6 py-3 rounded-xl transition-all duration-200 ${
                      getEffectiveTheme() === 'dark'
                        ? 'bg-gradient-to-r from-gray-700 to-gray-800 hover:from-gray-600 hover:to-gray-700 text-gray-300'
                        : 'bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 text-gray-700'
                    } shadow-md hover:shadow-lg`}
                  >
                    <span className="text-xl">📦</span>
                    <div className="text-left">
                      <div className="font-semibold text-sm">Carica Altri Tornei Archiviati</div>
                      <div className="text-xs opacity-75">
                        {archivedTournaments.length - visibleArchivedCount} rimanenti
                      </div>
                    </div>
                    <span className="text-lg group-hover:translate-x-1 transition-transform">→</span>
                  </button>
                </div>
              )}
            </>
            );
          })()}

            {/* Empty State */}
            {!loadingTournamentsCards && tournamentsCards.length === 0 && (
              <div className={`p-8 rounded-lg shadow text-center ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
                <div className="text-gray-400 text-6xl mb-4">🏆</div>
                <h3 className="text-lg font-semibold mb-2">Nessun torneo trovato</h3>
                <p className={getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                  Aggiungi partite con il nome del torneo per vederle qui raggruppate!
                </p>
              </div>
            )}

            {/* Tournament Details Modal */}
            {selectedTournamentModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className={`max-w-4xl w-full max-h-[90vh] overflow-y-auto rounded-lg shadow-xl ${
                  getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'
                }`}>
                  {/* Modal Header */}
                  <div className={`p-6 border-b ${
                    getEffectiveTheme() === 'dark' ? 'border-gray-700' : 'border-gray-200'
                  }`}>
                    <div className="flex justify-between items-center">
                      <h3 className="text-lg font-bold">🏆 {selectedTournamentModal}</h3>
                      <button
                        onClick={() => setSelectedTournamentModal(null)}
                        className={`text-gray-400 hover:text-gray-600 ${
                          getEffectiveTheme() === 'dark' ? 'hover:text-gray-300' : ''
                        }`}
                      >
                        ✕
                      </button>
                    </div>
                  </div>

                  {/* Modal Content */}
                  <div className="p-6">
                    <h4 className="font-semibold mb-4">📝 Partite del Torneo ({tournamentModalMatches.length})</h4>
                    <div className="space-y-3">
                      {tournamentModalMatches.map((match, index) => (
                        <div key={index} className={`p-3 rounded border ${
                          getEffectiveTheme() === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                        }`}>
                          <div className="flex justify-between items-center">
                            <div className="font-medium">
                              #{match.match_number}: {match.my_team} vs {match.opponent_team}
                            </div>
                            <div className="text-sm">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                (match.has_piazzati && match.piazzati_miei !== null && match.piazzati_avversario !== null)
                                  ? (match.piazzati_miei > match.piazzati_avversario ? 'bg-green-100 text-green-800' :
                                     match.piazzati_miei < match.piazzati_avversario ? 'bg-red-100 text-red-800' :
                                     'bg-yellow-100 text-yellow-800')
                                  : (match.my_goals > match.opponent_goals ? 'bg-green-100 text-green-800' :
                                     match.my_goals === match.opponent_goals ? 'bg-yellow-100 text-yellow-800' :
                                     'bg-red-100 text-red-800')
                              }`}>
                                {match.my_goals}-{match.opponent_goals}
                                {match.has_piazzati && ` 🎯${match.piazzati_miei}-${match.piazzati_avversario}`}
                              </span>
                            </div>
                          </div>
                          <div className={`text-xs mt-1 ${
                            getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            {formatDate(match.match_date)} vs {match.opponent_name}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Tournament Image Upload Modal */}
            {showTournamentForm && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className={`max-w-md w-full max-h-[90vh] rounded-lg shadow-xl flex flex-col ${
                  getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'
                }`}>
                  {/* Modal Header - Fixed */}
                  <div className={`flex-shrink-0 p-6 border-b ${
                    getEffectiveTheme() === 'dark' ? 'border-gray-700' : 'border-gray-200'
                  }`}>
                    <div className="flex justify-between items-center">
                      <h3 className="text-lg font-bold">
                        📸 {editingTournament?.name ? `Modifica ${editingTournament.name}` : 'Aggiungi Locandina Torneo'}
                      </h3>
                      <button
                        onClick={() => setShowTournamentForm(false)}
                        className={`text-gray-400 hover:text-gray-600 ${
                          getEffectiveTheme() === 'dark' ? 'hover:text-gray-300' : ''
                        }`}
                      >
                        ✕
                      </button>
                    </div>
                  </div>

                  {/* Modal Content - Scrollable */}
                  <div className="flex-1 overflow-y-auto p-6">
                    {/* Tournament Name Input (if creating new) */}
                    {!editingTournament?.name && (
                      <div className="mb-4">
                        <label className={`block text-sm font-medium mb-2 ${
                          getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          Nome Torneo
                        </label>
                        <input
                          type="text"
                          value={editingTournament?.name || ''}
                          onChange={(e) => setEditingTournament(prev => ({...prev, name: e.target.value}))}
                          className={`w-full px-3 py-2 border rounded-lg ${
                            getEffectiveTheme() === 'dark' 
                              ? 'bg-gray-700 border-gray-600 text-white' 
                              : 'bg-white border-gray-300'
                          }`}
                          placeholder="es. Champions League 2024"
                        />
                      </div>
                    )}

                    {/* Current Image Preview */}
                    {editingTournament?.poster_url && (
                      <div className="mb-4">
                        <label className={`block text-sm font-medium mb-2 ${
                          getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          Locandina Attuale
                        </label>
                        <img 
                          src={editingTournament.poster_url} 
                          alt="Current poster"
                          className="w-full h-32 object-cover rounded-lg border"
                        />
                      </div>
                    )}

                    {/* Image Upload */}
                    <div className="mb-4">
                      <label className={`block text-sm font-medium mb-2 ${
                        getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        {editingTournament?.poster_url ? 'Nuova Locandina' : 'Carica Locandina'}
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (file && editingTournament?.name) {
                            // Show compression progress
                            showToastNotification('🖼️ Comprimendo immagine...', 'info');
                            await uploadTournamentImage(file, editingTournament.name);
                          } else if (file) {
                            showToastNotification('Inserisci prima il nome del torneo', 'warning');
                          }
                        }}
                        disabled={uploadingTournamentImage}
                        className={`w-full px-3 py-2 border rounded-lg ${
                          getEffectiveTheme() === 'dark' 
                            ? 'bg-gray-700 border-gray-600 text-white' 
                            : 'bg-white border-gray-300'
                        }`}
                      />
                      {uploadingTournamentImage && (
                        <div className="mt-2 text-sm text-blue-600 flex items-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                          Caricamento...
                        </div>
                      )}
                    </div>

                    {/* Description */}
                    <div className="mb-4">
                      <label className={`block text-sm font-medium mb-2 ${
                        getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        Descrizione (opzionale)
                      </label>
                      <textarea
                        value={editingTournament?.description || ''}
                        onChange={(e) => setEditingTournament(prev => ({...prev, description: e.target.value}))}
                        className={`w-full px-3 py-2 border rounded-lg ${
                          getEffectiveTheme() === 'dark' 
                            ? 'bg-gray-700 border-gray-600 text-white' 
                            : 'bg-white border-gray-300'
                        }`}
                        rows="3"
                        placeholder="Descrizione del torneo..."
                      />
                    </div>

                    {/* 🏛️ SQUADRA MUSEUM - Associate Tournament to Museum Team */}
                    <div className="mb-4">
                      <label className={`block text-sm font-medium mb-2 ${
                        getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        🏛️ Associa a Squadra Museum (opzionale)
                      </label>
                      <select
                        value={editingTournament?.museum_team_id || ''}
                        onChange={(e) => setEditingTournament(prev => ({...prev, museum_team_id: e.target.value}))}
                        className={`w-full px-3 py-2 border rounded-lg ${
                          getEffectiveTheme() === 'dark' 
                            ? 'bg-gray-700 border-gray-600 text-white' 
                            : 'bg-white border-gray-300'
                        }`}
                      >
                        <option value="">-- Nessuna associazione --</option>
                        {museumTeams.map(team => (
                          <option key={team.id} value={team.id}>
                            {team.name} ({team.total_matches || 0} partite)
                          </option>
                        ))}
                      </select>
                      <p className={`text-xs mt-1 ${
                        getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        Associa tutte le partite di questo torneo a una squadra del Museum per aggiornare le statistiche.
                      </p>
                    </div>

                    {/* 🏁 STATO TORNEO - Active/Completed Status */}
                    <div className="mb-4">
                      <label className={`block text-sm font-medium mb-2 ${
                        getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        🏁 Stato Torneo
                      </label>
                      <div className="flex space-x-4">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="tournament-status"
                            value="active"
                            checked={(editingTournament?.status || 'active') === 'active'}
                            onChange={(e) => setEditingTournament(prev => ({...prev, status: e.target.value}))}
                            className="mr-2 text-green-600 focus:ring-green-500"
                          />
                          <span className={`text-sm ${
                            getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            Attivo
                          </span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="tournament-status"
                            value="completed"
                            checked={editingTournament?.status === 'completed'}
                            onChange={(e) => setEditingTournament(prev => ({...prev, status: e.target.value}))}
                            className="mr-2 text-gray-600 focus:ring-gray-500"
                          />
                          <span className={`text-sm ${
                            getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            Terminato
                          </span>
                        </label>
                      </div>
                    </div>

                  </div>

                  {/* Modal Footer - Fixed */}
                  <div className={`flex-shrink-0 p-6 border-t ${
                    getEffectiveTheme() === 'dark' ? 'border-gray-700' : 'border-gray-200'
                  }`}>
                    {/* Action Buttons */}
                    <div className="flex gap-3">
                      <button
                        onClick={() => setShowTournamentForm(false)}
                        className={`flex-1 px-4 py-2 rounded-lg border ${
                          getEffectiveTheme() === 'dark' 
                            ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
                            : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        Annulla
                      </button>
                      <button
                        onClick={async () => {
                          // Save tournament metadata (description, status, etc.)
                          if (editingTournament?.name) {
                            try {
                              const metadata = {
                                name: editingTournament.name,
                                description: editingTournament.description || '',
                                status: editingTournament.status || 'active',
                                poster_url: editingTournament.poster_url || null,
                                poster_thumbnail: editingTournament.poster_thumbnail || null
                              };
                              
                              const response = await makeAuthenticatedRequest(
                                `${BACKEND_URL}/api/tournaments/metadata`,
                                {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify(metadata)
                                }
                              );
                              
                              if (response && response.ok) {
                                showToastNotification('✅ Modifiche salvate', 'success');
                                
                                // Reset visible counts to show fresh data
                                setVisibleActiveCount(10);
                                setVisibleArchivedCount(1); // Only 1 archived initially
                                
                                // Reload tournaments to refresh the list
                                await loadTournamentsCards();
                              }
                            } catch (error) {
                              console.error('Error saving tournament metadata:', error);
                              showToastNotification('Errore nel salvare lo stato torneo', 'error');
                            }
                          }
                          
                          // Save museum team association if selected
                          if (editingTournament?.museum_team_id && editingTournament?.name) {
                            await associateTournamentToMuseumTeam(
                              editingTournament.name, 
                              editingTournament.museum_team_id
                            );
                          }
                          
                          setShowTournamentForm(false);
                        }}
                        disabled={uploadingTournamentImage}
                        className={`flex-1 px-4 py-2 rounded-lg text-white ${
                          uploadingTournamentImage 
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-green-600 hover:bg-green-700'
                        }`}
                      >
                        {editingTournament?.museum_team_id ? '💾 Salva e Chiudi' : 'Chiudi'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
          </div>
        )}

        {/* Monthly Stats Tab - Pastel Card Grid Design */}
        {activeTab === 'monthly' && (
          <div className="space-y-6">
            <h2 className="text-xl sm:text-2xl font-bold">📅 Statistiche Mensili</h2>
            
            {/* Responsive Grid of Monthly Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {monthlyStats.map((stat, index) => {
                // Cycle through pastel colors for variety
                const pastelColors = [
                  { bg: 'bg-blue-50/50', border: 'border-blue-200', text: 'text-blue-600', hover: 'hover:shadow-blue-200/50', darkBorder: 'border-blue-400/30', darkText: 'text-blue-400' },
                  { bg: 'bg-green-50/50', border: 'border-green-200', text: 'text-green-600', hover: 'hover:shadow-green-200/50', darkBorder: 'border-green-400/30', darkText: 'text-green-400' },
                  { bg: 'bg-purple-50/50', border: 'border-purple-200', text: 'text-purple-600', hover: 'hover:shadow-purple-200/50', darkBorder: 'border-purple-400/30', darkText: 'text-purple-400' },
                  { bg: 'bg-pink-50/50', border: 'border-pink-200', text: 'text-pink-600', hover: 'hover:shadow-pink-200/50', darkBorder: 'border-pink-400/30', darkText: 'text-pink-400' },
                  { bg: 'bg-orange-50/50', border: 'border-orange-200', text: 'text-orange-600', hover: 'hover:shadow-orange-200/50', darkBorder: 'border-orange-400/30', darkText: 'text-orange-400' },
                  { bg: 'bg-cyan-50/50', border: 'border-cyan-200', text: 'text-cyan-600', hover: 'hover:shadow-cyan-200/50', darkBorder: 'border-cyan-400/30', darkText: 'text-cyan-400' }
                ];
                const colorScheme = pastelColors[index % pastelColors.length];
                
                return (
                  <div 
                    key={index}
                    className={`p-5 rounded-lg shadow-md border-2 transition-all duration-300 hover:scale-105 ${
                      getEffectiveTheme() === 'dark'
                        ? `bg-gray-800 ${colorScheme.darkBorder}`
                        : `${colorScheme.bg} ${colorScheme.border} ${colorScheme.hover}`
                    }`}
                  >
                    {/* Month Header */}
                    <div className="mb-4 pb-3 border-b border-gray-200/50">
                      <h3 className={`text-lg font-bold ${
                        getEffectiveTheme() === 'dark' ? colorScheme.darkText : colorScheme.text
                      }`}>
                        {stat.month}
                      </h3>
                    </div>
                    
                    {/* Stats Grid */}
                    <div className="space-y-3">
                      {/* Matches Count */}
                      <div className="flex justify-between items-center">
                        <span className={`text-sm font-medium ${
                          getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          ⚽ Partite
                        </span>
                        <span className={`text-lg font-bold ${
                          getEffectiveTheme() === 'dark' ? colorScheme.darkText : colorScheme.text
                        }`}>
                          {stat.matches}
                        </span>
                      </div>
                      
                      {/* Win-Draw-Loss */}
                      <div className="flex justify-between items-center">
                        <span className={`text-sm font-medium ${
                          getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          📊 V-P-S
                        </span>
                        <div className="flex gap-1 text-sm font-semibold">
                          <span className="text-green-600">{stat.wins}</span>
                          <span className={getEffectiveTheme() === 'dark' ? 'text-gray-500' : 'text-gray-400'}>-</span>
                          <span className="text-yellow-600">{stat.draws}</span>
                          <span className={getEffectiveTheme() === 'dark' ? 'text-gray-500' : 'text-gray-400'}>-</span>
                          <span className="text-red-600">{stat.losses}</span>
                        </div>
                      </div>
                      
                      {/* Goals For/Against */}
                      <div className="flex justify-between items-center">
                        <span className={`text-sm font-medium ${
                          getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          🎯 Goal F/S
                        </span>
                        <span className={`text-sm font-bold ${
                          getEffectiveTheme() === 'dark' ? 'text-gray-200' : 'text-gray-700'
                        }`}>
                          {stat.goals_for} / {stat.goals_against}
                        </span>
                      </div>
                      
                      {/* Win Percentage Badge */}
                      <div className="pt-2 mt-2 border-t border-gray-200/50">
                        <div className="flex justify-between items-center">
                          <span className={`text-sm font-medium ${
                            getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            🏆 % Vittorie
                          </span>
                          <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                            stat.win_percentage >= 60 
                              ? 'bg-green-100 text-green-800' 
                              : stat.win_percentage >= 40 
                              ? 'bg-yellow-100 text-yellow-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {stat.win_percentage}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Empty State */}
            {monthlyStats.length === 0 && (
              <div className={`text-center py-12 rounded-lg border-2 border-dashed ${
                getEffectiveTheme() === 'dark' 
                  ? 'border-gray-700 text-gray-400' 
                  : 'border-gray-300 text-gray-500'
              }`}>
                <p className="text-lg">📅 Nessuna statistica mensile disponibile</p>
                <p className="text-sm mt-2">Aggiungi partite per vedere le statistiche per mese</p>
              </div>
            )}
          </div>
        )}

        {/* All Matches Tab - Performance Optimized */}
        {activeTab === 'matches' && (() => {
          // 🚀 Calculate theme ONCE per render instead of 100+ times
          const isDark = getEffectiveTheme() === 'dark';
          const containerClass = isDark ? 'bg-gray-800' : 'bg-white';
          const tableHeadClass = isDark ? 'bg-gray-700' : 'bg-gray-50';
          const headerTextClass = isDark ? 'text-gray-300' : 'text-gray-500';
          const tableBodyClass = isDark ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200';
          const cellTextClass = isDark ? 'text-gray-300' : 'text-gray-500';
          const primaryTextClass = isDark ? 'text-white' : 'text-gray-900';
          const secondaryTextClass = isDark ? 'text-gray-400' : 'text-gray-500';
          const rowHoverClass = isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50';
          const emptyTextClass = isDark ? 'text-gray-400' : 'text-gray-600';

          return (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <h2 className="text-xl sm:text-2xl font-bold">📝 Tutte le Partite</h2>
                <button
                  onClick={exportMatchesToCSV}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  📊 Esporta CSV
                </button>
              </div>
              
              <div className={`rounded-lg shadow overflow-hidden ${containerClass}`}>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className={tableHeadClass}>
                    <tr>
                      <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${headerTextClass}`}>
                        Data
                      </th>
                      <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${headerTextClass}`}>
                        Partita
                      </th>
                      <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${headerTextClass}`}>
                        Risultato
                      </th>
                      <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${headerTextClass}`}>
                        Esito
                      </th>
                      <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${headerTextClass}`}>
                        Torneo
                      </th>
                      <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${headerTextClass}`}>
                        Azioni
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${tableBodyClass}`}>
                    {matches.map((match, index) => (
                      <tr key={index} className={rowHoverClass}>
                        <td className={`px-3 sm:px-6 py-4 whitespace-nowrap text-sm ${cellTextClass}`}>
                          {formatDate(match.match_date)}
                        </td>
                        <td className={`px-3 sm:px-6 py-4 text-sm ${primaryTextClass}`}>
                          <div className="font-medium">{match.my_team} vs {match.opponent_team}</div>
                          <div className={`text-xs ${secondaryTextClass}`}>vs {match.opponent_name}</div>
                        </td>
                        <td className={`px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium ${primaryTextClass}`}>
                          <span className="text-lg flex items-center gap-1">
                            {match.my_goals} - {match.opponent_goals}
                            {match.has_piazzati && (
                              <span className="text-yellow-500 text-sm">
                                ({match.piazzati_miei}-{match.piazzati_avversario} p)
                              </span>
                            )}
                          </span>
                        </td>
                        <td className={`px-3 sm:px-6 py-4 whitespace-nowrap text-sm`}>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getResultColor(match.my_goals, match.opponent_goals, match)}`}>
                            {getMatchResult(match.my_goals, match.opponent_goals, match)}
                          </span>
                        </td>
                        <td className={`px-3 sm:px-6 py-4 whitespace-nowrap text-sm ${cellTextClass}`}>
                          {match.tournament || 'Amichevole'}
                        </td>
                        <td className={`px-3 sm:px-6 py-4 whitespace-nowrap text-sm ${cellTextClass}`}>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => editMatch(match.id)}
                              className="text-blue-600 hover:text-blue-900 font-medium"
                            >
                              ✏️ Modifica
                            </button>
                            <button
                              onClick={() => deleteMatch(match.id)}
                              className="text-red-600 hover:text-red-900 font-medium"
                            >
                              🗑️ Elimina
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {matches.length === 0 && (
              <div className={`p-8 rounded-lg shadow text-center ${containerClass}`}>
                <div className="text-gray-400 text-6xl mb-4">⚽</div>
                <h3 className="text-lg font-semibold mb-2">Nessuna partita registrata</h3>
                <p className={emptyTextClass}>Usa il tab "Aggiungi" per registrare le tue partite e vedere le statistiche!</p>
              </div>
            )}
          </div>
          );
        })()}

        {/* STAGIONI TAB 🍂 - Advanced Feature 1 */}
        {activeTab === 'seasons' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold">🍂 Analisi Stagioni</h2>
                <p className={getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                  Confronta le tue performance nelle diverse stagioni
                </p>
              </div>
              {currentSeason && (
                <div className={`px-4 py-2 rounded-lg ${getEffectiveTheme() === 'dark' ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-800'}`}>
                  Stagione Corrente: {currentSeason}
                </div>
              )}
            </div>

            {/* Season Comparison Section - Solo se ci sono almeno 2 stagioni con partite */}
            {seasons.length >= 2 && (
            <div className={`p-6 rounded-lg shadow ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
              <h3 className="text-lg font-semibold mb-4">🔄 Confronta Stagioni</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${getEffectiveTheme() === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
                    Prima Stagione
                  </label>
                  <select
                    value={selectedSeasonComparison.season1}
                    onChange={(e) => setSelectedSeasonComparison({...selectedSeasonComparison, season1: e.target.value})}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                      getEffectiveTheme() === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                    }`}
                  >
                    <option value="">Seleziona stagione...</option>
                    {seasons.map(season => (
                      <option key={season.name} value={season.name}>{season.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${getEffectiveTheme() === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}>
                    Seconda Stagione
                  </label>
                  <select
                    value={selectedSeasonComparison.season2}
                    onChange={(e) => setSelectedSeasonComparison({...selectedSeasonComparison, season2: e.target.value})}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                      getEffectiveTheme() === 'dark' ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                    }`}
                  >
                    <option value="">Seleziona stagione...</option>
                    {seasons.map(season => (
                      <option key={season.name} value={season.name}>{season.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <button
                onClick={() => compareSeasons(selectedSeasonComparison.season1, selectedSeasonComparison.season2)}
                disabled={!selectedSeasonComparison.season1 || !selectedSeasonComparison.season2}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                🔍 Confronta
              </button>

              {/* Comparison Results */}
              {seasonComparison && (
                <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className={`p-4 rounded-lg border-2 ${getEffectiveTheme() === 'dark' ? 'border-blue-600 bg-blue-900/20' : 'border-blue-300 bg-blue-50'}`}>
                    <h4 className="font-bold text-lg mb-2">{seasonComparison.season1.name}</h4>
                    <div className="space-y-1 text-sm">
                      <div>📊 {seasonComparison.season1.matches} partite</div>
                      <div className={getAccentColor(600)}>🏆 {seasonComparison.season1.win_percentage}% vittorie</div>
                      <div>⚽ {seasonComparison.season1.goals_for} goal fatti</div>
                      <div>🛡️ {seasonComparison.season1.goals_against} goal subiti</div>
                      <div>📈 Forma: {seasonComparison.season1.form}</div>
                    </div>
                  </div>
                  <div className={`p-4 rounded-lg border-2 ${getEffectiveTheme() === 'dark' ? 'border-purple-600 bg-purple-900/20' : 'border-purple-300 bg-purple-50'}`}>
                    <h4 className="font-bold text-lg mb-2">{seasonComparison.season2.name}</h4>
                    <div className="space-y-1 text-sm">
                      <div>📊 {seasonComparison.season2.matches} partite</div>
                      <div className={getAccentColor(600)}>🏆 {seasonComparison.season2.win_percentage}% vittorie</div>
                      <div>⚽ {seasonComparison.season2.goals_for} goal fatti</div>
                      <div>🛡️ {seasonComparison.season2.goals_against} goal subiti</div>
                      <div>📈 Forma: {seasonComparison.season2.form}</div>
                    </div>
                  </div>
                  
                  {/* Improvements/Declines */}
                  {(seasonComparison.improvements.length > 0 || seasonComparison.declines.length > 0) && (
                    <div className="lg:col-span-2 mt-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {seasonComparison.improvements.length > 0 && (
                          <div className="p-4 rounded-lg bg-green-100 dark:bg-green-900/20">
                            <h5 className="font-semibold text-green-800 dark:text-green-300 mb-2">📈 Miglioramenti</h5>
                            <ul className="space-y-1">
                              {seasonComparison.improvements.map((improvement, i) => (
                                <li key={i} className="text-sm text-green-700 dark:text-green-400">• {improvement}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {seasonComparison.declines.length > 0 && (
                          <div className="p-4 rounded-lg bg-red-100 dark:bg-red-900/20">
                            <h5 className="font-semibold text-red-800 dark:text-red-300 mb-2">📉 Peggioramenti</h5>
                            <ul className="space-y-1">
                              {seasonComparison.declines.map((decline, i) => (
                                <li key={i} className="text-sm text-red-700 dark:text-red-400">• {decline}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            )}

            {/* All Seasons Overview - Solo se ci sono stagioni con partite */}
            {seasons.length > 0 && (
              <div className={`p-6 rounded-lg shadow ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
                <h3 className="text-lg font-semibold mb-4">📋 Tutte le Stagioni</h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                {seasons.map((season, index) => (
                  <div key={season.name} className={`p-4 rounded-lg border ${
                    season.name === currentSeason 
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/20' 
                      : getEffectiveTheme() === 'dark' ? 'border-gray-700' : 'border-gray-200'
                  }`}>
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-bold">{season.name}</h4>
                      {season.name === currentSeason && (
                        <span className="text-xs px-2 py-1 bg-green-500 text-white rounded">CORRENTE</span>
                      )}
                    </div>
                    <div className="space-y-1 text-sm">
                      <div>📊 {season.matches} partite</div>
                      <div className={`font-medium ${getAccentColor(600)}`}>🏆 {season.win_percentage}% vittorie</div>
                      <div className="text-gray-500">⚽ {season.goals_for} goal • 🛡️ {season.goals_against} subiti</div>
                      <div className={`text-xs px-2 py-1 rounded ${
                        season.form === 'Ottima' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                        season.form === 'Buona' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' :
                        'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                      }`}>
                        Forma: {season.form}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              </div>
            )}

            {seasons.length === 0 && (
              <div className={`p-8 rounded-lg shadow text-center ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
                <div className="text-gray-400 text-6xl mb-4">🍂</div>
                <h3 className="text-lg font-semibold mb-2">Nessuna stagione trovata</h3>
                <p className={getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Gioca alcune partite per vedere le analisi stagionali!</p>
              </div>
            )}
          </div>
        )}

        {/* TRAINING OBJECTIVES TAB 🎯 */}
        {activeTab === 'challenges' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold">🎯 Obiettivi di Allenamento</h2>
                <p className={getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                  Completa gli obiettivi giornalieri per migliorare le tue prestazioni!
                </p>
              </div>
              <button
                onClick={loadDailyChallenges}
                disabled={loadingStates.challenges}
                className={`px-4 py-2 rounded-md transition-colors font-medium ${
                  loadingStates.challenges 
                    ? 'bg-gray-400 text-white cursor-not-allowed' 
                    : 'bg-purple-600 text-white hover:bg-purple-700'
                }`}
              >
                {loadingStates.challenges ? '⏳ Caricamento...' : '🔄 Aggiorna Obiettivi'}
              </button>
            </div>

            {/* Daily Challenges Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {dailyChallenges.map((challenge, index) => (
                <div key={challenge.id} className={`p-6 rounded-lg shadow border-l-4 ${
                  challenge.completed 
                    ? 'border-green-500 bg-green-50 dark:bg-green-900/20' 
                    : 'border-purple-500 bg-white dark:bg-gray-800'
                }`}>
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="font-bold text-lg">{challenge.name}</h3>
                    <div className={`text-xs px-2 py-1 rounded ${
                      challenge.completed 
                        ? 'bg-green-500 text-white' 
                        : 'bg-purple-500 text-white'
                    }`}>
                      {challenge.reward_xp || challenge.reward_points || 0} XP
                    </div>
                  </div>
                  
                  <p className={`text-sm mb-4 ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                    {challenge.description}
                  </p>
                  
                  {/* Progress Bar */}
                  <div className="mb-3">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium">Progresso</span>
                      <span className="text-sm">{challenge.progress || challenge.current_progress || 0}/{challenge.target_value || challenge.max_progress || 0}</span>
                    </div>
                    <div className={`w-full rounded-full h-2 ${getEffectiveTheme() === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`}>
                      <div 
                        className={`h-2 rounded-full transition-all duration-500 ${
                          challenge.completed ? 'bg-green-500' : 'bg-purple-500'
                        }`}
                        style={{ width: `${Math.min(((challenge.progress || challenge.current_progress || 0) / (challenge.target_value || challenge.max_progress || 1)) * 100, 100)}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Status */}
                  <div className="flex justify-between items-center">
                    <span className={`text-sm ${
                      challenge.completed ? 'text-green-600 dark:text-green-400' : 'text-purple-600 dark:text-purple-400'
                    }`}>
                      {challenge.completed ? '✅ Completata!' : '⏱️ In corso'}
                    </span>
                    <span className="text-xs text-gray-500">
                      Scade: {(() => {
                        try {
                          const dateStr = challenge.end_date || challenge.expires_at;
                          if (!dateStr) return 'Fine giornata';
                          
                          const date = new Date(dateStr);
                          if (isNaN(date.getTime())) {
                            return 'Fine giornata';
                          }
                          return date.toLocaleString('it-IT', { 
                            day: '2-digit',
                            month: '2-digit', 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          });
                        } catch (error) {
                          return 'Fine giornata';
                        }
                      })()}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {dailyChallenges.length === 0 && (
              <div className={`p-8 rounded-lg shadow text-center ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
                <div className="text-gray-400 text-6xl mb-4">🎯</div>
                <h3 className="text-lg font-semibold mb-2">Nessun obiettivo disponibile</h3>
                <p className={getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                  Gli obiettivi di allenamento vengono generati automaticamente. Clicca "Aggiorna Obiettivi" per visualizzarli!
                </p>
                <button
                  onClick={loadDailyChallenges}
                  className="mt-4 bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors font-medium"
                >
                  🎯 Genera Obiettivi del Giorno
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit Match Modal ✏️ - Performance Optimized */}
      {showEditForm && editingMatch && (() => {
        // 🚀 Calculate theme ONCE for entire modal
        const isDark = getEffectiveTheme() === 'dark';
        const modalBg = isDark ? 'bg-gray-800' : 'bg-white';
        const inputBg = isDark ? 'bg-gray-700' : 'bg-white';
        const inputBorder = isDark ? 'border-gray-600' : 'border-gray-300';
        const inputText = isDark ? 'text-white' : 'text-gray-900';
        const labelText = isDark ? 'text-gray-300' : 'text-gray-700';
        const closeButtonHover = isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100';

        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg shadow-lg ${modalBg}`}>
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">✏️ Modifica Partita</h3>
                <button
                  onClick={() => {
                    setShowEditForm(false);
                    setEditingMatch(null);
                    // Reset form
                    setFormData({
                      my_team: '',
                      opponent_team: '',
                      opponent_name: '',
                      my_goals: '',
                      opponent_goals: '',
                      match_date: new Date().toISOString().split('T')[0],
                      tournament: '',
                      my_museum_team_id: '',
                      // PIAZZATI
                      has_piazzati: false,
                      piazzati_miei: '',
                      piazzati_avversario: ''
                    });
                  }}
                  className={`text-gray-400 hover:text-gray-600 text-2xl ${closeButtonHover}`}
                >
                  ×
                </button>
              </div>

              <form onSubmit={updateMatch} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* La Mia Squadra */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${labelText}`}>
                      La Mia Squadra
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.my_team}
                      onChange={(e) => setFormData({...formData, my_team: e.target.value})}
                      list="teams-list"
                      className={`w-full px-3 py-2 border rounded-md ${inputBg} ${inputBorder} ${inputText}`}
                      placeholder="Nome squadra..."
                    />
                  </div>

                  {/* Squadra Avversaria */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${labelText}`}>
                      Squadra Avversaria
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.opponent_team}
                      onChange={(e) => setFormData({...formData, opponent_team: e.target.value})}
                      list="teams-list"
                      className={`w-full px-3 py-2 border rounded-md ${
                        getEffectiveTheme() === 'dark' 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300'
                      }`}
                      placeholder="Nome squadra avversaria..."
                    />
                  </div>

                  {/* Nome Avversario */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      getEffectiveTheme() === 'dark' ? 'text-gray-200' : 'text-gray-700'
                    }`}>
                      Nome Avversario
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.opponent_name}
                      onChange={(e) => setFormData({...formData, opponent_name: e.target.value})}
                      list="players-list"
                      className={`w-full px-3 py-2 border rounded-md ${
                        getEffectiveTheme() === 'dark' 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300'
                      }`}
                      placeholder="Nome giocatore..."
                    />
                  </div>

                  {/* Data Partita */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      getEffectiveTheme() === 'dark' ? 'text-gray-200' : 'text-gray-700'
                    }`}>
                      Data
                    </label>
                    <input
                      type="date"
                      required
                      value={formData.match_date}
                      onChange={(e) => setFormData({...formData, match_date: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-md ${
                        getEffectiveTheme() === 'dark' 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300'
                      }`}
                    />
                  </div>

                  {/* I Miei Goal */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      getEffectiveTheme() === 'dark' ? 'text-gray-200' : 'text-gray-700'
                    }`}>
                      I Miei Goal
                    </label>
                    <input
                      type="number"
                      min="0"
                      required
                      value={formData.my_goals}
                      onChange={(e) => setFormData({...formData, my_goals: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-md ${
                        getEffectiveTheme() === 'dark' 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300'
                      }`}
                      placeholder="0"
                    />
                  </div>

                  {/* Goal Avversario */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      getEffectiveTheme() === 'dark' ? 'text-gray-200' : 'text-gray-700'
                    }`}>
                      Goal Avversario
                    </label>
                    <input
                      type="number"
                      min="0"
                      required
                      value={formData.opponent_goals}
                      onChange={(e) => setFormData({...formData, opponent_goals: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-md ${
                        getEffectiveTheme() === 'dark' 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300'
                      }`}
                      placeholder="0"
                    />
                  </div>
                </div>

                {/* Torneo */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    getEffectiveTheme() === 'dark' ? 'text-gray-200' : 'text-gray-700'
                  }`}>
                    Torneo (opzionale)
                  </label>
                  <input
                    type="text"
                    value={formData.tournament}
                    onChange={(e) => setFormData({...formData, tournament: e.target.value})}
                    list="tournaments-list"
                    className={`w-full px-3 py-2 border rounded-md ${
                      getEffectiveTheme() === 'dark' 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300'
                    }`}
                    placeholder="Nome torneo..."
                  />
                </div>

                {/* 🏛️ SQUADRA MUSEUM - NEW DROPDOWN FOR ASSOCIATION */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    getEffectiveTheme() === 'dark' ? 'text-gray-200' : 'text-gray-700'
                  }`}>
                    🏛️ Associa a Squadra Museum (opzionale)
                  </label>
                  <select
                    value={formData.my_museum_team_id || ''}
                    onChange={(e) => setFormData({...formData, my_museum_team_id: e.target.value})}
                    className={`w-full px-3 py-2 border rounded-md ${
                      getEffectiveTheme() === 'dark' 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300'
                    }`}
                  >
                    <option value="">-- Nessuna associazione --</option>
                    {museumTeams.map(team => (
                      <option key={team.id} value={team.id}>
                        {team.name} ({team.total_matches || 0} partite)
                      </option>
                    ))}
                  </select>
                  {loadingMuseumTeams && (
                    <p className={`text-xs mt-1 ${
                      getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      Caricamento squadre Museum...
                    </p>
                  )}
                  <p className={`text-xs mt-1 ${
                    getEffectiveTheme() === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    Associa questa partita a una squadra del Museum per aggiornare automaticamente le statistiche.
                  </p>
                </div>

                {/* 🎯 PIAZZATI (Shootouts) Section */}
                <div className={`p-4 rounded-lg border ${
                  formData.has_piazzati 
                    ? (getEffectiveTheme() === 'dark' ? 'border-yellow-500 bg-yellow-900/20' : 'border-yellow-400 bg-yellow-50')
                    : (getEffectiveTheme() === 'dark' ? 'border-gray-600' : 'border-gray-200')
                }`}>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.has_piazzati}
                      onChange={(e) => setFormData({
                        ...formData, 
                        has_piazzati: e.target.checked,
                        piazzati_miei: e.target.checked ? formData.piazzati_miei : '',
                        piazzati_avversario: e.target.checked ? formData.piazzati_avversario : ''
                      })}
                      className="w-5 h-5 text-yellow-500 rounded focus:ring-yellow-500"
                    />
                    <span className={`ml-3 font-medium ${
                      getEffectiveTheme() === 'dark' ? 'text-gray-200' : 'text-gray-700'
                    }`}>
                      🎯 Partita decisa ai piazzati
                    </span>
                  </label>
                  
                  {formData.has_piazzati && (
                    <div className="mt-4">
                      <div className="flex items-center justify-center space-x-4">
                        <div className="text-center">
                          <label className={`block text-xs font-medium mb-1 ${
                            getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-600'
                          }`}>
                            I Miei Piazzati
                          </label>
                          <input
                            type="number"
                            min="0"
                            value={formData.piazzati_miei}
                            onChange={(e) => setFormData({...formData, piazzati_miei: e.target.value})}
                            className={`w-20 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 text-center text-lg font-bold ${
                              getEffectiveTheme() === 'dark' 
                                ? 'bg-gray-700 border-gray-600 text-white' 
                                : 'bg-white border-gray-300'
                            }`}
                            placeholder="0"
                          />
                        </div>
                        
                        <div className="flex items-end pb-2">
                          <span className="text-2xl font-bold text-yellow-500">-</span>
                        </div>
                        
                        <div className="text-center">
                          <label className={`block text-xs font-medium mb-1 ${
                            getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-600'
                          }`}>
                            Piazzati Avversario
                          </label>
                          <input
                            type="number"
                            min="0"
                            value={formData.piazzati_avversario}
                            onChange={(e) => setFormData({...formData, piazzati_avversario: e.target.value})}
                            className={`w-20 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 text-center text-lg font-bold ${
                              getEffectiveTheme() === 'dark' 
                                ? 'bg-gray-700 border-gray-600 text-white' 
                                : 'bg-white border-gray-300'
                            }`}
                            placeholder="0"
                          />
                        </div>
                      </div>
                      <p className={`text-xs text-center mt-2 ${
                        getEffectiveTheme() === 'dark' ? 'text-yellow-400' : 'text-yellow-600'
                      }`}>
                        Inserisci il risultato dei tiri piazzati
                      </p>
                    </div>
                  )}
                </div>

                {/* Buttons */}
                <div className="flex justify-end space-x-4 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditForm(false);
                      setEditingMatch(null);
                    }}
                    className={`px-4 py-2 border border-gray-300 rounded-md text-sm font-medium ${
                      getEffectiveTheme() === 'dark' 
                        ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Annulla
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                  >
                    {loading ? 'Salvando...' : 'Salva Modifiche'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        );
      })()}
      
      {/* 📝 AGGIUNGI PARTITA COMPLETA MODAL */}
      {showQuickAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-lg shadow-xl ${getEffectiveTheme() === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`p-6 border-b ${getEffectiveTheme() === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex justify-between items-center">
                <h3 className={`text-lg font-semibold ${getEffectiveTheme() === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  ➕ Aggiungi Nuova Partita
                </h3>
                <button
                  onClick={() => setShowQuickAddModal(false)}
                  className={`text-gray-400 hover:text-gray-600 ${getEffectiveTheme() === 'dark' ? 'hover:text-gray-300' : ''}`}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <form onSubmit={(e) => {
              e.preventDefault();
              handleSubmit(e);
              setShowQuickAddModal(false);
            }} className="p-6 space-y-6">
              
              {/* Quick Template Buttons */}
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setFormData({
                      ...formData,
                      my_goals: 2,
                      opponent_goals: 1
                    });
                  }}
                  className="p-3 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm font-medium"
                >
                  🏆 Vittoria 2-1
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setFormData({
                      ...formData,
                      my_goals: 1,
                      opponent_goals: 1
                    });
                  }}
                  className="p-3 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors text-sm font-medium"
                >
                  🤝 Pareggio 1-1
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setFormData({
                      ...formData,
                      my_goals: 1,
                      opponent_goals: 2
                    });
                  }}
                  className="p-3 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm font-medium"
                >
                  😞 Sconfitta 1-2
                </button>
              </div>

              {/* Squadre Section */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    🏟️ Mia Squadra
                  </label>
                  <input
                    type="text"
                    value={formData.my_team}
                    onChange={(e) => setFormData({...formData, my_team: e.target.value})}
                    list="teams-list"
                    className={`w-full px-3 py-2 border rounded-md ${
                      getEffectiveTheme() === 'dark' 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300'
                    }`}
                    placeholder="Nome squadra..."
                    required
                  />
                </div>
                
                <div>
                  <label className={`block text-sm font-medium mb-2 ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    🏟️ Squadra Avversaria
                  </label>
                  <input
                    type="text"
                    value={formData.opponent_team}
                    onChange={(e) => setFormData({...formData, opponent_team: e.target.value})}
                    list="teams-list"
                    className={`w-full px-3 py-2 border rounded-md ${
                      getEffectiveTheme() === 'dark' 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300'
                    }`}
                    placeholder="Nome squadra avversaria..."
                    required
                  />
                </div>
              </div>

              {/* Avversario */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  👤 Nome Avversario
                </label>
                <input
                  type="text"
                  value={formData.opponent_name}
                  onChange={(e) => setFormData({...formData, opponent_name: e.target.value})}
                  list="players-list"
                  className={`w-full px-3 py-2 border rounded-md ${
                    getEffectiveTheme() === 'dark' 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300'
                  }`}
                  placeholder="Nome avversario..."
                  required
                />
              </div>

              {/* Goal e Data */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    ⚽ Miei Goal
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="20"
                    value={formData.my_goals}
                    onChange={(e) => setFormData({...formData, my_goals: parseInt(e.target.value) || 0})}
                    className={`w-full px-3 py-2 text-center text-lg font-bold border rounded-md ${
                      getEffectiveTheme() === 'dark' 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300'
                    }`}
                    required
                  />
                </div>
                
                <div>
                  <label className={`block text-sm font-medium mb-2 ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    ⚽ Goal Avversario
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="20"
                    value={formData.opponent_goals}
                    onChange={(e) => setFormData({...formData, opponent_goals: parseInt(e.target.value) || 0})}
                    className={`w-full px-3 py-2 text-center text-lg font-bold border rounded-md ${
                      getEffectiveTheme() === 'dark' 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300'
                    }`}
                    required
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    📅 Data Partita
                  </label>
                  <input
                    type="date"
                    value={formData.match_date}
                    onChange={(e) => setFormData({...formData, match_date: e.target.value})}
                    className={`w-full px-3 py-2 border rounded-md ${
                      getEffectiveTheme() === 'dark' 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300'
                    }`}
                    required
                  />
                </div>
              </div>

              {/* Torneo */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  🏆 Torneo (opzionale)
                </label>
                <input
                  type="text"
                  value={formData.tournament}
                  onChange={(e) => setFormData({...formData, tournament: e.target.value})}
                  list="tournaments-list"
                  className={`w-full px-3 py-2 border rounded-md ${
                    getEffectiveTheme() === 'dark' 
                      ? 'bg-gray-700 border-gray-600 text-white' 
                      : 'bg-white border-gray-300'
                  }`}
                  placeholder="Nome torneo..."
                />
              </div>

              {/* Museum Team Links */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${getEffectiveTheme() === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    🏟️ Squadra Museum (opzionale)
                  </label>
                  <select
                    value={formData.my_museum_team_id || ''}
                    onChange={(e) => setFormData({...formData, my_museum_team_id: e.target.value || null})}
                    className={`w-full px-3 py-2 border rounded-md ${
                      getEffectiveTheme() === 'dark' 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300'
                    }`}
                  >
                    <option value="">Nessuna squadra museum</option>
                    {museumTeams.length === 0 ? (
                      <option disabled>⚠️ Museum vuoto - Aggiungi squadre prima</option>
                    ) : (
                      museumTeams.map(team => (
                        <option key={team.id} value={team.id}>
                          {team.name} ({team.country || team.team_type || 'Club'})
                        </option>
                      ))
                    )}
                  </select>
                </div>

                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={() => setShowTeamForm(true)}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors"
                  >
                    <span>🏟️</span>
                    <span>Aggiungi al Museum</span>
                  </button>
                </div>
              </div>

              {/* Result Preview */}
              <div className={`p-4 rounded-lg text-center ${
                formData.has_piazzati && formData.piazzati_miei && formData.piazzati_avversario
                  ? (parseInt(formData.piazzati_miei) > parseInt(formData.piazzati_avversario) ? 'bg-green-100 text-green-700' :
                     parseInt(formData.piazzati_miei) < parseInt(formData.piazzati_avversario) ? 'bg-red-100 text-red-700' :
                     'bg-yellow-100 text-yellow-700')
                  : (formData.my_goals > formData.opponent_goals ? 'bg-green-100 text-green-700' :
                     formData.my_goals < formData.opponent_goals ? 'bg-red-100 text-red-700' :
                     'bg-yellow-100 text-yellow-700')
              }`}>
                <div className="text-3xl font-bold mb-2">
                  {formData.my_goals} - {formData.opponent_goals}
                  {formData.has_piazzati && formData.piazzati_miei && formData.piazzati_avversario && (
                    <span className="text-lg ml-2">🎯 {formData.piazzati_miei}-{formData.piazzati_avversario}</span>
                  )}
                </div>
                <div className="text-lg">
                  {formData.has_piazzati && formData.piazzati_miei && formData.piazzati_avversario
                    ? (parseInt(formData.piazzati_miei) > parseInt(formData.piazzati_avversario) ? '🏆 Vittoria ai piazzati!' :
                       parseInt(formData.piazzati_miei) < parseInt(formData.piazzati_avversario) ? '😞 Sconfitta ai piazzati' :
                       '🤝 Pareggio')
                    : (formData.my_goals > formData.opponent_goals ? '🏆 Vittoria!' :
                       formData.my_goals < formData.opponent_goals ? '😞 Sconfitta' :
                       '🤝 Pareggio')}
                </div>
                {formData.my_team && formData.opponent_team && (
                  <div className="text-sm mt-2 opacity-75">
                    {formData.my_team} vs {formData.opponent_team}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowQuickAddModal(false)}
                  className={`flex-1 px-4 py-3 border border-gray-300 rounded-md text-sm font-medium ${
                    getEffectiveTheme() === 'dark' 
                      ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-3 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                >
                  {loading ? 'Salvando...' : '✅ Salva Partita'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 📅 CALENDARIO 2.0 PROFESSIONALE */}
      {showCalendar && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="max-w-7xl w-full max-h-[95vh] overflow-hidden relative">
            <CalendarContainer
              events={calendarEvents}
              onEventCreate={(eventData) => {
                createCalendarEvent(eventData);
              }}
              onEventUpdate={(eventId, eventData) => {
                updateCalendarEvent(eventId, eventData);
              }}
              onEventDelete={(eventId) => {
                deleteCalendarEvent(eventId);
              }}
            />
            
            {/* Close Button */}
            <button
              onClick={() => setShowCalendar(false)}
              className="absolute top-4 right-4 bg-white bg-opacity-90 hover:bg-opacity-100 text-gray-700 rounded-full p-3 shadow-lg transition-all duration-200 z-10"
            >
              <span className="text-xl font-bold">✕</span>
            </button>
          </div>
        </div>
      )}

      {/* ➕ AGGIUNGI EVENTO MODAL */}
      {showAddEventModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] flex flex-col">
            {/* Header fisso */}
            <div className="p-6 border-b flex-shrink-0">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-bold text-gray-900">➕ Aggiungi Evento</h3>
                <button
                  onClick={() => setShowAddEventModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Contenuto scrollabile */}
            <div className="flex-1 overflow-y-auto">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  createCalendarEvent(eventFormData);
                }}
                className="p-6 space-y-4"
                id="eventForm"
              >
              {/* Nome evento */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome Torneo/Evento *
                </label>
                <input
                  type="text"
                  value={eventFormData.name}
                  onChange={(e) => setEventFormData(prev => ({...prev, name: e.target.value}))}
                  placeholder="es. Torneo Estivo Roma"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Data */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Data *
                </label>
                <input
                  type="date"
                  value={eventFormData.date}
                  onChange={(e) => setEventFormData({...eventFormData, date: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Ora */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ora (opzionale)
                </label>
                <input
                  type="time"
                  value={eventFormData.time}
                  onChange={(e) => setEventFormData({...eventFormData, time: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              {/* Categoria */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Categoria * 
                  <span className="text-xs text-gray-500 ml-1">(seleziona una categoria)</span>
                </label>
                <div className="space-y-2">
                  <label className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    eventFormData.category === 'training' 
                      ? 'border-green-500 bg-green-50' 
                      : 'border-gray-200 hover:border-green-300 hover:bg-green-25'
                  }`}>
                    <input
                      type="radio"
                      name="category"
                      value="training"
                      checked={eventFormData.category === 'training'}
                      onChange={(e) => setEventFormData({...eventFormData, category: e.target.value})}
                      className="mr-3 text-green-600"
                      required
                    />
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                      <span className="font-medium text-green-700">🏃 Allenamento / Amichevole</span>
                    </div>
                  </label>
                  <label className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    eventFormData.category === 'club_tournament' 
                      ? 'border-orange-500 bg-orange-50' 
                      : 'border-gray-200 hover:border-orange-300 hover:bg-orange-25'
                  }`}>
                    <input
                      type="radio"
                      name="category"
                      value="club_tournament"
                      checked={eventFormData.category === 'club_tournament'}
                      onChange={(e) => setEventFormData({...eventFormData, category: e.target.value})}
                      className="mr-3 text-orange-600"
                    />
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-orange-500 rounded-full mr-2"></div>
                      <span className="font-medium text-orange-700">🏆 Torneo Club</span>
                    </div>
                  </label>
                  <label className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    eventFormData.category === 'national_tournament' 
                      ? 'border-red-500 bg-red-50' 
                      : 'border-gray-200 hover:border-red-300 hover:bg-red-25'
                  }`}>
                    <input
                      type="radio"
                      name="category"
                      value="national_tournament"
                      checked={eventFormData.category === 'national_tournament'}
                      onChange={(e) => setEventFormData({...eventFormData, category: e.target.value})}
                      className="mr-3 text-red-600"
                    />
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
                      <span className="font-medium text-red-700">🥇 Torneo Nazionale</span>
                    </div>
                  </label>
                </div>
              </div>

              {/* Descrizione */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descrizione (opzionale)
                </label>
                <textarea
                  value={eventFormData.description}
                  onChange={(e) => setEventFormData({...eventFormData, description: e.target.value})}
                  placeholder="Aggiungi dettagli sull'evento..."
                  rows="3"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              </form>
            </div>

            {/* Footer fisso con pulsanti */}
            <div className="p-6 border-t flex-shrink-0 bg-gray-50">
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAddEventModal(false)}
                  className="flex-1 px-4 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  form="eventForm"
                  className="flex-1 px-4 py-3 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors font-semibold"
                >
                  ➕ Aggiungi Evento
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 🔔 TOAST NOTIFICATIONS */}
      {toastMessage && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg transition-all duration-300 ${
          toastMessage.type === 'success' 
            ? 'bg-green-500 text-white' 
            : 'bg-red-500 text-white'
        }`}>
          <div className="flex items-center space-x-2">
            <span className="text-lg">
              {toastMessage.type === 'success' ? '✅' : '❌'}
            </span>
            <span className="font-medium">{toastMessage.message}</span>
            <button
              onClick={() => setToastMessage(null)}
              className="ml-2 text-white hover:text-gray-200 text-xl font-bold"
            >
              ×
            </button>
          </div>
        </div>
      )}
      
      {/* 💳 UPGRADE TO PREMIUM MODAL */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 m-4 max-w-md w-full">
            <div className="text-center">
              <div className="text-4xl mb-4">💎</div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Upgrade to Premium</h3>
              
              {/* Avviso Trial Scaduto */}
              {subscriptionStatus?.trial_expired && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                  <p className="text-red-800 font-medium text-sm">⚠️ Il tuo periodo di prova è scaduto!</p>
                  <p className="text-red-600 text-xs">Passa a Premium per continuare.</p>
                </div>
              )}
              
              <p className="text-gray-600 mb-6">
                Accesso illimitato a tutte le funzionalità di Subbuteo Manager per un anno intero!
              </p>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="text-2xl font-bold text-green-800">€5,00</div>
                <div className="text-sm text-green-600">per anno</div>
                <div className="text-xs text-gray-500 mt-1">Rinnovo automatico</div>
              </div>

              <div className="text-left mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">✅ Cosa ottieni:</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center">
                    <span className="text-green-500 mr-2">✓</span>
                    Statistiche avanzate illimitate
                  </li>
                  <li className="flex items-center">
                    <span className="text-green-500 mr-2">✓</span>
                    Backup automatico dei dati
                  </li>
                  <li className="flex items-center">
                    <span className="text-green-500 mr-2">✓</span>
                    Supporto prioritario
                  </li>
                  <li className="flex items-center">
                    <span className="text-green-500 mr-2">✓</span>
                    Accesso a tutte le funzionalità future
                  </li>
                </ul>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setShowUpgradeModal(false)}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Non ora
                </button>
                <button
                  onClick={createPayment}
                  disabled={paymentProcessing}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                >
                  {paymentProcessing ? (
                    <span className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                      Processando...
                    </span>
                  ) : (
                    'Paga con PayPal'
                  )}
                </button>
              </div>

              <p className="text-xs text-gray-500 mt-4">
                Pagamento sicuro processato da PayPal. Puoi cancellare in qualsiasi momento.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* 🎉 PAYMENT SUCCESS MODAL */}
      {showPaymentSuccess && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 m-4 max-w-md w-full text-center">
            <div className="text-6xl mb-4">🎉</div>
            <h3 className="text-xl font-bold text-green-800 mb-2">Pagamento Completato!</h3>
            <p className="text-gray-600 mb-6">
              Benvenuto in Subbuteo Manager Premium! Il tuo abbonamento è ora attivo per un anno intero.
            </p>
            <button
              onClick={() => setShowPaymentSuccess(false)}
              className="w-full px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
            >
              Continua
            </button>
          </div>
        </div>
      )}
      
      {/* 🎯 DESKTOP FAB - Add Match (Floating Action Button) */}
      <button
        onClick={() => setActiveTab('add-match')}
        className={`hidden md:flex fixed bottom-8 right-8 z-30 items-center justify-center space-x-2 px-6 py-4 rounded-2xl shadow-2xl transition-all duration-300 hover:scale-110 hover:shadow-3xl group ${
          activeTab === 'add-match'
            ? 'bg-gradient-to-r from-green-500 to-emerald-600 ring-4 ring-green-300'
            : 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700'
        } text-white font-bold text-lg`}
        style={{
          boxShadow: '0 10px 40px rgba(59, 130, 246, 0.4)'
        }}
      >
        <span className="text-3xl group-hover:rotate-90 transition-transform duration-300">➕</span>
        <span>Aggiungi Partita</span>
      </button>

      {/* 📱 MOBILE BOTTOM TAB BAR */}
      <div className={`md:hidden fixed bottom-0 left-0 right-0 z-30 border-t transition-colors ${
        getEffectiveTheme() === 'dark' ? 'bg-gray-900/95 border-gray-800' : 'bg-white/95 border-slate-200'
      } backdrop-blur-xl`}>
        <div className="flex items-center justify-around h-16 px-2">
          {bottomTabs.map(tab => {
            const isActive = tab.id === 'menu' ? false : activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  if (tab.id === 'menu') {
                    setSidebarOpen(true);
                  } else {
                    setActiveTab(tab.id);
                  }
                }}
                className={`flex flex-col items-center justify-center flex-1 h-full space-y-1 transition-all duration-200 relative ${
                  isActive
                    ? 'text-blue-600'
                    : getEffectiveTheme() === 'dark'
                    ? 'text-gray-400 hover:text-white'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <span className="text-2xl">{tab.icon}</span>
                <span className={`text-xs font-medium ${isActive ? 'font-bold' : ''}`}>
                  {tab.label}
                </span>
                {isActive && (
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-gradient-to-r from-blue-400 to-purple-400 rounded-t-full" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Close Main Content Wrapper */}
      </div>
      
    </div>
  );
}


// 🔐 AUTHENTICATION SELECTION COMPONENT

// 🔐 ADMIN DASHBOARD COMPONENT - Moved to pages/AdminDashboard.jsx
// 📅 CALENDARIO 2.0 - Moved to components/calendar/CalendarContainer.jsx

// Wrapper per pagina evento pubblica
const EventoPublicWrapper = () => {
  const { eventoId } = useParams();
  return <PaginaEventoPubblica eventoId={eventoId} />;
};

// 🚀 MAIN APP EXPORT WITH AUTH PROVIDER AND ROUTING
const AppWithAuth = () => {
  return (
    <BrowserRouter>
    <ConfirmDialogProvider>
      <Routes>
        <Route path="/" element={<LandingPageWrapper />} />
        <Route path="/scopri" element={<LandingPageWrapper />} />
        <Route path="/login" element={
          <AuthProvider>
            <App />
          </AuthProvider>
        } />
        <Route path="/registrati" element={
          <AuthProvider>
            <RegisterFormWrapper />
          </AuthProvider>
        } />
        <Route path="/registrati/:inviteCode" element={
          <AuthProvider>
            <RegisterFormWrapper />
          </AuthProvider>
        } />
        {/* 👁️ USER VIEWING CLUB PAGE */}
        <Route path="/mio-club/:clubId" element={
          <AuthProvider>
            <ClubViewWrapper />
          </AuthProvider>
        } />
        {/* 🏢 CLUB ROUTES */}
        <Route path="/login-club" element={
          <ClubAuthProvider>
            <ClubLoginForm />
          </ClubAuthProvider>
        } />
        <Route path="/registra-club" element={
          <ClubAuthProvider>
            <ClubRegisterForm />
          </ClubAuthProvider>
        } />
        <Route path="/club/dashboard" element={
          <ClubAuthProvider>
            <ClubDashboard />
          </ClubAuthProvider>
        } />
        <Route path="/club/public/:username" element={<PublicClubPage />} />
        <Route path="/club/:username" element={<PublicClubPage />} />
        <Route path="/profilo/:username" element={<PublicProfilePage />} />
        <Route path="/attiva-codice" element={<AttivaCodicePage />} />
        <Route path="/join" element={<AttivaCodicePage />} />
        <Route path="/area-socio" element={<AreaSocioPage />} />
        <Route path="/guida" element={<GuidaPage />} />
        <Route path="/evento/:eventoId" element={<EventoPublicWrapper />} />
        <Route path="/club/*" element={
          <ClubAuthProvider>
            <ClubDashboard />
          </ClubAuthProvider>
        } />
        <Route path="/app/*" element={
          <AuthProvider>
            <App />
          </AuthProvider>
        } />
        {/* PayPal Payment Return Routes */}
        <Route path="/payment/success" element={
          <AuthProvider>
            <App />
          </AuthProvider>
        } />
        <Route path="/payment/cancel" element={
          <AuthProvider>
            <App />
          </AuthProvider>
        } />
        <Route path="/club-payment/success" element={
          <ClubAuthProvider>
            <ClubDashboard />
          </ClubAuthProvider>
        } />
        <Route path="/club-payment/cancel" element={
          <ClubAuthProvider>
            <ClubDashboard />
          </ClubAuthProvider>
        } />
      </Routes>
    </ConfirmDialogProvider>
    </BrowserRouter>
  );
};

// Landing Page Wrapper with navigation
const LandingPageWrapper = () => {
  const navigate = useNavigate();
  
  const handleNavigateToApp = () => {
    navigate('/login');
  };

  const handleNavigateToRegister = () => {
    navigate('/registrati');
  };
  
  return <LandingPage onNavigateToApp={handleNavigateToApp} onNavigateToRegister={handleNavigateToRegister} />;
};

// Register Form Wrapper for standalone route
const RegisterFormWrapper = () => {
  const { inviteCode } = useParams();
  const { isAuthenticated, isLoading } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    // If already authenticated, redirect to app
    if (!isLoading && isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, isLoading, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Caricamento...</p>
        </div>
      </div>
    );
  }

  return <RegisterForm inviteCode={inviteCode} />;
};

// 🌐 PUBLIC CLUB PAGE - Accessible without login

// 👁️ Club View Wrapper - User viewing club page
const ClubViewWrapper = () => {
  const { clubId } = useParams();
  const navigate = useNavigate();
  const [clubData, setClubData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [authChecked, setAuthChecked] = useState(false);
  
  // Statistiche e classifiche
  const [sociStats, setSociStats] = useState([]);
  const [classifiche, setClassifiche] = useState({ stagione: [], storico: [], gol: [] });
  const [records, setRecords] = useState(null);
  const [activeClassifica, setActiveClassifica] = useState('stagione');
  const [expandedSocio, setExpandedSocio] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  const getToken = () => localStorage.getItem('subbuteo_token') || sessionStorage.getItem('subbuteo_token');

  useEffect(() => {
    const checkAuth = () => {
      const token = getToken();
      if (!token) {
        setTimeout(() => {
          const tokenRetry = getToken();
          if (!tokenRetry) {
            navigate('/login');
          } else {
            setAuthChecked(true);
          }
        }, 100);
      } else {
        setAuthChecked(true);
      }
    };
    checkAuth();
  }, [navigate]);

  useEffect(() => {
    if (authChecked && clubId) {
      loadClubData();
      loadStats();
    }
  }, [authChecked, clubId]);

  const loadClubData = async () => {
    const token = getToken();
    if (!token) { navigate('/login'); return; }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/user/clubs/${clubId}/view`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setClubData(data);
      } else if (response.status === 401) {
        navigate('/login');
        return;
      } else {
        const err = await response.json();
        setError(err.detail || 'Errore nel caricamento');
      }
    } catch (e) {
      setError('Errore di connessione');
    }
    setLoading(false);
  };

  const loadStats = async () => {
    const token = getToken();
    if (!token) return;
    
    setLoadingStats(true);
    try {
      // Carica statistiche soci
      const statsRes = await fetch(`${BACKEND_URL}/api/clubs/${clubId}/soci-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (statsRes.ok) {
        const data = await statsRes.json();
        setSociStats(data.soci_stats || []);
      }
      
      // Carica classifiche
      const classRes = await fetch(`${BACKEND_URL}/api/clubs/${clubId}/classifiche`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (classRes.ok) {
        const data = await classRes.json();
        setClassifiche(data);
      }
      
      // Carica record
      const recRes = await fetch(`${BACKEND_URL}/api/clubs/${clubId}/record`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (recRes.ok) {
        const data = await recRes.json();
        setRecords(data);
      }
    } catch (e) {
      console.error('Errore caricamento stats:', e);
    }
    setLoadingStats(false);
  };

  // Helper per forma
  const renderForma = (forma) => {
    return forma.map((r, i) => (
      <span key={i} className={`w-5 h-5 rounded-full inline-flex items-center justify-center text-xs font-bold mr-1 ${
        r === 'V' ? 'bg-green-500 text-white' : r === 'P' ? 'bg-gray-400 text-white' : 'bg-red-500 text-white'
      }`}>{r}</span>
    ));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Caricamento club...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-6xl mb-4">🚫</div>
          <p className="text-red-600 mb-4">{error}</p>
          <button onClick={() => navigate('/login')} className="px-4 py-2 bg-blue-600 text-white rounded-lg">
            Torna all'app
          </button>
        </div>
      </div>
    );
  }

  if (!clubData) return null;

  const { club, membership, soci, stats } = clubData;

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-gradient-to-r from-blue-600 to-blue-800 text-white shadow-lg">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <button onClick={() => navigate('/login')} className="text-blue-200 hover:text-white text-sm mb-4 flex items-center gap-1">
            ← Torna alla tua area
          </button>
          <div className="flex items-center gap-4">
            <div className="bg-white/20 rounded-full p-4">
              <span className="text-4xl">🏢</span>
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">{club.name}</h1>
              {club.city && <p className="text-blue-200">📍 {club.city}</p>}
            </div>
          </div>
          
          <div className="mt-4 flex items-center gap-3">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              membership.role === 'admin' ? 'bg-orange-500 text-white' : 'bg-blue-500 text-white'
            }`}>
              {membership.role === 'admin' ? '🔑 Admin' : '👤 Socio'}
            </span>
            <span className="text-blue-200 text-sm">
              Membro dal {new Date(membership.joined_at).toLocaleDateString('it-IT')}
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        
        {/* Record Club Widget */}
        {records && (records.bomber?.gol > 0 || records.vittoria_record?.diff > 0) && (
          <div className="bg-gradient-to-r from-yellow-400 to-orange-500 rounded-xl shadow-lg p-4 text-white">
            <h3 className="font-bold text-sm mb-3 opacity-90">🏆 RECORD DEL CLUB</h3>
            <div className="grid grid-cols-3 gap-4 text-center">
              {records.bomber?.gol > 0 && (
                <div>
                  <div className="text-2xl font-bold">{records.bomber.gol}⚽</div>
                  <div className="text-xs opacity-90">Bomber: {records.bomber.nome?.split(' ')[0]}</div>
                </div>
              )}
              {records.vittoria_record?.diff > 0 && (
                <div>
                  <div className="text-2xl font-bold">{records.vittoria_record.risultato}</div>
                  <div className="text-xs opacity-90">Vittoria Record</div>
                </div>
              )}
              {records.imbattuto?.serie > 1 && (
                <div>
                  <div className="text-2xl font-bold">{records.imbattuto.serie}🔥</div>
                  <div className="text-xs opacity-90">Vittorie: {records.imbattuto.nome?.split(' ')[0]}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Classifiche Unificate */}
        {(classifiche.stagione.length > 0 || classifiche.gol.length > 0) && (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            {/* Tab Header */}
            <div className="flex border-b">
              {[
                { id: 'stagione', label: '🏆 Stagione', data: classifiche.stagione },
                { id: 'storico', label: '📅 Storico', data: classifiche.storico },
                { id: 'gol', label: '⚽ Gol', data: classifiche.gol }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveClassifica(tab.id)}
                  className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
                    activeClassifica === tab.id
                      ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-600'
                      : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            
            {/* Classifica Content */}
            <div className="p-4">
              {classifiche[activeClassifica]?.length === 0 ? (
                <p className="text-gray-500 text-center py-4">Nessun dato disponibile</p>
              ) : (
                <div className="space-y-2">
                  {classifiche[activeClassifica]?.slice(0, 10).map((s, i) => (
                    <div key={s.socio_id || i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                          i === 0 ? 'bg-yellow-400 text-yellow-900' :
                          i === 1 ? 'bg-gray-300 text-gray-700' :
                          i === 2 ? 'bg-orange-300 text-orange-800' :
                          'bg-gray-100 text-gray-600'
                        }`}>{i + 1}</span>
                        <span className="font-medium">{s.nome}</span>
                      </div>
                      <div className="text-right">
                        {activeClassifica === 'stagione' && (
                          <span className="text-blue-600 font-bold">{s.punti_ranking_totali} pts</span>
                        )}
                        {activeClassifica === 'storico' && (
                          <span className="text-green-600 font-bold">{s.perc_vittorie}%</span>
                        )}
                        {activeClassifica === 'gol' && (
                          <span className="text-purple-600 font-bold">{s.gol_fatti}⚽</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Card Soci con Stats */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-lg font-bold mb-4">📊 Statistiche Soci</h2>
          
          {loadingStats ? (
            <div className="text-center py-8 text-gray-500">Caricamento statistiche...</div>
          ) : sociStats.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-2">📊</div>
              <p className="text-gray-500">Nessuna statistica disponibile</p>
              <p className="text-gray-400 text-sm">Le statistiche appariranno dopo il primo torneo</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sociStats.map((s) => (
                <div 
                  key={s.socio_id} 
                  className={`border-2 rounded-xl p-4 cursor-pointer transition-all ${
                    expandedSocio === s.socio_id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'
                  }`}
                  onClick={() => setExpandedSocio(expandedSocio === s.socio_id ? null : s.socio_id)}
                >
                  {/* Header Card */}
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="font-bold text-gray-800">{s.nome}</h3>
                    {sociStats.indexOf(s) < 3 && (
                      <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                        sociStats.indexOf(s) === 0 ? 'bg-yellow-400 text-yellow-900' :
                        sociStats.indexOf(s) === 1 ? 'bg-gray-300 text-gray-700' :
                        'bg-orange-300 text-orange-800'
                      }`}>#{sociStats.indexOf(s) + 1}</span>
                    )}
                  </div>
                  
                  {/* 4 Stats Chiave */}
                  <div className="grid grid-cols-4 gap-2 text-center mb-3">
                    <div>
                      <div className={`text-xl font-bold ${
                        s.perc_vittorie >= 60 ? 'text-green-600' : s.perc_vittorie >= 40 ? 'text-yellow-600' : 'text-red-500'
                      }`}>{s.perc_vittorie}%</div>
                      <div className="text-xs text-gray-400">vitt.</div>
                    </div>
                    <div>
                      <div className="text-xl font-bold text-purple-600">{s.gol_fatti}</div>
                      <div className="text-xs text-gray-400">gol</div>
                    </div>
                    <div>
                      <div className="text-xl font-bold text-blue-600">{s.tornei_vinti}</div>
                      <div className="text-xs text-gray-400">🏆</div>
                    </div>
                    <div>
                      <div className="text-xl font-bold text-orange-500">{s.podi}</div>
                      <div className="text-xs text-gray-400">podi</div>
                    </div>
                  </div>
                  
                  {/* Forma */}
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-gray-400 mr-1">Forma:</span>
                    {s.forma?.length > 0 ? renderForma(s.forma) : <span className="text-gray-400 text-xs">-</span>}
                  </div>
                  
                  {/* Dettagli Espansi */}
                  {expandedSocio === s.socio_id && (
                    <div className="mt-4 pt-4 border-t border-gray-200 text-sm space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Partite</span>
                        <span className="font-medium">{s.partite} ({s.vittorie}V {s.pareggi}P {s.sconfitte}S)</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Gol subiti</span>
                        <span className="font-medium">{s.gol_subiti}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Diff. reti</span>
                        <span className={`font-medium ${s.diff_reti > 0 ? 'text-green-600' : s.diff_reti < 0 ? 'text-red-500' : ''}`}>
                          {s.diff_reti > 0 ? '+' : ''}{s.diff_reti}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Tornei giocati</span>
                        <span className="font-medium">{s.tornei_giocati}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Punti ranking</span>
                        <span className="font-bold text-blue-600">{s.punti_ranking_totali} pts</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Lista Soci Base */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-lg font-bold mb-4">👥 Tutti i Soci ({soci.length})</h2>
          
          {soci.length === 0 ? (
            <p className="text-gray-500 text-center py-4">Nessun socio ancora registrato</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {soci.map((s, i) => (
                <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                    s.ruolo === 'admin' ? 'bg-orange-500' : 'bg-blue-500'
                  }`}>
                    {s.nome_completo?.charAt(0) || '?'}
                  </div>
                  <div>
                    <p className="font-medium">{s.nome_completo}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      s.status === 'attivo' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {s.status === 'attivo' ? '✅ Attivo' : '⏳ In attesa'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Admin Notice */}
        {membership.can_edit && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <p className="text-orange-800 font-medium">
              🔑 Sei Admin di questo club. Per gestire il club, accedi dalla 
              <a href="/login-club" className="text-orange-600 underline ml-1">Dashboard Club</a>.
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default AppWithAuth;